
import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import { AppUser, CampingSpot, GpxRoute, RouteRegion, MapBounds, Coordinates, WikiSpot, AppNotification, RouteFollowState, RouteFollowPoi, RoutePlanAlternative, BathingPlace, ConditionTag, DiscoverPOI, SvvCamera, FollowMode } from './types';
import MapComponent from './components/Map';
import SpotList from './components/SpotList';
import Header from './components/layout/Header';
import AppEntryView from './components/layout/AppEntryView';
import AuthOverlay from './components/AuthOverlay';
import ProfileOverlay from './components/ProfileOverlay';
import AddSpotModal from './components/AddSpotModal';
import { useEventCallback } from './hooks/useEventCallback';
import SymbolLegend from './components/SymbolLegend';
import LegalGuide from './components/LegalGuide';
import DebugConsole from './components/DebugConsole';
import AdminPanel from './components/AdminPanel';
import AdminRouteUpload from './components/AdminRouteUpload';
import MobileSpotSheet from './components/MobileSpotSheet';
import LoadingOverlay from './components/LoadingOverlay';
import UpgradeOverlay from './components/UpgradeOverlay';
import VehicleOnboardingOverlay from './components/VehicleOnboardingOverlay';
import WelcomeSheet from './components/WelcomeSheet';
import SharedReviewModal from './components/SharedReviewModal';
import TesterApplicationModal from './components/TesterApplicationModal';
import TesterInboxModal from './components/TesterInboxModal';
import MapCorrectionModal from './components/MapCorrectionModal';
import MapContextMenuModal from './components/map/MapContextMenuModal';
import { useFirebaseSync } from './hooks/useFirebaseSync';
import { useDiscover } from './hooks/useDiscover';
import { useSpotManager } from './hooks/useSpotManager';
import { useAnalysisManager, AnalysisStats } from './hooks/useAnalysisManager';
import { useSelectionManager } from './hooks/useSelectionManager';
import { useBackNavigation } from './hooks/useBackNavigation';
import { useAndroidAuto } from './hooks/useAndroidAuto';
import { isAndroidAutoConnected, showAutoCheckInPrompt, onAutoCheckInConfirmed, sendSpotsToAuto, sendPositionToAuto } from './services/androidAutoService';
import { parseWidgetDeepLink } from './utils/parseWidgetDeepLink';
import { buildWidgetSnapshot } from './services/widgetSnapshotService';
import { pushWidgetSnapshot } from './services/widgetBridgeService';
import { isNewsFeedVisible } from './services/newsFeed';
import { AndroidAutoBanner } from './components/AndroidAutoBanner';
import { DrivingModeOverlay } from './components/DrivingModeOverlay';
import { getDateLocale } from './services/localeHelpers';
import { useNativeNavMap } from './hooks/useNativeNavMap';
import { useRestAreas } from './hooks/useRestAreas';
import { useOverpassPOIBatch } from './hooks/useOverpassPOIBatch';
import { useCoastalSpots } from './hooks/useCoastalSpots';
import { useAppSearch } from './hooks/useAppSearch';
import { suggestDeletion, fetchAllRoutes, upsertSpotsToFirebase, upsertPrivateSpot, fetchPrivateSpots, uploadSpotImages, fetchSystemConfig, updateUserSettings, toggleFavoriteSpot, analyzeVerifiedSpotVehicles, processGoogleRedirectResult, processAppleRedirectResult, analyzeRouteServer, subscribeToRouteUpdates, subscribeToTesterThreadsForAdmin, getCachedSpotsInArea, fetchSpotsByIds, logFeatureUsage, fetchGpxWithProxy, fetchRecentVerifiedSpots, fetchRecentReviews, hasUserReviewedSpot, isSpotInEnabledCountry, recordUserCountryRequest, db, verifyStripeCheckout, reconcileVippsAgreement, setClientAdminStatus, fetchGooglePhotosForSpot } from './services/firebaseService';
import { findNearestSwedishRoad } from './services/swedishRoadNetworkService';
import { detectCountry } from './utils/countryDetection';
import { isAutocamperSpot } from './utils/autocamperSpots';
import { getDoc, doc, updateDoc, onSnapshot, collection } from 'firebase/firestore';
import { fetchRoadFromClick, snapToNearestRoad, fetchAllOverpassPOIs, fetchCandidateSpots } from './services/overpassService';
import { getSpotSource, mergeSpotsByIdentity } from './services/spotIdentity';
import { geoJsonToGpx, isPointNearPolyline, getDistance } from './services/geometryUtils';
import { fetchDrivingRoute, formatDuration } from './services/routingService';
import { enrichSpotWithGeoData, estimateTerrainSlopePercent } from './services/geoValidationService';
import { reverseGeocode, searchPlace, isCoordinateLabel } from './services/geocodingService';
import { fetchGlobalWarnings } from './services/varsomService';
import { calculateRestAreaScore, fetchNVDBData } from './services/nvdbService';
import { fetchWildlifeForRoute } from './services/wildlifeService';
import { MAP_DEFAULT_CENTER, MAP_ZOOM_LEVEL, GEOFENCE_CHECKIN_ENABLED } from './constants';
import { Loader2, Search, Plus, Map as MapIcon, Route, List, CheckCircle, Trash2, RefreshCw, X, AlertTriangle, ShieldAlert, MessageSquare, Sparkles, Navigation, MapPin, CloudUpload, Compass } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import { useTheme } from './contexts/ThemeContext';
import AppOverlays from './components/layout/AppOverlays';
import { useWhatsNewUnread } from './components/whatsnew/WhatsNewModal';
import { useGpsTracking } from './hooks/useGpsTracking';
import { useRouteFollow } from './hooks/useRouteFollow';
import { useTabletOrientationGuard } from './hooks/useTabletOrientationGuard';
import { useHomeRegionDetection } from './hooks/useHomeRegionDetection';
import { useRegionViewTracking } from './hooks/useRegionViewTracking';
import { useLiveLocations } from './hooks/useLiveLocations';
import { usePushNotificationListeners } from './hooks/usePushNotificationListeners';
import { useAppUpdate } from './hooks/useAppUpdate';
import TrackingPanel from './components/TrackingPanel';
import NavigationHUD from './components/navigation/NavigationHUD';
import NavigationChoiceModal from './components/navigation/NavigationChoiceModal';
import QuickAddFAB from './components/navigation/QuickAddFAB';
import MyTracksModal from './components/MyTracksModal';
import RoutePlanBar from './components/RoutePlanBar';
import { fetchRouteAlternatives, fetchMultiStopRoute } from './services/routePlanningService';
import { saveTrack, trackPointsToElevationProfile, convertTrackToRoute } from './services/trackService';
import { getNextTier, getUserTier, contributionScore, toggleVisitSpot, addPastStay, deletePastStay, VisitProgressResult, recordCheckOut } from './services/visitService';
import { markSpotOccupied, extendStay, clearSpotOccupancy, evaluateAutoCheckIn, calculateRasteplassOccupancyUntil, setCurrentCheckIn, clearCurrentCheckIn, autoCheckOutPrevious, occupancyEndDate } from './services/occupancyService';
import { writeSpotVehicleVisit } from './services/spotVehicleVisitService';
import { addConditionReport } from './services/conditionService';
import AutoCheckInBanner, { isDismissedRecently, setDismissed } from './components/spots/AutoCheckInBanner';
import { runProximityCheckIn, findNearbyCheckInSpot } from './services/spotProximity';
import CheckInOutPill, { type CheckInOutPillProps } from './components/spots/CheckInOutPill';
import { scheduleProximityCheckInNotification } from './services/checkInNotifications';
import AutoNavOverlay from './components/AutoNavOverlay';
import AndroidAutoRouteSheet from './components/AndroidAutoRouteSheet';
import { useAutoNavigationMode } from './hooks/useAutoNavigationMode';
import { useGeofenceCheckIn } from './hooks/useGeofenceCheckIn';
import { buildVehicleSnapshot } from './services/vehicleService';
import { HeadingBuffer } from './utils/headingBuffer';
import { joinMapByToken, hasUnreadCollabChat, hasUnseenCollabContent } from './services/collaborativeMapsService';
import { markAllNotificationsAsRead, markNotificationAsRead } from './services/notificationHistoryService';
import { requestNotificationPermission, checkNotificationPermission } from './services/notificationService';
import { hasPaidAccess, hasPremiumAccess as checkPremiumAccess, hasTierAccess, isAdminUser, isActiveTester, hasCopilotAccess, hasReplayAccess } from './services/accessControl';
import { resolveRole } from './utils/roleMemory';
import { skalViseLaster, initialLoadingTail, LOADING_TAIL_MS } from './utils/loadingTail';
import { readBetaCountryPrefs, resolveCountryAccess, BETA_COUNTRY_EVENT } from './utils/betaCountries';
import { buildTripSpec as buildReplayTripSpec, isViableTrip as isViableReplayTrip } from './services/replayEngine';
import { useToast } from './contexts/ToastContext';
import { PremiumNudgeProvider } from './contexts/PremiumNudgeContext';
import type { PremiumNudgeHandle } from './contexts/PremiumNudgeContext';
import { spotFeatureToWaypointIcon, getWaypointMeta, synthesizeSpotFromWaypoint, waypointHasLayerCategory, unwrapSyntheticSpotId } from './services/waypointService';
import { uploadWaypointPhoto } from './services/waypointPhotoService';
import { capturePhoto } from './services/cameraService';
import ExifReader from 'exifreader';
import { compressImages, compressImagesDetailed } from './utils/imageCompression';
import { WaypointIcon, LineContent, AreaContent, TrackContent, SavedTrack } from './types';
import PackingListModal from './components/PackingListModal';
import { useMyContent } from './hooks/useMyContent';
import { useDrawingMode } from './hooks/useDrawingMode';
import { addWaypointContent, addLineContent, addAreaContent, deleteContent } from './services/myContentService';
import { addAutonavCandidate, subscribeToAutonavCandidates, deleteAutonavCandidate, type AutonavCandidate } from './services/autonavCandidatesService';
import ReviewCandidatesModal from './components/ReviewCandidatesModal';
import MyContentPanel from './components/mycontent/MyContentPanel';
import MyContentInner from './components/mycontent/MyContentInner';
import SidebarRail, { RAIL_COLLAPSED_WIDTH, RAIL_EXPANDED_WIDTH } from './components/layout/SidebarRail';
import SidebarPanel, { SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH } from './components/layout/SidebarPanel';
import MobileBottomNav from './components/layout/MobileBottomNav';
import MapMiniCards from './components/map/MapMiniCards';
import RouteOverviewMini from './components/map/RouteOverviewMini';
import FollowSpotOverlay from './components/map/FollowSpotOverlay';
import type { SidebarMode, TabKey } from './types/sidebar';
import DrawingToolbar from './components/map/DrawingToolbar';
import DrawingSaveDialog from './components/map/DrawingSaveDialog';
import DrawingLayer from './components/map/DrawingLayer';
import DrawingActiveLayer from './components/map/DrawingActiveLayer';
import DrawingEditLayer from './components/map/DrawingEditLayer';
import UserContentLayer from './components/map/UserContentLayer';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
import LevelUpModal from './components/gamification/LevelUpModal';
import VerifyAfterVisitModal from './components/spots/VerifyAfterVisitModal';
import CampingRatingModal from './components/spots/CampingRatingModal';
import FeatureRevealModal from './components/gamification/FeatureRevealModal';
import ContributionProgressToast, { ContributionProgressToastState } from './components/gamification/ContributionProgressToast';
import { UserTier } from './types';
import { useNetworkStatus } from './hooks/useNetworkStatus';
import { useCarScreen } from './hooks/useCarScreen';
import OfflineBanner from './components/OfflineBanner';
import { resolveInitialRouteState } from './services/appRouteState';
import { openExternalNavigation } from './services/navigationService';
import CountyDownloadModal from './components/CountyDownloadModal';
import { shouldRecommendDownload, markRecommendationShown, resetPerfMetrics } from './services/performanceService';
import { reverseGeocodeRegion } from './services/regionEnrichmentService';
import { useNordicComingSoon, NordicCountry } from './hooks/useNordicComingSoon';
import NordicComingSoonBanner from './components/NordicComingSoonBanner';
import SaveToMyContentModal from './components/mycontent/SaveToMyContentModal';
import TabletFollowPanel from './components/routes/TabletFollowPanel';
import { useFollowTarget } from './hooks/useFollowTarget';
import { useNavigationAlerts } from './hooks/useNavigationAlerts';
import { ContextualAlert } from './hooks/useContextualAlerts';
import FollowPreflightModal from './components/routes/FollowPreflightModal';
import OfflineMapManager from './components/map/OfflineMapManager';
import CopilotPanel from './components/copilot/CopilotPanel';
import CopilotMobileSheet from './components/copilot/CopilotMobileSheet';
import { useTripCopilot } from './hooks/useTripCopilot';
import type { PlanTripSuccess } from './services/tripPlannerService';
import { stopLabel } from './services/tripPlannerService';
import { loadSharedTrip } from './services/copilotTripService';
import { downloadDataForArea } from './services/offlineDataService';
import { downloadTiles, estimateTileCount } from './services/tileDownloaderService';
import { REWARD_EVENT_NAME, RewardEventKind, RewardEventPayload } from './services/rewardEvents';
import { deriveRoadAccessClass } from './services/roadAccessClassifier';
import { useLocationGate } from './contexts/LocationGateContext';

const Map3DView = lazy(() => import('./components/map/Map3DView'));
const ArticleQuickReviewModal = lazy(() => import('./components/admin/ArticleQuickReviewModal'));

const FREE_ZOOM_MAX = 13;

type RouteFacilityLoadingState = {
  routeId: string | null;
  fricamp: boolean;
  restAreas: boolean;
  campsites: boolean;
  attractions: boolean;
  chargers: boolean;
  wildlife: boolean;
};

type RouteReturnContext = {
  routeIds: string[];
  selectedRouteId: string | null;
  previewRegion: RouteRegion | null;
};

type FollowPreflightState = {
  mode: FollowMode;
  title: string;
  bounds: MapBounds | null;
  route?: GpxRoute | null;
  continueAction: () => void | Promise<void>;
};

function App() {
  const routeState = resolveInitialRouteState(window.location);
  const {
    isPrivacyPath,
    isTermsPath,
    resolvedReviewId,
    resolvedSharedSpotId,
    resolvedSharedRouteId,
    resolvedSeoCounty,
  } = routeState;

  if (isPrivacyPath || isTermsPath || resolvedReviewId || resolvedSharedSpotId || resolvedSharedRouteId || resolvedSeoCounty) {
    return <AppEntryView routeState={routeState} />;
  }

  return <AppMain routeState={routeState} />;
}

function AppMain({ routeState }: { routeState: ReturnType<typeof resolveInitialRouteState> }) {
  const {
    showAndroidGuide: initialShowAndroidGuide,
    collabInviteParam,
    collabTokenParam,
    forcedLanguageParam,
    pendingOpenSpotAutoFavoriteParam,
    parsedSharedMap,
    resolvedOpenSpotId,
    resolvedRouteId,
    resolvedSeoMunicipality,
    resolvedSharedWaypoint,
  } = routeState;

  const { t } = useTranslation();
  const addToast = useToast();
  const { setTheme: setAppTheme } = useTheme();
  const { gatedGetCurrentPosition, gatedWatchPosition } = useLocationGate();
  const nudgeRef = useRef<PremiumNudgeHandle>(null);
  const headingBufferRef = useRef(new HeadingBuffer());
  const [nordicConfig, setNordicConfig] = useState<Partial<Record<NordicCountry, boolean>>>(() => {
    try {
      const cached = localStorage.getItem('fricamp_nordic_config');
      if (cached) return JSON.parse(cached) as Partial<Record<NordicCountry, boolean>>;
    } catch {}
    return {};
  });
  const [nordicConfigReady, setNordicConfigReady] = useState(false);
  const { isOffline } = useNetworkStatus();
  const prevOfflineRef = useRef(isOffline);
  const [isAuthOpen, setIsAuthOpen] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('login') === '1'; }
    catch { return false; }
  });
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isStickerBoardOpen, setIsStickerBoardOpen] = useState(false);
  const [isReplayOpen, setIsReplayOpen] = useState(false);
  const [readLocalNotifIds, setReadLocalNotifIds] = useState<string[]>(() => {
    try {
      const stored = window.localStorage.getItem('fricamp_read_local_notifs');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [preSelectedFiles, setPreSelectedFiles] = useState<File[]>([]);
  const [showLegend, setShowLegend] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [showTierGuide, setShowTierGuide] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const whatsNewUnread = useWhatsNewUnread();
  const [locationCache, setLocationCache] = useState<Record<string, { lat: number; lng: number }>>({});
  const [showAdmin, setShowAdmin] = useState(false);
  const [mobileAdminInboxOnly, setMobileAdminInboxOnly] = useState(false);
  const [adminBadgeCount, setAdminBadgeCount] = useState(0);
  const [showRouteUpload, setShowRouteUpload] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showTesterApplication, setShowTesterApplication] = useState(false);
  const [showAndroidGuide, setShowAndroidGuide] = useState(initialShowAndroidGuide);
  const [showInstallApp, setShowInstallApp] = useState(false);
  const [showFeedbackInbox, setShowFeedbackInbox] = useState(false);
  const [feedbackPrefill, setFeedbackPrefill] = useState<string | undefined>(undefined);
  const [testerApplicationsEnabled, setTesterApplicationsEnabled] = useState(false);
  const [vippsPaymentEnabled, setVippsPaymentEnabled] = useState(false);
  const [sharedReviewId, setSharedReviewId] = useState<string | null>(null);
  const [showMyTracks, setShowMyTracks] = useState(false);
  const [trackSaveError, setTrackSaveError] = useState<string | null>(null);
  // Ruteplanlegging (on-map)
  const [routePlan, setRoutePlan] = useState<{ toCoords: Coordinates; toName: string; fromCoords?: Coordinates; fromName?: string; waypoints: { coords: Coordinates; name: string }[] } | null>(null);
  const [routePlanAlternatives, setRoutePlanAlternatives] = useState<RoutePlanAlternative[]>([]);
  const [routePlanSelectedIndex, setRoutePlanSelectedIndex] = useState<number | null>(null);
  const [routePlanFetching, setRoutePlanFetching] = useState(false);
  const [routePlanFromName, setRoutePlanFromName] = useState('');
  const [routePlanError, setRoutePlanError] = useState<string | null>(null);
  const [showRoutePlanContent, setShowRoutePlanContent] = useState(false);
  const [routePlanWildlifeCount, setRoutePlanWildlifeCount] = useState(0);
  const [routePreviewWebcams, setRoutePreviewWebcams] = useState<SvvCamera[]>([]);
  const [showCollabMapModal, setShowCollabMapModal] = useState(false);
  const [mapContextMenuCoords, setMapContextMenuCoords] = useState<{ latitude: number; longitude: number; name?: string } | null>(null);
  const [activeCollabMapId, setActiveCollabMapId] = useState<string | null>(null);
  const [addToMapSpotId, setAddToMapSpotId] = useState<string | null>(null);
  const [addToMapRouteId, setAddToMapRouteId] = useState<string | null>(null);
  const [collabReturnContext, setCollabReturnContext] = useState<{ mapId: string } | null>(null);
  const [saveToMyContentSpot, setSaveToMyContentSpot] = useState<CampingSpot | null>(null);
  const [saveToMyContentRoute, setSaveToMyContentRoute] = useState<GpxRoute | null>(null);
  const [showMyContent, setShowMyContent] = useState(false);
  const [myContentInitialTab, setMyContentInitialTab] = useState<TabKey>('folders');
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPackingList, setShowPackingList] = useState(false);
  const [mapCorrectionCoords, setMapCorrectionCoords] = useState<Coordinates | null>(null);
  const [varsomWarnings, setVarsomWarnings] = useState<any[]>([]);
  const isWinterSeason = useMemo(() => {
    const m = new Date().getMonth();
    return m >= 10 || m <= 3;
  }, []);

  const [analysisReport, setAnalysisReport] = useState<AnalysisStats | null>(null);
  const [levelUpTier, setLevelUpTier] = useState<UserTier | null>(null);
  const [verifyPromptSpot, setVerifyPromptSpot] = useState<CampingSpot | null>(null);
  const [reviewPromptSpot, setReviewPromptSpot] = useState<CampingSpot | null>(null);
  const [campingRatingSpot, setCampingRatingSpot] = useState<CampingSpot | null>(null);
  const [showFeatureReveal, setShowFeatureReveal] = useState(false);
  const [contributionToast, setContributionToast] = useState<ContributionProgressToastState | null>(null);
  const contributionToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const layerTipShownThisSessionRef = useRef(false);
  const layerTipLastLoginUidRef = useRef<string | null>(null);
  const [showUpgradeReminder, setShowUpgradeReminder] = useState(false);
  const [showWelcomeSheet, setShowWelcomeSheet] = useState(false);
  const [showVehiclePrompt, setShowVehiclePrompt] = useState(false);
  const [bottomNavVisible, setBottomNavVisible] = useState(!Capacitor.isNativePlatform());
  const [showLayerTip, setShowLayerTip] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>('news');
  const [adminTab, setAdminTab] = useState<'spots' | 'suggestions' | 'routes' | 'pending' | 'tools' | 'ai' | 'testers'>('spots');

  const [mobileView, setMobileView] = useState<'map' | 'list'>('map');
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 640);
  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const [is3DMode, setIs3DMode] = useState(false);
  const [basemap3D, setBasemap3D] = useState<'topo' | 'satellite'>('topo');
  const [showCountyDownload, setShowCountyDownload] = useState(false);
  const [followMapOrientation, setFollowMapOrientation] = useState<'north_up' | 'heading_up'>('north_up');
  const [followViewMode, setFollowViewMode] = useState<'2d' | '3d'>('2d');
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const stored = window.localStorage.getItem('fricamp_sidebar_width');
    const parsed = Number(stored);
    if (Number.isFinite(parsed)) {
      return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, parsed));
    }
    return SIDEBAR_MIN_WIDTH;
  });
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [railExpanded, setRailExpanded] = useState<boolean>(() => {
    return window.localStorage.getItem('fricamp_rail_expanded') === '1';
  });
  const railWidthPx = railExpanded ? RAIL_EXPANDED_WIDTH : RAIL_COLLAPSED_WIDTH;
  const [newsCount, setNewsCount] = useState(0);
  const [newsLastSeen, setNewsLastSeen] = useState<number>(() => {
    const stored = window.localStorage.getItem('fricamp_news_last_seen');
    return stored ? Number(stored) : 0;
  });

  // Restore last viewport from localStorage synchronously to avoid flash-of-default-position
  // and ensure the first Firestore query targets the right area
  const [mapCenter, setMapCenter] = useState<Coordinates>(() => {
    if (resolvedSharedWaypoint) return { latitude: resolvedSharedWaypoint.latitude, longitude: resolvedSharedWaypoint.longitude };
    if (parsedSharedMap?.center) return parsedSharedMap.center;
    try {
      const saved = localStorage.getItem('fricamp_last_position');
      if (saved) {
        const { latitude, longitude } = JSON.parse(saved);
        if (typeof latitude === 'number' && typeof longitude === 'number') {
          return { latitude, longitude };
        }
      }
    } catch {}
    return MAP_DEFAULT_CENTER;
  });
  const [mapZoom, setMapZoom] = useState(() => {
    if (resolvedSharedWaypoint) return 14;
    if (parsedSharedMap?.zoom) return parsedSharedMap.zoom;
    try {
      const saved = localStorage.getItem('fricamp_last_position');
      if (saved) {
        const { zoom } = JSON.parse(saved);
        if (typeof zoom === 'number') return zoom;
      }
    } catch {}
    return MAP_ZOOM_LEVEL;
  });

  const [currentViewBounds, setCurrentViewBounds] = useState<MapBounds | null>(() => {
    if (parsedSharedMap?.bounds) return parsedSharedMap.bounds;
    // Use saved position for initial bounds so IndexedDB cache can serve data immediately
    try {
      const saved = localStorage.getItem('fricamp_last_position');
      if (saved) {
        const { latitude, longitude, zoom } = JSON.parse(saved);
        if (typeof latitude === 'number' && typeof longitude === 'number') {
          // Approximate degrees visible on actual device screen at this zoom level.
          // Tile size is 256px; 1 tile covers 360/2^z degrees of longitude.
          // 40% padding ensures spots near the viewport edge load eagerly.
          const z = typeof zoom === 'number' ? zoom : MAP_ZOOM_LEVEL;
          const screenH = typeof window !== 'undefined' ? (window.innerHeight || 700) : 700;
          const screenW = typeof window !== 'undefined' ? (window.innerWidth || 400) : 400;
          const tilePx = 256;
          const latSpan = (screenH / tilePx) * (180 / Math.pow(2, z)) * 1.4;
          const lonSpan = (screenW / tilePx) * (360 / Math.pow(2, z)) * 1.4;
          return { latMin: latitude - latSpan / 2, latMax: latitude + latSpan / 2, lonMin: longitude - lonSpan / 2, lonMax: longitude + lonSpan / 2 };
        }
      }
    } catch {}
    return { latMin: MAP_DEFAULT_CENTER.latitude - 0.1, latMax: MAP_DEFAULT_CENTER.latitude + 0.1, lonMin: MAP_DEFAULT_CENTER.longitude - 0.2, lonMax: MAP_DEFAULT_CENTER.longitude + 0.2 };
  });
  const prevBoundsRef = useRef<MapBounds | null>(null);
  // Speiler autoNav.active slik at handleMapMove (definert før autoNav) kan struping
  // av bounds-drevet fetch mens «kjøremodus» følger GPS-prikken kontinuerlig.
  const autoNavActiveRef = useRef(false);
  // Auto-nav-kandidater: kjappe punkter markert mens man kjører, til gjennomgang i ettertid.
  const [autonavCandidates, setAutonavCandidates] = useState<AutonavCandidate[]>([]);
  const [showReviewCandidates, setShowReviewCandidates] = useState(false);
  const reviewingCandidateIdRef = useRef<string | null>(null);

  const [mapCommand, setMapCommand] = useState<{ center: Coordinates; zoom: number; bounds?: [[number, number], [number, number]]; offsetY?: number; id: number } | null>(
    resolvedSharedWaypoint
      ? {
        center: { latitude: resolvedSharedWaypoint.latitude, longitude: resolvedSharedWaypoint.longitude },
        zoom: 14,
        id: Date.now()
      }
      : parsedSharedMap
        ? {
          center: parsedSharedMap.center,
          zoom: parsedSharedMap.zoom,
          id: Date.now()
        }
        : null
  );

  // Innhold som er framhevet via «Vis på kart» (mappe). Tegnes alltid på kartet,
  // også linkede punkter som ellers vises via spot-laget når spoten er lastet.
  const [focusedContent, setFocusedContent] = useState<any[]>([]);

  const [coordinatePin, setCoordinatePin] = useState<Coordinates | null>(
    resolvedSharedWaypoint ? { latitude: resolvedSharedWaypoint.latitude, longitude: resolvedSharedWaypoint.longitude } : null
  );
  const [discoverHighlight, setDiscoverHighlight] = useState<Coordinates | null>(null);
  const [discoverSelectedId, setDiscoverSelectedId] = useState<string | null>(null);
  const [discoverHiddenCats, setDiscoverHiddenCats] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('discover_hidden_cats') || '[]')); }
    catch { return new Set(); }
  });
  const [barrierHighlightSpot, setBarrierHighlightSpot] = useState<CampingSpot | null>(null);

  const [routes, setRoutes] = useState<GpxRoute[]>([]);
  const [routeVehicleFilterOff, setRouteVehicleFilterOff] = useState(false);
  const [activeRouteIds, setActiveRouteIds] = useState<Set<string>>(new Set());
  const [activeRouteGeometry, setActiveRouteGeometry] = useState<Coordinates[] | null>(null);
  const [analyzingRouteIds, setAnalyzingRouteIds] = useState<Set<string>>(new Set());
  const [selectedRouteId, setSelectedRouteId] = useState<string | null>(null);
  const [routeReturnContext, setRouteReturnContext] = useState<RouteReturnContext | null>(null);
  const [routeFacilityLoading, setRouteFacilityLoading] = useState<RouteFacilityLoadingState>({
    routeId: null,
    fricamp: false,
    restAreas: false,
    campsites: false,
    attractions: false,
    chargers: false,
    wildlife: false
  });
  const [pendingRouteId, setPendingRouteId] = useState<string | null>(resolvedRouteId);
  const [pendingOpenSpotId, setPendingOpenSpotId] = useState<string | null>(resolvedOpenSpotId);
  const [pendingOpenSpotAutoFavorite, setPendingOpenSpotAutoFavorite] = useState<boolean>(() => !!resolvedOpenSpotId && pendingOpenSpotAutoFavoriteParam);
  const pendingOpenSpotSelectionRef = useRef<Set<string>>(new Set());
  const pendingOpenSpotFetchRef = useRef<Set<string>>(new Set());
  const pendingOpenSpotFavoriteRef = useRef<Set<string>>(new Set());

  const [previewRegion, setPreviewRegion] = useState<RouteRegion | null>(null);
  const [previewFilteredRouteIds, setPreviewFilteredRouteIds] = useState<Set<string> | null>(null);
  const [loadingRouteId, setLoadingRouteId] = useState<string | null>(null);
  const [editingRoute, setEditingRoute] = useState<GpxRoute | null>(null);
  const [preloadedRouteFile, setPreloadedRouteFile] = useState<File | null>(null);
  const [preloadedRouteMetadata, setPreloadedRouteMetadata] = useState<any>(undefined);
  const [isRegionLoading, setIsRegionLoading] = useState(false);

  const [editingSpot, setEditingSpot] = useState<CampingSpot | undefined>(undefined);

  const [activeAnalysisIds, setActiveAnalysisIds] = useState<Set<string>>(new Set());
  const [wikiSpots, setWikiSpots] = useState<WikiSpot[]>([]);

  const DEFAULT_LAYERS = {
    topo: true,
    satellite: false,
    protected: false,
    warnings: false,
    hiking: false,
    hikingFoot: false,
    hikingSki: false,
    hikingBike: false,
    hikingOther: false,
    restAreas: true,
    osmAmenities: false,
    commercialCampsites: true,
    commercialRvParks: true,
    barriers: false,
    chargers: false,
    dumpStations: false,
    wasteStations: false,
    bathingPlaces: false,
    coastal: false,
    mountainRoads: false,
    gpxRoutes: false,
    winterRoads: false,
    algorithmic: true,
    wildlife: false,
    fishing: false,
    vehicleFiltered: false,
    propertyBoundaries: false,
    precipitationRadar: false,
    hillshade: false,
    webcams: false,
    trafficEvents: false,
    discoverAttractions: false,
    forestRoads: false,
    tractorRoads: false,
    serviceRoads: false,
    playgrounds: false,
    // Danske parkeringsplasser fra GeoDanmark. Statiske geocellefiler bak
    // Hosting-CDN-en, ikke Firestore — se services/parkingDkService.ts.
    parkingDk: false
  };

  // Migrate the legacy single `hiking` toggle to the per-type sub-toggles: a returning
  // user who had "Turstier" on keeps all route types on.
  const migrateLayers = (parsed: Record<string, any>) => {
    const merged = { ...DEFAULT_LAYERS, ...parsed };
    const hasSubKeys = ['hikingFoot', 'hikingSki', 'hikingBike', 'hikingOther'].some(k => k in parsed);
    if (parsed.hiking && !hasSubKeys) {
      merged.hikingFoot = true;
      merged.hikingSki = true;
      merged.hikingBike = true;
      merged.hikingOther = true;
    }
    return merged;
  };

  const [layers, setLayers] = useState(() => {
    // Delingslenker for kartutsnitt vises med satellittvisning
    if (parsedSharedMap) {
      return { ...DEFAULT_LAYERS, topo: false, satellite: true };
    }
    try {
      const saved = window.localStorage.getItem('fricamp_map_layers');
      if (saved) {
        return migrateLayers(JSON.parse(saved));
      }
    } catch { /* ignore */ }

    return DEFAULT_LAYERS;
  });

  const [highlightedRoad, setHighlightedRoad] = useState<any>(null);
  const [isLoadingRoad, setIsLoadingRoad] = useState(false);
  const [routeBuilderSegments, setRouteBuilderSegments] = useState<any[]>([]);
  const [creatingRouteSpotId, setCreatingRouteSpotId] = useState<string | null>(null);
  const [isCreatingSpotRoute, setIsCreatingSpotRoute] = useState(false);
  const [routeCreationStatus, setRouteCreationStatus] = useState<{ type: 'loading' | 'success' | 'error'; text: string } | null>(null);
  const [routePlanningSpot, setRoutePlanningSpot] = useState<CampingSpot | null>(null);
  const [routeStartMode, setRouteStartMode] = useState<'current' | 'address' | 'map_pick'>('current');
  const [routeAddressQuery, setRouteAddressQuery] = useState('');
  const [routeAddressResults, setRouteAddressResults] = useState<any[]>([]);
  const [routeAddressLoading, setRouteAddressLoading] = useState(false);
  const [routeAddressStart, setRouteAddressStart] = useState<Coordinates | null>(null);
  const [routeAddressLabel, setRouteAddressLabel] = useState('');
  const [routeAddressLockedQuery, setRouteAddressLockedQuery] = useState('');
  const [routeAddressConfirmed, setRouteAddressConfirmed] = useState(false);
  const [routeMapStart, setRouteMapStart] = useState<Coordinates | null>(null);
  const [routeMapStartLabel, setRouteMapStartLabel] = useState('');
  const [isPickingRouteStartOnMap, setIsPickingRouteStartOnMap] = useState(false);
  const [routePendingSpotForMapPick, setRoutePendingSpotForMapPick] = useState<CampingSpot | null>(null);
  const [showRoutePlannerHint, setShowRoutePlannerHint] = useState(false);
  const [navChoiceTarget, setNavChoiceTarget] = useState<{ coords: Coordinates; name: string } | null>(null);
  const [filterVerified, setFilterVerified] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const toggleFilter = (id: string) => { setActiveFilters(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; }); logFeatureUsage('filter_change'); };
  const clearFilters = () => setActiveFilters(new Set());

  const { currentUser, setCurrentUser, authReady, favoriteSpotIds, visits, visitedSpotIds, collaborativeMaps, notifications, unreadNotificationsCount, logoutUser } = useFirebaseSync();
  // Sikkerhet: Ukjent Firestore-tilstand skal ikke gi premium-tilgang.
  // PremiumNudge holdes uansett tilbake av separat _firestoreReady-sjekk
  // (se f.eks. linje 2491) så vi unngår nudge-flash for ekte testere.
  const isPaidUser = useMemo(
    () => currentUser?._firestoreReady ? hasPaidAccess(currentUser, false) : false,
    [currentUser]
  );
  const isPremiumUser = useMemo(() => checkPremiumAccess(currentUser, false), [currentUser]);
  const hasPremiumAccess = useCallback((u: AppUser | null) => checkPremiumAccess(u, false), []);
  const hasBathingLayerAccess = useMemo(() => hasPaidAccess(currentUser, false, 'bathing_places') || hasTierAccess(currentUser, 'bathing_places'), [currentUser]);
  const dismissLayerTip = useCallback(() => setShowLayerTip(false), []);

  useEffect(() => {
    if (!authReady || currentUser || layerTipShownThisSessionRef.current) return;

    try {
      if (window.sessionStorage.getItem('fricamp_map_layers_tip_guest_seen_v1') === '1') return;
      window.sessionStorage.setItem('fricamp_map_layers_tip_guest_seen_v1', '1');
    } catch {
      // If sessionStorage is unavailable, still show the lightweight hint once this session.
    }

    layerTipShownThisSessionRef.current = true;
    const timer = window.setTimeout(() => setShowLayerTip(true), 900);
    return () => window.clearTimeout(timer);
  }, [authReady, currentUser]);

  useEffect(() => {
    if (!authReady || !currentUser?.uid) return;
    if (layerTipLastLoginUidRef.current === currentUser.uid) return;

    layerTipLastLoginUidRef.current = currentUser.uid;
    const storageKey = `fricamp_map_layers_tip_login_count_v1_${currentUser.uid}`;
    let count = 0;

    try {
      const stored = Number(window.localStorage.getItem(storageKey) || '0');
      count = Number.isFinite(stored) ? stored : 0;
      if (count >= 3) return;
      window.localStorage.setItem(storageKey, String(count + 1));
    } catch {
      // Local storage failures should not block the onboarding hint.
    }

    const delay = layerTipShownThisSessionRef.current ? 3500 : 900;
    layerTipShownThisSessionRef.current = true;
    const timer = window.setTimeout(() => setShowLayerTip(true), delay);
    return () => window.clearTimeout(timer);
  }, [authReady, currentUser?.uid]);

  // Tester opt-in for Sweden (persisted in localStorage)
  // Testerens/adminens egne av/på-brytere for beta-land (standard: alle PÅ).
  const [betaCountryPrefs, setBetaCountryPrefs] = useState(readBetaCountryPrefs);
  useEffect(() => {
    const handler = () => setBetaCountryPrefs(readBetaCountryPrefs());
    window.addEventListener(BETA_COUNTRY_EVENT, handler);
    return () => window.removeEventListener(BETA_COUNTRY_EVENT, handler);
  }, []);

  // Admin og testere får Sverige, Danmark og Finland UTEN å skru på noe selv —
  // bryterne i profilen kan bare skjule beta-land, aldri åpne dem. Reglene bor i
  // utils/betaCountries.ts og er testet der.
  const effectiveNordicConfig = useMemo(() => {
    // Eier-e-post gir alltid alle land. currentUser.email settes i ALLE fire
    // auth-stiene (preliminary/timeout/onSnapshot/error), mens isAdmin/isTester
    // kan falle bort hvis Firestore-user-doc-snapshotet feiler/tar for lang tid
    // (f.eks. når nettleserens tracking-prevention bremser IndexedDB). Denne
    // sjekken gjør Finland-synlighet robust for eieren uansett auth-utfall.
    const ownerEmail = ['jonborgentakle@gmail.com', 'dicpnorway@gmail.com']
      .includes((currentUser?.email || '').toLowerCase());

    // Rollen HUSKES per bruker. Uten det mistet admin Danmark i det øyeblikket
    // brukerdokumentet ikke kunne leses — målt i produksjon 15.08.2026, der en
    // konto som er `isAdmin: true` i basen fikk `{"sweden":true,"denmark":false}`
    // fordi en nettleserutvidelse blokkerte firestore.googleapis.com.
    const rolle = resolveRole(currentUser);

    return resolveCountryAccess({
      launched: nordicConfig,
      role: rolle,
      ownerEmail,
      prefs: betaCountryPrefs,
    });
  }, [currentUser, nordicConfig, betaCountryPrefs]);

  // Si HVEM økten kjørte som når et land er stengt. Uten dette er «kartet er
  // tomt over Danmark» umulig å skille fra «du er logget inn med feil konto»,
  // «brukerdokumentet ble ikke lest» og «landet er ikke lansert» — tre helt
  // ulike årsaker med samme symptom. Logges bare når noe faktisk er stengt.
  useEffect(() => {
    const alleÅpne = effectiveNordicConfig.sweden && effectiveNordicConfig.denmark
      && effectiveNordicConfig.finland && effectiveNordicConfig.iceland;
    if (alleÅpne || !nordicConfigReady) return;
    const rolle = resolveRole(currentUser);
    console.warn(`[land-gating] økten kjører som ${currentUser?.email || 'IKKE INNLOGGET'}`
      + ` — isAdmin=${rolle.isAdmin}, isTester=${rolle.isTester}.`
      + ` Uten admin/tester gjelder lanseringsbryteren system/nordic_config:`
      + ` ${JSON.stringify(nordicConfig)}. Aktive land i økten: ${JSON.stringify(effectiveNordicConfig)}.`);
  }, [effectiveNordicConfig, currentUser, nordicConfig, nordicConfigReady]);

  const { activeCountry: nordicCountry, checkCoords: checkNordic, dismiss: dismissNordic } = useNordicComingSoon(effectiveNordicConfig);

  // Søk kun i aktiverte land. Admin og testere får alltid treff i SE+DK.
  const searchBypassGating = useMemo(
    () => isAdminUser(currentUser) || currentUser?.isTester === true,
    [currentUser]
  );
  const searchCountries = useMemo(() => {
    const countries = ['no'];
    if (effectiveNordicConfig.sweden || searchBypassGating) countries.push('se');
    if (effectiveNordicConfig.denmark || searchBypassGating) countries.push('dk');
    if (effectiveNordicConfig.finland || searchBypassGating) countries.push('fi');
    if (effectiveNordicConfig.iceland || searchBypassGating) countries.push('is');
    return countries;
  }, [effectiveNordicConfig, searchBypassGating]);

  // Suppress viewport spot fetching when previewing a region (only route lines needed)
  const spotManagerBounds = previewRegion ? null : currentViewBounds;
  // Kjøretilstanden (followRouteState, autoNav) defineres lenger ned i filen, så den
  // sendes inn som ref og settes av en effekt der. useSpotManager leser refen i det
  // øyeblikket utsnittet endrer seg — som er når svaret faktisk trengs.
  const isDrivingRef = useRef(false);
  // Settes av handleMapMove: true bare når BRUKEREN dro kartet (Leaflet `dragend`),
  // ikke ved programmatiske flytt fra søk eller «vis i kart». Uten dette ville
  // «Søk i dette området» dukket opp rett etter at brukeren søkte etter et sted.
  const userDraggedRef = useRef(false);
  const { spots, setSpots, isLoading, setIsLoading, isFetchingBounds, spotsReady, isFirstTimeLoad, scanProgress, scanArea, updateSpotLocally, removeSpotLocally, clearAlgorithmicSpots, needsManualSearch, searchHere, searchCircle } = useSpotManager(spotManagerBounds, favoriteSpotIds, mapZoom, currentUser?.uid, effectiveNordicConfig, isDrivingRef, userDraggedRef);

  /**
   * Viser lasting til kartet er FERDIG, ikke bare til nettverket er det.
   *
   * `isFetchingBounds` slår av i det svaret er lest, men da gjenstår den lange
   * delen: å bygge og klynge markørene. Målt 16.08.2026 forsvant indikatoren etter
   * 0,3 s mens kartet fortsatt jobbet. Se utils/loadingTail.
   */
  const lastehaleRef = useRef(initialLoadingTail());
  const [viserLasting, setViserLasting] = useState(false);
  useEffect(() => {
    setViserLasting(skalViseLaster(isFetchingBounds, lastehaleRef.current, Date.now()));
    if (isFetchingBounds) return;
    // Halen slukker ikke av seg selv — den trenger en oppvekking når tiden er ute.
    const t = setTimeout(
      () => setViserLasting(skalViseLaster(false, lastehaleRef.current, Date.now())),
      LOADING_TAIL_MS + 50,
    );
    return () => clearTimeout(t);
  }, [isFetchingBounds]);

  // Live-lytter på private spots (owner-only subcollection).
  // onSnapshot sikrer at punkter opprettet på Android (eller andre enheter)
  // umiddelbart dukker opp på desktop uten reload.
  useEffect(() => {
    if (!currentUser?.uid) return;
    const uid = currentUser.uid;
    const colRef = collection(db, 'users', uid, 'private_spots');
    const unsubscribe = onSnapshot(
      colRef,
      (snap) => {
        if (snap.empty) return;
        const privateSpots = snap.docs.map(d => ({
          ...(d.data() as import('./types').CampingSpot),
          id: d.id,
          visibility: 'private' as const,
        }));
        setSpots(prev => mergeSpotsByIdentity(prev, privateSpots));
      },
      (err) => {
        console.warn('[privateSpots] onSnapshot failed:', err);
      }
    );
    return () => unsubscribe();
  }, [currentUser?.uid, setSpots]);

  const routeLoadKeyRef = useRef<string | null>(null);

  // When a route becomes active on the map, load route facilities directly
  // so users see route context immediately even without manual layer toggles.
  // Facility loading is deferred until a route card is opened (selectedRouteId set).
  const handleRouteActivated = useCallback((
    coords: Coordinates[] | null,
    routeContext?: { routeId?: string | null; expectedWildSpots?: number }
  ) => {
    setActiveRouteGeometry(coords);
    if (!coords || coords.length === 0) {
      routeLoadKeyRef.current = null;
      setRouteFacilityLoading({
        routeId: null,
        fricamp: false,
        restAreas: false,
        campsites: false,
        attractions: false,
        chargers: false,
        wildlife: false
      });
      return;
    }

    // Only load facilities when a route card is opened (selectedRouteId set),
    // not when routes are just toggled on the map or region is previewed.
    const contextRouteId0 = routeContext?.routeId ?? selectedRouteId ?? null;
    if (!contextRouteId0) return;

    const routePath = coords.filter(
      (c) => Number.isFinite(c.latitude) && Number.isFinite(c.longitude)
    );
    if (routePath.length === 0) return;

    const lats = routePath.map(c => c.latitude);
    const lons = routePath.map(c => c.longitude);
    const routeBounds: MapBounds = {
      latMin: Math.min(...lats) - 0.03,
      latMax: Math.max(...lats) + 0.03,
      lonMin: Math.min(...lons) - 0.03,
      lonMax: Math.max(...lons) + 0.03
    };
    const contextRouteId = routeContext?.routeId ?? selectedRouteId ?? 'route-context';
    const routeKey = `${contextRouteId}:${routeBounds?.latMin?.toFixed(3)}:${routeBounds?.latMax?.toFixed(3)}:${routeBounds?.lonMin?.toFixed(3)}:${routeBounds?.lonMax?.toFixed(3)}`;
    if (routeLoadKeyRef.current === routeKey) return;
    routeLoadKeyRef.current = routeKey;

    const selectedRoute = selectedRouteId ? routes.find(r => r.id === selectedRouteId) : null;
    const expectedWildSpots = Number(routeContext?.expectedWildSpots ?? selectedRoute?.stats?.wildSpotsCount ?? 0);
    const loadingRouteId = routeContext?.routeId ?? selectedRoute?.id ?? selectedRouteId ?? null;
    setRouteFacilityLoading({
      routeId: loadingRouteId,
      fricamp: expectedWildSpots > 0,
      restAreas: true,
      campsites: true,
      attractions: false,
      chargers: false,
      wildlife: false
    });

    const patchRouteLoading = (patch: Partial<RouteFacilityLoadingState>) => {
      if (routeLoadKeyRef.current !== routeKey) return;
      setRouteFacilityLoading((prev) => {
        if (prev.routeId && loadingRouteId && prev.routeId !== loadingRouteId) return prev;
        return { ...prev, ...patch };
      });
    };

    const isNearRoute = (point: Coordinates, distanceMeters: number) =>
      isPointNearPolyline(point, routePath, distanceMeters);

    const mergeRouteSpots = (incoming: CampingSpot[], distanceMeters: number) => {
      if (routeLoadKeyRef.current !== routeKey || incoming.length === 0) return;
      const alongRoute = incoming.filter((s) => isNearRoute(s.location, distanceMeters));
      if (alongRoute.length === 0) return;
      setSpots(prev => mergeSpotsByIdentity(prev, alongRoute));

      if (!loadingRouteId) return;
      setRoutes((prev) =>
        prev.map((route) => {
          if (route.id !== loadingRouteId) return route;
          const existingSpotIds = Array.isArray(route.associatedSpotIds) ? route.associatedSpotIds : [];
          const newRouteSpots = alongRoute.filter((spot) => !existingSpotIds.includes(spot.id));
          const mergedSpotIds = Array.from(new Set([...existingSpotIds, ...alongRoute.map((spot) => spot.id)]));
          const wildSpotsDelta = newRouteSpots.filter((spot) =>
            spot.spotType === 'algorithmic' || spot.spotType === 'confirmed' || spot.spotType === 'coastal'
          ).length;
          const campsitesDelta = newRouteSpots.filter((spot) => spot.spotType === 'commercial_campsite').length;
          const restAreasDelta = newRouteSpots.filter((spot) => spot.spotType === 'rasteplass').length;
          const chargersDelta = newRouteSpots.filter((spot) =>
            Array.isArray(spot.features) && spot.features.some((feature) => String(feature).toUpperCase().includes('LADING'))
          ).length;
          const attractionsDelta = newRouteSpots.filter((spot) => spot.spotType === 'attraction').length;
          const existingStats = route.stats || {
            wildSpotsCount: 0,
            campsitesCount: 0,
            restAreasCount: 0,
            attractionsCount: 0,
            chargersCount: 0,
            wildlifeCount: 0,
            totalSpots: 0,
          };

          return {
            ...route,
            associatedSpotIds: mergedSpotIds,
            stats: {
              ...existingStats,
              wildSpotsCount: (existingStats.wildSpotsCount || 0) + wildSpotsDelta,
              campsitesCount: (existingStats.campsitesCount || 0) + campsitesDelta,
              restAreasCount: (existingStats.restAreasCount || 0) + restAreasDelta,
              attractionsCount: (existingStats.attractionsCount || 0) + attractionsDelta,
              chargersCount: (existingStats.chargersCount || 0) + chargersDelta,
              totalSpots:
                (existingStats.wildSpotsCount || 0) + wildSpotsDelta +
                (existingStats.campsitesCount || 0) + campsitesDelta +
                (existingStats.restAreasCount || 0) + restAreasDelta +
                (existingStats.attractionsCount || 0) + attractionsDelta +
                (existingStats.chargersCount || 0) + chargersDelta +
                (existingStats.wildlifeCount || 0),
            },
          };
        })
      );
    };

    const patchLiveRouteRestAreas = (liveCount: number) => {
      if (!loadingRouteId || !Number.isFinite(liveCount) || liveCount <= 0) return;
      if (routeLoadKeyRef.current !== routeKey) return;

      setRoutes((prev) =>
        prev.map((r) => {
          if (r.id !== loadingRouteId) return r;

          const currentStats = r.stats || {
            wildSpotsCount: 0,
            campsitesCount: 0,
            restAreasCount: 0,
            totalSpots: 0
          };
          const currentRestAreas = currentStats.restAreasCount || 0;
          const nextRestAreas = Math.max(currentRestAreas, liveCount);
          if (nextRestAreas === currentRestAreas) return r;

          const nextTotal =
            (currentStats.wildSpotsCount || 0) +
            (currentStats.campsitesCount || 0) +
            nextRestAreas +
            (currentStats.attractionsCount || 0) +
            (currentStats.chargersCount || 0) +
            (currentStats.wildlifeCount || 0);

          return {
            ...r,
            stats: {
              ...currentStats,
              restAreasCount: nextRestAreas,
              totalSpots: Math.max(currentStats.totalSpots || 0, nextTotal)
            }
          };
        })
      );
    };

    const loadFricampFallback = async () => {
      if (expectedWildSpots <= 0) return;
      const maxTerrainSlopePercent = 9;

      const latSpan = routeBounds.latMax - routeBounds.latMin;
      const lonSpan = routeBounds.lonMax - routeBounds.lonMin;
      const midLat = (routeBounds.latMin + routeBounds.latMax) / 2;
      const areaKm2 = latSpan * 111 * lonSpan * 111 * Math.cos((midLat * Math.PI) / 180);

      if (!Number.isFinite(areaKm2) || areaKm2 <= 0) return;

      const toChunkBounds = (coords: Coordinates[], padding = 0.02): MapBounds | null => {
        if (!coords || coords.length < 2) return null;
        const lats = coords.map(c => c.latitude);
        const lons = coords.map(c => c.longitude);
        return {
          latMin: Math.max(-90, Math.min(...lats) - padding),
          latMax: Math.min(90, Math.max(...lats) + padding),
          lonMin: Math.max(-180, Math.min(...lons) - padding),
          lonMax: Math.min(180, Math.max(...lons) + padding)
        };
      };

      const getChunkBounds = (): MapBounds[] => {
        // Small/medium routes can use one query directly.
        if (areaKm2 <= 3000) return [routeBounds];

        // Large routes are split into ~40km chunks to avoid timeouts on one giant bbox.
        const chunkDistanceMeters = 40_000;
        const maxChunks = 8;
        const chunks: MapBounds[] = [];

        let startIndex = 0;
        let accumDist = 0;
        for (let i = 1; i < routePath.length; i++) {
          const prev = routePath[i - 1];
          const curr = routePath[i];
          accumDist += getDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
          const shouldSplit = accumDist >= chunkDistanceMeters && chunks.length < maxChunks - 1;
          if (!shouldSplit) continue;

          const bounds = toChunkBounds(routePath.slice(startIndex, i + 1), 0.02);
          if (bounds) chunks.push(bounds);
          startIndex = i;
          accumDist = 0;
        }

        const tail = toChunkBounds(routePath.slice(startIndex), 0.02);
        if (tail) chunks.push(tail);
        return chunks.length > 0 ? chunks : [routeBounds];
      };

      const chunkBounds = getChunkBounds();
      const enrichRouteCandidates = async (incoming: CampingSpot[], concurrency = 4): Promise<CampingSpot[]> => {
        if (incoming.length === 0) return [];
        const output: (CampingSpot | null)[] = new Array(incoming.length).fill(null);
        let cursor = 0;

        const worker = async () => {
          while (cursor < incoming.length) {
            const idx = cursor++;
            const spot = incoming[idx];
            if (!spot) continue;
            if (spot.spotType !== 'algorithmic' && spot.spotType !== 'confirmed') {
              output[idx] = spot;
              continue;
            }

            try {
              const slope = await estimateTerrainSlopePercent(spot.location.latitude, spot.location.longitude, 8);
              if (typeof slope === 'number' && slope > maxTerrainSlopePercent) {
                output[idx] = null;
                continue;
              }
              output[idx] = typeof slope === 'number'
                ? { ...spot, terrainSlopePercent: slope }
                : spot;
            } catch {
              output[idx] = spot;
            }
          }
        };

        await Promise.all(
          Array.from({ length: Math.max(1, Math.min(concurrency, incoming.length)) }, () => worker())
        );

        return output.filter((s): s is CampingSpot => !!s);
      };

      for (const bounds of chunkBounds) {
        if (routeLoadKeyRef.current !== routeKey) return;
        try {
          const candidates = await fetchCandidateSpots(bounds, { minDistanceHouses: 150, nearWater: false }, []);
          if (routeLoadKeyRef.current !== routeKey || candidates.length === 0) continue;
          const fricampCandidates = candidates.filter(
            (s) => s.spotType === 'algorithmic' || s.spotType === 'confirmed'
          );
          const slopeFilteredCandidates = await enrichRouteCandidates(fricampCandidates, 4);
          // Slightly wider corridor for chunked fallback due coarse chunk bounds.
          mergeRouteSpots(slopeFilteredCandidates, 2000);
        } catch (e) {
          console.warn("Fricamp fallback fetch for route chunk failed:", e);
        }
      }
    };

    getCachedSpotsInArea(routeBounds)
      .then(async (cached) => {
        mergeRouteSpots(cached, 1800);
        const cachedWildAlongRoute = cached.filter((s) =>
          (s.spotType === 'algorithmic' || s.spotType === 'confirmed') &&
          isNearRoute(s.location, 1800)
        ).length;

        // If route stats promise many Fricamp points but cached data does not contain them,
        // fetch live candidate Fricamp spots for this route.
        if (expectedWildSpots > 0 && cachedWildAlongRoute < Math.min(6, expectedWildSpots)) {
          await loadFricampFallback();
        }
      })
      .catch(async (e) => {
        console.warn("Failed to load cached route spots:", e);
        await loadFricampFallback();
      })
      .finally(() => patchRouteLoading({ fricamp: false }));

    fetchAllOverpassPOIs(routeBounds)
      .then(({ campsites, amenities }) => {
        mergeRouteSpots([...campsites, ...amenities], 1600);
      })
      .catch((e) => console.warn("Failed to load Overpass route POIs:", e))
      .finally(() => patchRouteLoading({ campsites: false }));

    fetchNVDBData(routeBounds)
      .then((nvdbRes) => {
        const restAreaSpots: CampingSpot[] = nvdbRes.objects.map((obj) => {
          const isSnuplass = obj.typeId === 40;
          const restScore = isSnuplass ? 0 : calculateRestAreaScore(obj.rasteplassInfo);
          const canSleep = !isSnuplass && obj.rasteplassInfo?.toalett === true && obj.rasteplassInfo?.vinterstengt === false;

          return {
            id: `nvdb-${obj.id}`,
            source: 'nvdb',
            externalId: String(obj.id),
            importVersion: 'nvdb-v4',
            lastSyncedAt: new Date().toISOString(),
            name: obj.name || (isSnuplass ? "Snuplass" : "Rasteplass"),
            spotType: 'rasteplass',
            sourceLevel: 1,
            location: obj.location,
            accessRoute: obj.geometryPoints || undefined,
            nearestHouse: 999,
            description: isSnuplass ? "Verifisert snuplass eller lomme (SVV)." : `Verifisert ${obj.rasteplassInfo?.type?.toLowerCase() || 'rasteplass'} (SVV).`,
            score: restScore,
            suitabilityColor: '#2563eb',
            features: [isSnuplass ? "SNUPLASS" : "RASTEPLASS", "NVDB"],
            reasoning: "Offentlig verifisert objekt fra Vegvesenet.",
            trafficRisk: "Normal",
            buildingProximity: "N/A",
            highway: "Veg",
            surfaceType: 'Asfalt',
            isPublicAccess: true,
            isSleepFriendly: canSleep,
            rasteplassMetadata: obj.rasteplassInfo,
            legal: {
              isUtmark: true,
              inProtectedArea: false,
              hasBarrier: false,
              distToFarmland: 999,
              nearMajorRoad: true
            }
          } as CampingSpot;
        });
        const liveRestAreasAlongRoute = restAreaSpots.reduce((count, spot) => (
          isNearRoute(spot.location, 1600) ? count + 1 : count
        ), 0);
        patchLiveRouteRestAreas(liveRestAreasAlongRoute);
        mergeRouteSpots(restAreaSpots, 1600);
      })
      .catch((e) => console.warn("Failed to load NVDB route rest areas:", e))
      .finally(() => patchRouteLoading({ restAreas: false }));
  }, [setSpots, selectedRouteId, routes]);

  const handleLoadCachedRouteSpotsByIds = useCallback((spotIds: string[]) => {
    if (!spotIds || spotIds.length === 0) return;
    fetchSpotsByIds(spotIds)
      .then((loaded) => {
        if (loaded.length === 0) return;
        setSpots(prev => mergeSpotsByIdentity(prev, loaded));
      })
      .catch((e) => console.warn("Failed to load route spot IDs:", e));
  }, [setSpots]);

  // Toast når bruker kommer tilbake online
  useEffect(() => {
    if (prevOfflineRef.current && !isOffline) {
      addToast(t('offline.back_online', 'Du er tilbake online'), 'success');
    } else if (!prevOfflineRef.current && isOffline) {
      addToast(t('offline.went_offline', 'Du er offline — lagrede kart er fortsatt tilgjengelig'), 'warning');
    }
    prevOfflineRef.current = isOffline;
  }, [isOffline, addToast, t]);

  useEffect(() => {
    if (currentUser) {
      const hasSeen = localStorage.getItem('fricamp_has_seen_gamification_reveal');
      if (!hasSeen) {
        const timer = setTimeout(() => setShowFeatureReveal(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [currentUser]);

  const handleCloseFeatureReveal = () => {
    localStorage.setItem('fricamp_has_seen_gamification_reveal', 'true');
    setShowFeatureReveal(false);
  };

  const showContributionFeedback = useCallback((
    kind: RewardEventKind,
    spot: CampingSpot,
    message: string,
    rewardPoints: number,
    progress?: VisitProgressResult
  ) => {
    const vc = currentUser?.verifiedSpotCount || 0;
    const currentTier = progress?.currentTier || getUserTier(visits, vc);
    const nextTier = progress?.nextTier ?? getNextTier(visits, vc);
    const uniqueSpots = progress?.uniqueSpots ?? new Set(visits.map(v => v.spotId)).size;
    const spotsToNextTier = progress?.spotsToNextTier ?? (nextTier ? Math.max(0, nextTier.threshold - contributionScore(visits, vc)) : 0);

    setContributionToast({
      kind,
      title: spot.name || t('spot.unnamed', 'Ukjent plass'),
      message,
      rewardPoints,
      currentTier,
      nextTier,
      uniqueSpots,
      spotsToNextTier,
    });

    if (contributionToastTimerRef.current) clearTimeout(contributionToastTimerRef.current);
    contributionToastTimerRef.current = setTimeout(() => {
      setContributionToast(null);
      contributionToastTimerRef.current = null;
    }, 7000);
  }, [t, visits, currentUser?.verifiedSpotCount]);

  useEffect(() => {
    const handleRewardEvent = (event: Event) => {
      const payload = (event as CustomEvent<RewardEventPayload>).detail;
      if (!payload) return;
      const vc = currentUser?.verifiedSpotCount || 0;
      const currentTier = getUserTier(visits, vc);
      const nextTier = getNextTier(visits, vc);
      const uniqueSpots = new Set(visits.map(v => v.spotId)).size;
      const spotsToNextTier = nextTier ? Math.max(0, nextTier.threshold - contributionScore(visits, vc)) : 0;

      setContributionToast({
        kind: payload.kind,
        title: payload.title,
        message: payload.message,
        rewardPoints: payload.rewardPoints,
        currentTier,
        nextTier,
        uniqueSpots,
        spotsToNextTier,
      });

      if (contributionToastTimerRef.current) clearTimeout(contributionToastTimerRef.current);
      contributionToastTimerRef.current = setTimeout(() => {
        setContributionToast(null);
        contributionToastTimerRef.current = null;
      }, 7000);
    };

    window.addEventListener(REWARD_EVENT_NAME, handleRewardEvent);
    return () => window.removeEventListener(REWARD_EVENT_NAME, handleRewardEvent);
  }, [visits, currentUser?.verifiedSpotCount]);

  useEffect(() => { logFeatureUsage('map_view'); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Stripe checkout verification safety net — if webhook failed, verify session client-side
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isSuccess = params.get('success') === 'true';
    const sessionId = params.get('session_id');

    if (!isSuccess || !sessionId || !currentUser?.uid || !currentUser._firestoreReady) return;

    // Clean URL immediately to prevent re-triggering
    params.delete('success');
    params.delete('session_id');
    params.delete('canceled');
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    if (currentUser.subscriptionStatus === 'paid') {
      addToast(t('checkout.verification_success'), 'success');
      return;
    }

    verifyStripeCheckout(sessionId)
      .then((result) => {
        if (result.status === 'upgraded' || result.status === 'already_paid') {
          addToast(t('checkout.verification_success'), 'success');
        }
      })
      .catch((err) => {
        console.error('Checkout verification failed:', err);
      });
  }, [currentUser?.uid, currentUser?._firestoreReady, currentUser?.subscriptionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Vipps verification safety net — speiler Stripe-nettet over. Vipps' webhook
  // er upålitelig (var aldri registrert i portalen), så når brukeren returnerer
  // fra Vipps verifiserer vi avtalestatus direkte og setter paid om aktiv.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isVippsReturn = params.get('vipps_agreement') === 'pending';

    if (!isVippsReturn || !currentUser?.uid || !currentUser._firestoreReady) return;

    // Clean URL immediately to prevent re-triggering
    params.delete('vipps_agreement');
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    if (currentUser.subscriptionStatus === 'paid') {
      addToast(t('checkout.verification_success'), 'success');
      return;
    }

    // Vipps-aktivering kan henge noen sekunder etter godkjenning — prøv på nytt.
    let cancelled = false;
    let attempts = 0;
    const tryReconcile = () => {
      if (cancelled) return;
      attempts++;
      reconcileVippsAgreement()
        .then((result) => {
          if (cancelled) return;
          if (result.status === 'upgraded' || result.status === 'already_paid') {
            addToast(t('checkout.verification_success'), 'success');
          } else if (result.status === 'not_active' && result.vippsStatus === 'PENDING' && attempts < 4) {
            setTimeout(tryReconcile, 2500);
          }
        })
        .catch((err) => {
          console.error('Vipps reconciliation failed:', err);
        });
    };
    tryReconcile();
    return () => { cancelled = true; };
  }, [currentUser?.uid, currentUser?._firestoreReady, currentUser?.subscriptionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const scanAreaRef = useRef(scanArea);
  const startedOnMobileRef = useRef(window.innerWidth < 640);
  const didStartupLocateRef = useRef(false);
  const lastPosSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sharedViewLockUntilRef = useRef(parsedSharedMap || resolvedSharedWaypoint ? Date.now() + 2500 : 0);

  // Cleanup timers on unmount to prevent setState on unmounted component
  useEffect(() => {
    return () => {
      if (contributionToastTimerRef.current) clearTimeout(contributionToastTimerRef.current);
      if (lastPosSaveTimerRef.current) clearTimeout(lastPosSaveTimerRef.current);
    };
  }, []);

  // Show toast for shared waypoint deep link
  useEffect(() => {
    if (resolvedSharedWaypoint) {
      addToast(`📍 ${resolvedSharedWaypoint.name}`, 'info');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const preserveSelectionUntilRef = useRef(0);
  useEffect(() => { scanAreaRef.current = scanArea; }, [scanArea]);

  const {
    selectedSpotId, setSelectedSpotId, activeHikingTrailId, setActiveHikingTrailId,
    showMobileSheet, setShowMobileSheet, handleSelectSpot, handleSelectSpotDetails, reanalyzeSpot,
    generatingMetaIds
  } = useSelectionManager(spots, setSpots, setMapCenter, setMapZoom, setLayers, setActiveAnalysisIds, setMapCommand, addToast, isPaidUser);

  const {
    isManualAnalyzing, isViewportAnalyzing, analysisProgress, pendingAnalysisCoords, setPendingAnalysisCoords,
    queueLength,
    handleCancelScan, handleScanWithBg, handleDeepAnalyzeViewport, handleManualAnalysis, handleReevaluateViewport, isOverlayMinimized, handleMinimize
  } = useAnalysisManager(currentViewBounds, spots, setSpots, scanArea, setIsLoading, handleSelectSpot, activeAnalysisIds, setActiveAnalysisIds, addToast);

  const {
    status: trackingStatus,
    points: trackPoints,
    trackWaypoints,
    stats: trackStats,
    gpsError,
    draftRecovery,
    restoreDraft,
    addTrackWaypoint,
    start: startTracking,
    pause: pauseTracking,
    resume: resumeTracking,
    stop: stopTracking,
    reset: resetTracking,
  } = useGpsTracking(spots, currentUser);

  // Android back-button + browser history integration
  useBackNavigation(
    {
      isAuthOpen, isProfileOpen, showUpgrade, showAdmin, showAddModal,
      showNotifications, showPackingList, showMyTracks, showTesterApplication,
      showMobileSheet, mobileView, selectedRouteId,
    },
    {
      setIsAuthOpen, setIsProfileOpen, setShowUpgrade, setShowAdmin, setShowAddModal,
      setShowNotifications, setShowPackingList, setShowMyTracks, setShowTesterApplication,
      setShowMobileSheet, onSetMobileView: setMobileView,
      onCloseRouteDetails: () => setSelectedRouteId(null),
    },
    () => addToast('Trykk tilbake igjen for å avslutte', 'info'),
  );

  // Track TrackingPanel snap state for QuickAddFAB/NavigationHUD visibility
  const [trackingPanelSnap, setTrackingPanelSnap] = useState<'mini' | 'peek' | 'half' | 'full'>('half');

  // Discover feed — weather, attractions, cultural heritage
  const discoverData = useDiscover(currentViewBounds, mapCenter, sidebarMode === 'discover' || layers.discoverAttractions);

  // Merge user-created attraction spots into discover attractions
  const mergedDiscoverAttractions = useMemo(() => {
    const userAttractionPois: DiscoverPOI[] = spots
      .filter(s => s.spotType === 'attraction' && s.location)
      .map(s => ({
        id: `user_${s.id}`,
        name: s.name,
        type: 'attraction' as const,
        category: s.attractionCategory || 'attraction',
        lat: s.location!.latitude,
        lon: s.location!.longitude,
        imageUrl: s.photoUrls?.[0],
        description: s.description,
        source: 'user' as const,
      }));
    return [...discoverData.attractions, ...userAttractionPois];
  }, [discoverData.attractions, spots]);

  // Gjenopprett uferdig turlogg fra forrige sesjon
  useEffect(() => {
    if (draftRecovery && trackingStatus === 'idle') {
      const shouldRestore = window.confirm(
        `Du har en uferdig tur (${draftRecovery.points.length} punkter, ${Math.floor(draftRecovery.elapsedSeconds / 60)} min). Vil du gjenoppta den?`
      );
      if (shouldRestore) {
        restoreDraft(draftRecovery);
      } else {
        resetTracking();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { isSharing, shareError, nearbyUsers, startSharing, stopSharing, activeGroupId, activeGroupName, groupViewers } = useLiveLocations(currentUser);


  // --- Naviger til en plass fra varsel ---
  // opts.verify: åpne plassen KLAR for verifisering (ett-tapps fra push/e-post/web).
  // Er plassen allerede verifisert, åpnes anmeldelse-modus i stedet. Vi hopper
  // bevisst over 150m-porten her — brukeren valgte eksplisitt å verifisere.
  const handleGoToSpot = useCallback(async (spotId: string, opts?: { verify?: boolean }) => {
    const openPrompt = (spot: CampingSpot) => {
      if (!opts?.verify) return;
      if (spot.isVerified) setReviewPromptSpot(spot);
      else setVerifyPromptSpot(spot);
    };
    const existing = spots.find(s => s.id === spotId);
    if (existing) {
      handleSelectSpot(existing);
      openPrompt(existing);
      return;
    }
    try {
      const loaded = await fetchSpotsByIds([spotId]);
      if (loaded.length > 0) {
        setSpots(prev => mergeSpotsByIdentity(prev, loaded));
        handleSelectSpot(loaded[0]);
        openPrompt(loaded[0]);
      }
    } catch (e) {
      console.warn('Failed to load spot from notification:', e);
    }
  }, [spots, handleSelectSpot, setSpots]);

  // --- Hurtiggodkjenning av artikkelutkast (kun admin får varselet) ---
  const [reviewArticleId, setReviewArticleId] = useState<string | null>(null);

  // --- Native push-varsler (Android/iOS) ---
  usePushNotificationListeners({
    isAuthenticated: !!currentUser,
    onOpenNotifications: () => setShowNotifications(true),
    onOpenCollabMap: (mapId) => {
      setActiveCollabMapId(mapId);
      setAddToMapSpotId(null);
      setShowCollabMapModal(true);
    },
    onGoToSpot: handleGoToSpot,
    onOpenArticleReview: setReviewArticleId,
  });

  // --- Hurtiggodkjenning fra web-push / e-post: ?review_article=<id> ---
  // Samme mønster som ?verify= under: vent til Firestore er klar, rydd så URL-en
  // slik at et refresh ikke åpner modalen på nytt.
  useEffect(() => {
    if (!currentUser?.uid || !currentUser._firestoreReady) return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get('review_article');
    if (!id) return;
    params.delete('review_article');
    window.history.replaceState({}, '', params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname);
    setReviewArticleId(id);
  }, [currentUser?.uid, currentUser?._firestoreReady]);

  // Ventende innsjekk fra geofence-varsel når appen var lukket/i bakgrunn (tap →
  // deeplink ?checkin=<spotId>). Konsumeres når spots er lastet, se effekt lenger
  // ned (triggerCheckInPrompt er definert der).
  const pendingCheckinRef = useRef<string | null>(null);
  const [checkinNonce, setCheckinNonce] = useState(0);

  // --- Ett-tapps-verifisering fra e-post/web: ?verify=<spotId> ---
  // Åpner plassen klar for verifisering når brukeren følger en verifiser-lenke
  // fra e-post (desktop-nettleser eller PWA på mobil) eller push på web. Krever
  // innlogget bruker; venter til Firestore er klar, rydder så URL-en.
  useEffect(() => {
    if (!currentUser?.uid || !currentUser._firestoreReady) return;
    const params = new URLSearchParams(window.location.search);
    const verifySpotId = params.get('verify');
    if (!verifySpotId) return;
    params.delete('verify');
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
    handleGoToSpot(verifySpotId, { verify: true });
  }, [currentUser?.uid, currentUser?._firestoreReady, handleGoToSpot]);

  // Når verifiser/anmeld-kortet åpnes, skyv pinnen opp over kortet (bunnkortet
  // dekker nedre ~60 %) så brukeren ser HVILKEN plass det gjelder. Kortet har
  // ingen mørk backdrop — kartet er synlig og interaktivt bak det.
  useEffect(() => {
    const spot = verifyPromptSpot || reviewPromptSpot;
    if (!spot?.location) return;
    const targetZoom = isPaidUser ? 17 : Math.min(17, FREE_ZOOM_MAX);
    setMapCommand({
      center: spot.location,
      zoom: targetZoom,
      offsetY: Math.round(window.innerHeight * 0.22),
      id: performance.now(),
    });
  }, [verifyPromptSpot, reviewPromptSpot, isPaidUser]);

  useAppUpdate();
  useHomeRegionDetection(currentUser);
  useRegionViewTracking(currentViewBounds, !!currentUser);

  // --- Forsinket visning av bunnmeny på native (unngå visuell støy ved oppstart) ---
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const timer = setTimeout(() => setBottomNavVisible(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  // --- OTA: meld at denne bundlen fungerer (ellers ruller Capgo tilbake etter 10s),
  //     og gi brukeren synlig tilbakemelding når en ny bundle lastes ned/tas i bruk. ---
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let downloadSub: { remove: () => void } | undefined;
    let completeSub: { remove: () => void } | undefined;
    let announcedDownloading = false;
    import('@capgo/capacitor-updater')
      .then(async ({ CapacitorUpdater }) => {
        CapacitorUpdater.notifyAppReady().catch((e) => console.warn('[OTA] notifyAppReady failed:', e));
        // Versjonsvisning ved app-åpning (eier-ønske): vis hvilken JS-bundle som
        // FAKTISK kjører nå. `current()` gir OTA-bundlens versjon, ellers 'builtin'
        // → da viser vi den native versjonen. Kort info-toast, én gang per økt.
        try {
          const cur: any = await CapacitorUpdater.current();
          const bundleVer = cur?.bundle?.version;
          const ver = (bundleVer && bundleVer !== 'builtin') ? bundleVer : (cur?.native || bundleVer || '');
          if (ver) addToast(t('ota.version', 'Fricamp v{{ver}}', { ver }), 'info');
        } catch (e) { console.warn('[OTA] current() failed:', e); }
        // `download` fyrer gjentatte ganger (per prosent) — vis toasten kun én gang.
        downloadSub = await CapacitorUpdater.addListener('download', () => {
          if (announcedDownloading) return;
          announcedDownloading = true;
          addToast(t('ota.updating', 'Oppdaterer …'), 'info');
        });
        // directUpdate:false → ny bundle tas i bruk ved neste kald-start.
        completeSub = await CapacitorUpdater.addListener('downloadComplete', () => {
          addToast(t('ota.updated', 'Oppdatering klar – tas i bruk ved omstart'), 'success');
        });
      })
      .catch((e) => console.warn('[OTA] updater init failed:', e));
    return () => { downloadSub?.remove(); completeSub?.remove(); };
  }, []);

  // --- Auto-spør om varseltillatelse for testere som ikke har aktivert det ennå ---
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !currentUser?.uid) return;

    // Prompt if:
    // 1. User is a tester AND hasn't enabled notifications in-app yet
    // OR
    // 2. User HAS enabled notifications in-app, but OS permission is missing
    const shouldCheck = currentUser.isTester || currentUser.notificationsEnabled;

    if (shouldCheck) {
      const timer = setTimeout(async () => {
        const hasOsPermission = await checkNotificationPermission();

        // If they want notifications but OS says no, OR they are a tester who hasn't opted in yet
        const needsPrompt = (!hasOsPermission && currentUser.notificationsEnabled) ||
          (currentUser.isTester && !currentUser.notificationsEnabled);

        if (needsPrompt) {
          requestNotificationPermission(currentUser.uid).catch(() => { });
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentUser?.uid, currentUser?.isTester, currentUser?.notificationsEnabled]);

  // --- Håndter OAuth-redirect fra Chrome Custom Tab (PKCE-flyt) ---
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listenerPromise = CapacitorApp.addListener('appUrlOpen', async (event) => {
      const url = event.url ?? '';

      // App Links / Universal Links: https://app.fricamp.com/?verify=<spotId>
      // Åpner appen klar for verifisering fra e-post/web på mobil. Aktiveres når
      // native-releasen med assetlinks/AASA + intent-filter/entitlement er ute;
      // parsingen her er ufarlig og klar på forhånd.
      if (url.startsWith('https://app.fricamp.com') || url.startsWith('https://www.fricamp.com')) {
        try {
          const sp = new URL(url).searchParams;
          const verifySpotId = sp.get('verify');
          if (verifySpotId) {
            await handleGoToSpot(verifySpotId, { verify: true });
            return;
          }
          // Geofence-innsjekk-varsel (app allerede i live): ?checkin=<spotId>
          const checkinSpotId = sp.get('checkin');
          if (checkinSpotId) {
            pendingCheckinRef.current = checkinSpotId;
            setCheckinNonce(n => n + 1);
            return;
          }
        } catch { /* ignore malformed URL */ }
        return;
      }

      if (!url.startsWith('com.takleconsulting.fricamp:')) return;

      // Geofence-varsel-tap (Android, app-død/-bakgrunn): custom-scheme-deeplink
      // com.takleconsulting.fricamp://checkin?spotId=<id>
      if (url.includes('://checkin')) {
        try {
          const checkinSpotId = new URL(url).searchParams.get('spotId');
          if (checkinSpotId) {
            pendingCheckinRef.current = checkinSpotId;
            setCheckinNonce(n => n + 1);
          }
        } catch { /* ignore malformed URL */ }
        return;
      }

      // Home screen-widget-tap: com.takleconsulting.fricamp://spot?spotId=<id>.
      // Ren parser gjenkjenner KUN denne lenken (ikke ://checkin/OAuth), så den kan
      // ikke kapre grenene over. (Kald-start via getLaunchUrl kommer med native
      // widget-target i S2/S3 — ingen widget kan fyre denne lenken før da.)
      const widgetLink = parseWidgetDeepLink(url);
      if (widgetLink?.kind === 'spot') {
        await handleGoToSpot(widgetLink.spotId);
        return;
      }

      try {
        if (url.includes('apple-oauth2redirect')) {
          await processAppleRedirectResult(url);
        } else {
          await processGoogleRedirectResult(url);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('OAuth feil:', msg);
        alert(t('auth.errors.oauth_failed', 'Innlogging feilet') + ':\n' + msg);
      }
    });
    return () => { listenerPromise.then(l => l.remove()); };
  }, []);

  // --- Reset stale UI state on Android app resume ---
  // NOTE: Do NOT close showAddModal here — native file pickers (ExifImagePicker)
  // trigger a resume event when they return, which would close the registration modal.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listenerPromise = CapacitorApp.addListener('resume', () => {
      // Only reset pending analysis if modal is not open (avoid interrupting active registration)
      if (!showAddModal) {
        setPendingAnalysisCoords(null);
      }
    });
    return () => { listenerPromise.then(l => l.remove()); };
  }, [showAddModal]);

  // --- Offline upload queue state & processor ---
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const [uploadQueueProcessing, setUploadQueueProcessing] = useState(false);
  const [uploadQueueProgress, setUploadQueueProgress] = useState<{ current: number; total: number } | null>(null);

  // Refresh pending count periodically and after queue changes
  const refreshPendingCount = useCallback(() => {
    import('./services/offlineUploadQueue').then(({ getPendingCount }) => {
      setPendingUploadCount(getPendingCount());
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshPendingCount();
    // Also refresh when coming online
    const onOnline = () => refreshPendingCount();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [refreshPendingCount]);

  useEffect(() => {
    import('./services/offlineUploadQueue').then(({ startQueueProcessor }) => {
      startQueueProcessor(
        (result) => {
          if (result.processed > 0) {
            addToast(t('toast.images_synced', `${result.processed} bilde(r) lastet opp`), 'success');
          }
          setUploadQueueProgress(null);
          refreshPendingCount();
        },
        (progress) => {
          setUploadQueueProgress({ current: progress.current, total: progress.total });
        },
        (processing) => {
          setUploadQueueProcessing(processing);
          if (!processing) refreshPendingCount();
        }
      );
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleManualQueueRetry = useCallback(() => {
    if (uploadQueueProcessing) return;
    import('./services/offlineUploadQueue').then(({ manualProcessQueue }) => {
      setUploadQueueProcessing(true);
      manualProcessQueue(
        (progress) => {
          setUploadQueueProgress({ current: progress.current, total: progress.total });
        },
        (result) => {
          if (result.processed > 0) {
            addToast(t('toast.images_synced', `${result.processed} bilde(r) lastet opp`), 'success');
          }
          if (result.failed > 0) {
            addToast(t('toast.images_failed', `${result.failed} bilde(r) kunne ikke lastes opp`), 'warning');
          }
          setUploadQueueProgress(null);
          setUploadQueueProcessing(false);
          refreshPendingCount();
        }
      );
    }).catch(() => {
      setUploadQueueProcessing(false);
    });
  }, [uploadQueueProcessing, addToast, t, refreshPendingCount]);

  // --- My Content (unified hub) ---
  const myContent = useMyContent(currentUser?.uid ?? null, visits);

  // Reisefilm-promo: vis promo-kort/coach-mark kun når brukeren har tilgang OG
  // faktisk har en reise å vise (≥2 innsjekk m/posisjon). Samme viabilitetssjekk
  // som «Reisefilm»-knappen i PeriodReplayControls.
  const replayPromoEligible = useMemo(
    () => hasReplayAccess(currentUser) && isViableReplayTrip(buildReplayTripSpec(visits).spec),
    [currentUser, visits],
  );
  const drawing = useDrawingMode();

  // Map spotId → first photo URL for visit thumbnails
  const spotPhotos = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of spots) {
      const url = s.photoUrls?.[0] || s.photoUrl;
      if (url) m.set(s.id, url);
    }
    return m;
  }, [spots]);

  const handleDrawingMapClick = (coords: [number, number]) => {
    if (drawing.state === 'drawing') {
      drawing.addPoint(coords[0], coords[1]);
    }
  };

  const handleSaveDrawing = async (name: string, color: string, note: string, strokeWidth: number, strokeStyle: 'solid' | 'dashed' | 'dotted') => {
    if (!currentUser?.uid) return;
    if (drawing.toolType === 'line') {
      await addLineContent(currentUser.uid, name, drawing.points, color, strokeWidth, strokeStyle, note);
    } else if (drawing.toolType === 'area') {
      await addAreaContent(currentUser.uid, name, drawing.points, color, 0.15, strokeWidth, note);
    }
    drawing.resetAfterSave();
  };

  const handleDeleteContentItem = async (id: string) => {
    const item = myContent.content.find(c => c.id === id);
    if (!item) return;
    if (!window.confirm(t('myContent.delete_item_confirm', 'Slette «{{name}}»?', { name: item.name }))) return;

    // Cascade-spørsmålet stilles bare til admin — markSpotAsInvalid er et
    // no-op for alle andre, så et «ja» ville ikke fjernet den delte plassen.
    const linkedSpotId = item.type === 'waypoint' ? (item as any).linkedSpotId : undefined;
    const cascade = !!linkedSpotId && !!currentUser?.isAdmin && window.confirm(
      t('myContent.cascade_delete_confirm', 'Dette punktet er også delt med fellesskapet. Slette den delte plassen fra kartet for alle andre også?')
    );

    try {
      await deleteContent(id, { alsoDeleteLinkedSpot: cascade });
      if (linkedSpotId && !currentUser?.isAdmin && currentUser?.uid) {
        try {
          await suggestDeletion(linkedSpotId, currentUser.uid, 'Eier slettet sitt eget punkt i Mitt innhold', currentUser.displayName ?? null);
          addToast(t('myContent.shared_spot_reported', 'Punktet er slettet. Den delte plassen er meldt til gjennomgang.'), 'success');
          return;
        } catch (err) {
          console.warn('[handleDeleteContentItem] could not report shared spot:', err);
        }
      }
      addToast(t('myContent.item_deleted', 'Slettet'), 'success');
    } catch (err) {
      console.error('[handleDeleteContentItem] delete failed:', err);
      addToast(t('common.error_generic', 'Noe gikk galt'), 'error');
    }
  };

  const handleOpenMyContent = (tab: TabKey = 'folders') => {
    setMyContentInitialTab(tab);
    if (isDesktop) {
      setSidebarMode('mycontent');
      setSidebarExpanded(true);
    } else {
      setSidebarMode('mycontent');
      setMobileView('list');
    }
  };

  // --- Følg rute (Route Follow) ---

  // Hjelpefunksjon: beregn kumulativ distanse langs rute for et punkt
  const findDistanceAlong = useCallback((point: Coordinates, routeCoords: Coordinates[]): number => {
    let minDist = Infinity;
    let closestIdx = 0;
    for (let i = 0; i < routeCoords.length; i++) {
      const d = getDistance(point.latitude, point.longitude, routeCoords[i].latitude, routeCoords[i].longitude);
      if (d < minDist) { minDist = d; closestIdx = i; }
    }
    let cumDist = 0;
    for (let i = 1; i <= closestIdx; i++) {
      cumDist += getDistance(routeCoords[i - 1].latitude, routeCoords[i - 1].longitude, routeCoords[i].latitude, routeCoords[i].longitude);
    }
    return cumDist;
  }, []);

  const getBoundsForCoords = useCallback((coords: Coordinates[], paddingLat = 0.06, paddingLon = 0.08): MapBounds | null => {
    if (!coords || coords.length === 0) return null;
    const lats = coords.map((coord) => coord.latitude);
    const lons = coords.map((coord) => coord.longitude);
    return {
      latMin: Math.min(...lats) - paddingLat,
      latMax: Math.max(...lats) + paddingLat,
      lonMin: Math.min(...lons) - paddingLon,
      lonMax: Math.max(...lons) + paddingLon,
    };
  }, []);

  const [followRouteState, setFollowRouteState] = useState<RouteFollowState | null>(null);
  const [navigationBuilding, setNavigationBuilding] = useState(false); // true while async route build is in progress
  const [followPreflight, setFollowPreflight] = useState<FollowPreflightState | null>(null);
  const [followPreflightDataReady, setFollowPreflightDataReady] = useState(false);
  const [followPreflightDataLoading, setFollowPreflightDataLoading] = useState(false);
  const [followPreflightDataStatus, setFollowPreflightDataStatus] = useState<string | null>(null);
  const [showFollowOfflineManager, setShowFollowOfflineManager] = useState(false);

  // GPS-posisjon: hent umiddelbart ved follow-start som fallback til trackPoints
  const [initialGpsPosition, setInitialGpsPosition] = useState<Coordinates | null>(null);
  useEffect(() => {
    if (!followRouteState) { setInitialGpsPosition(null); return; }
    if (trackPoints.length > 0) return;
    gatedGetCurrentPosition(
      (pos) => setInitialGpsPosition({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => {
        if (followRouteState.routeCoords.length > 0) {
          setInitialGpsPosition(followRouteState.routeCoords[0]);
        }
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, [followRouteState, trackPoints.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Last known GPS position — persists across sessions via localStorage
  const LAST_KNOWN_POS_KEY = 'fricamp_last_known_gps';
  const [lastKnownGpsPosition, setLastKnownGpsPosition] = useState<Coordinates | null>(() => {
    try {
      const stored = localStorage.getItem(LAST_KNOWN_POS_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const [gpsLastUpdated, setGpsLastUpdated] = useState<number>(0);
  // Live speed from passive watchPosition (m/s). Used for auto-nav when not actively tracking.
  const [liveGpsSpeed, setLiveGpsSpeed] = useState<number | null>(null);

  const userPosition = trackPoints.length > 0
    ? { latitude: trackPoints[trackPoints.length - 1].latitude, longitude: trackPoints[trackPoints.length - 1].longitude }
    : initialGpsPosition || lastKnownGpsPosition;

  // Update lastKnownGpsPosition & timestamp whenever we get a real GPS position
  useEffect(() => {
    const pos = trackPoints.length > 0
      ? { latitude: trackPoints[trackPoints.length - 1].latitude, longitude: trackPoints[trackPoints.length - 1].longitude }
      : initialGpsPosition;
    if (pos) {
      setLastKnownGpsPosition(pos);
      setGpsLastUpdated(Date.now());
      try { localStorage.setItem(LAST_KNOWN_POS_KEY, JSON.stringify(pos)); } catch {}
    }
  }, [trackPoints.length, initialGpsPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-shot GPS fetch on app start — center map on user's actual location
  // Uses gatedGetCurrentPosition to ensure prominent disclosure is shown first
  const didCenterOnGpsRef = useRef(false);
  useEffect(() => {
    if (resolvedSharedWaypoint || parsedSharedMap) return;
    gatedGetCurrentPosition(
      (pos) => {
        const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setLastKnownGpsPosition(coords);
        setGpsLastUpdated(Date.now());
        try { localStorage.setItem(LAST_KNOWN_POS_KEY, JSON.stringify(coords)); } catch {}
        if (!didCenterOnGpsRef.current) {
          didCenterOnGpsRef.current = true;
          setMapCenter(coords);
        }
      },
      () => { /* silently ignore — keep last saved position */ },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refs holder ferske spots/bruker uten å re-abonnere den passive watchen (som
  // ellers ville reetablert seg hver gang spots-lista endres).
  const proximitySpotsRef = useRef(spots);
  const proximityUserRef = useRef(currentUser);
  useEffect(() => { proximitySpotsRef.current = spots; }, [spots]);
  useEffect(() => { proximityUserRef.current = currentUser; }, [currentUser]);

  // Passive GPS watch when not actively tracking — keeps position + speed fresh so
  // auto-navigation can detect movement even when the user hasn't started a recording.
  // watchPosition is more responsive than polling and survives timer throttling in
  // background-tab–suspended browsers (e.g. Tesla in-car browser).
  useEffect(() => {
    if (trackingStatus !== 'idle') return;
    const watchId = gatedWatchPosition(
      (pos) => {
        const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setLastKnownGpsPosition(coords);
        setGpsLastUpdated(Date.now());
        setLiveGpsSpeed(typeof pos.coords.speed === 'number' && pos.coords.speed >= 0 ? pos.coords.speed : null);
        try { localStorage.setItem(LAST_KNOWN_POS_KEY, JSON.stringify(coords)); } catch {}
        // Dwell-basert innsjekk-push virker nå også uten aktiv turlogg (så lenge
        // appen er i forgrunnen). Ekte bakgrunns-geofence er neste steg.
        runProximityCheckIn(coords, proximitySpotsRef.current, {
          currentUserUid: proximityUserRef.current?.uid,
          isDismissed: isDismissedRecently,
          notify: scheduleProximityCheckInNotification,
        });
      },
      () => {},
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 }
    );
    return () => {
      if (typeof watchId === 'number') {
        try { navigator.geolocation.clearWatch(watchId); } catch {}
      }
      setLiveGpsSpeed(null);
    };
  }, [trackingStatus, gatedWatchPosition]);

  const activeHeading = trackPoints.length > 0 ? (trackPoints[trackPoints.length - 1].heading ?? null) : null;
  const activeSpeed = trackPoints.length > 0 ? (trackPoints[trackPoints.length - 1].speed ?? 0) : 0;

  // Android Auto data bridge — sends spots, position, route and map style to car display.
  // The hook reports two flags:
  //   - isAutoPluggedIn: a head unit is physically connected (use for the upsell pill)
  //   - hasAutoAccess: the current user has Premium / admin / tester
  // We then derive `isAutoConnected` = both true. Everywhere else in this file
  // `isAutoConnected` means "the car is actually rendering content, defer phone UI".
  // Without this gate, a non-premium plug-in would freeze the phone in browse-only
  // mode while the car screen sits empty — locking the user out of both surfaces.
  const { isAutoConnected: isAutoPluggedIn, hasAccess: hasAutoAccess } = useAndroidAuto({
    user: currentUser,
    spots,
    viewBounds: currentViewBounds,
    userPosition,
    userHeading: activeHeading ?? 0,
    userSpeed: activeSpeed,
    mapStyle: layers.satellite ? 'satellite' : 'topo',
    language: i18n.language || 'no',
    followRouteState,
    onSpotSelected: (spotId: string) => {
      const spot = spots.find(s => s.id === spotId);
      if (spot) handleSelectSpot(spot);
    },
    onNavigateRequest: (spotId: string) => {
      const spot = spots.find(s => s.id === spotId);
      if (spot) {
        handleSelectSpot(spot);
        addToast('Navigasjon startet fra Android Auto', 'info');
      }
    },
    onCheckIn: (spotId: string) => {
      const spot = spots.find(s => s.id === spotId);
      if (spot) {
        handleToggleVisit({ stopPropagation: () => {} } as any, spotId);
        addToast('Sjekket inn fra Android Auto!', 'success');
      }
    },
    onMapStyleChange: (style) => {
      setLayers(prev => ({ ...prev, satellite: style === 'satellite' }));
      addToast(`Kart byttet til ${style}`, 'info');
    },
    onSyncRequest: () => {
      if (userPosition) {
        sendSpotsToAuto(spots, userPosition);
        sendPositionToAuto(userPosition, activeHeading ?? 0, activeSpeed);
        addToast('Data synkronisert med Android Auto', 'success');
      }
    },
  });
  // "Car is rendering content" — premium + plugged in. Phone UI defers to this.
  const isAutoConnected = isAutoPluggedIn && hasAutoAccess;

  // Push heading into ring buffer for trailer articulation
  if (activeHeading !== null) {
    headingBufferRef.current.push(activeHeading, Date.now());
  }
  const trailerHeading = headingBufferRef.current.getDelayedHeading(2000);

  const routeFollowResult = useRouteFollow(
    followRouteState,
    userPosition,
    activeHeading
  );
  const followMode: FollowMode | null = followRouteState && routeFollowResult.isFollowing
    ? 'route_follow'
    : trackingStatus === 'recording'
      ? 'free_drive_follow'
      : null;
  const followActive = followMode !== null;

  // Home screen-widget (Android «Nærmeste fricamp»): skriv snapshotet når posisjon,
  // plasser, premium eller språk endres. Uavhengig av Android Auto — widgeten er en
  // gratis trakt og skal populeres for alle. Native-only (no-op på web); intern
  // throttle i pushWidgetSnapshot demper disk-skriv + widget-reload til reelle endringer.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    pushWidgetSnapshot(buildWidgetSnapshot({
      position: userPosition,
      spots,
      hasPremium: isPremiumUser,
      lang: i18n.language || 'no',
      now: Date.now(),
    }));
  }, [userPosition?.latitude, userPosition?.longitude, spots, isPremiumUser, i18n.language]);

  // Native MapLibre overlay for smooth 60fps navigation rendering on Android.
  // CRITICAL: this is a native plugin that draws its OWN MapLibre map on top
  // of the WebView — it bypasses every React-state gate (is3DMode, Map3DView
  // suspense, etc.). When Android Auto owns navigation we must disable it
  // explicitly, otherwise the phone shows the immersive nav surface even when
  // we've turned off every JS-side 3D control.
  // `spots` is forwarded so the hook can compute and render the
  // "next Fricamp along route" marker on the native map (same algorithm as
  // the AA-side compute).
  useNativeNavMap({
    followRouteState,
    userPosition,
    userHeading: activeHeading,
    mapStyle: layers.satellite ? 'satellite' : 'topo',
    isFollowing: followActive && !isAutoConnected,
    spots,
    progressM: routeFollowResult.progressM,
  });
  const { isNative: isNativeApp, isTablet: isTabletDevice, isLandscape: isLandscapeOrientation, mustRotateForFollow } = useTabletOrientationGuard(followActive);
  // Tablet follow-panel is suppressed when Android Auto owns navigation
  const showTabletFollowPanel = followActive && isNativeApp && isTabletDevice && isLandscapeOrientation && !isAutoConnected;
  const followTarget = useFollowTarget({
    mode: followMode,
    userPosition,
    routeCoords: followRouteState?.routeCoords ?? null,
    routeProgressM: routeFollowResult.progressM,
    spots,
    routePois: followRouteState?.pois ?? [],
  });
  const followTargetSpot = followTarget?.spotId
    ? spots.find((spot) => spot.id === followTarget.spotId) ?? null
    : null;

  // Nearest spot along entire route (for "find nearest" button when no spot is within 3km)
  const nearestRouteSpot = useMemo(() => {
    if (!followActive || !userPosition || !followRouteState?.routeCoords) return null;
    // Don't show if FollowSpotOverlay already has a spot
    if (followTarget) return null;
    const routeCoords = followRouteState.routeCoords;
    let best: { spot: CampingSpot; distance: number } | null = null;
    for (const spot of spots) {
      if (!spot.location || spot.isDeleted || spot.isInvalid) continue;
      if (!isPointNearPolyline(spot.location, routeCoords, 5000)) continue;
      const d = getDistance(userPosition.latitude, userPosition.longitude, spot.location.latitude, spot.location.longitude);
      if (!best || d < best.distance) best = { spot, distance: d };
    }
    return best;
  }, [followActive, userPosition, followRouteState?.routeCoords, followTarget, spots]);

  // Lyd- og haptikkvarsler under navigasjon
  useNavigationAlerts({
    isOffRoute: routeFollowResult.isOffRoute,
    nearbyPoi: routeFollowResult.nearbyPoi,
    isFollowing: followActive,
  });

  // Manual pin-tap override: user taps a map pin → show that spot in overlay for 60s
  const [followOverrideSpotId, setFollowOverrideSpotId] = useState<string | null>(null);
  const followOverrideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const followSheetAutoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const followOverrideSpot = followOverrideSpotId ? spots.find(s => s.id === followOverrideSpotId) ?? null : null;
  // The spot shown in the overlay: override > automatic target
  const followDisplaySpot = followOverrideSpot ?? followTargetSpot;
  const isFollowOverride = followOverrideSpot !== null;

  // Clear override when follow mode ends
  useEffect(() => {
    if (!followActive) {
      setFollowOverrideSpotId(null);
      setNavigationBuilding(false);
      if (followOverrideTimerRef.current) clearTimeout(followOverrideTimerRef.current);
      if (followSheetAutoCloseRef.current) clearTimeout(followSheetAutoCloseRef.current);
    } else {
      setNavigationBuilding(false);
    }
  }, [followActive]);

  const prevFollowRouteIdRef = useRef<string | null>(null);

  const handleDownloadFollowData = useCallback(async () => {
    if (!followPreflight?.bounds || followPreflightDataLoading) return;
    setFollowPreflightDataLoading(true);
    setFollowPreflightDataStatus('Henter steder og ruter...');
    try {
      await downloadDataForArea(
        followPreflight.bounds,
        {
          includeSpots: true,
          includeRoutes: followPreflight.mode === 'route_follow',
        },
        (status) => setFollowPreflightDataStatus(status)
      );
      setFollowPreflightDataReady(true);
      setFollowPreflightDataStatus('Lokale data er klare.');
    } catch (error) {
      console.warn('Failed to predownload follow data:', error);
      setFollowPreflightDataStatus('Kunne ikke lagre data lokalt.');
    } finally {
      setFollowPreflightDataLoading(false);
    }
  }, [followPreflight, followPreflightDataLoading]);

  const openFollowPreflight = useCallback((payload: FollowPreflightState) => {
    // Route building is done — dismiss the spinner
    setNavigationBuilding(false);
    // Immediately close sidebar/sheet so it doesn't linger behind the preflight modal
    setSidebarExpanded(false);
    setMobileView('map');
    setShowMobileSheet(false);
    setFollowPreflight(payload);
    setFollowPreflightDataReady(false);
    setFollowPreflightDataLoading(false);
    setFollowPreflightDataStatus(null);
  }, [isDesktop, setShowMobileSheet]);

  useEffect(() => {
    const currentId = followRouteState?.routeId ?? null;
    if (currentId !== prevFollowRouteIdRef.current) {
      prevFollowRouteIdRef.current = currentId;
      setFollowMapOrientation('north_up');
      setFollowViewMode('2d');
    }
  }, [followRouteState?.routeId]);

  useEffect(() => {
    if (followViewMode === '3d' && !is3DMode) setIs3DMode(true);
    if (followViewMode === '2d' && is3DMode) setIs3DMode(false);
  }, [followViewMode, is3DMode]);

  // Auto-minimize TrackingPanel on touch devices when tracking starts or follow begins
  useEffect(() => {
    if (isTouchDevice && trackingStatus !== 'idle') {
      setTrackingPanelSnap('mini');
    }
  }, [trackingStatus, followActive, is3DMode]);

  // When Android Auto is connected, the phone must never be in immersive nav
  // mode (3D, follow camera). Reactive on is3DMode/followViewMode too so even
  // if the user toggles 3D after AA is up, we snap back to browse mode.
  // No infinite loop: setters become no-ops once values match.
  useEffect(() => {
    if (!isAutoConnected) return;
    if (is3DMode) setIs3DMode(false);
    if (followViewMode === '3d') setFollowViewMode('2d');
    if (followActive) setTrackingPanelSnap('mini');
  }, [isAutoConnected, followActive, is3DMode, followViewMode]);

  // DEBUG: trace nav-mode state to logcat so we can confirm what's actually
  // rendering when the user reports "phone is in 3D under AA".
  useEffect(() => {
    console.log(`[FricampDebug] navState: is3DMode=${is3DMode} isAutoConnected=${isAutoConnected} followViewMode=${followViewMode} followActive=${followActive}`);
  }, [is3DMode, isAutoConnected, followViewMode, followActive]);

  // When AA takes over and a route is already in progress, fit the Leaflet
  // map to the route bbox so the user can see the whole journey on the phone
  // instead of the world-zoomed default leftover from native-map dismount.
  useEffect(() => {
    if (!isAutoConnected) return;
    if (!followRouteState?.routeCoords || followRouteState.routeCoords.length < 2) return;
    const coords = followRouteState.routeCoords;
    let latMin = Infinity, latMax = -Infinity, lonMin = Infinity, lonMax = -Infinity;
    for (const c of coords) {
      if (c.latitude < latMin) latMin = c.latitude;
      if (c.latitude > latMax) latMax = c.latitude;
      if (c.longitude < lonMin) lonMin = c.longitude;
      if (c.longitude > lonMax) lonMax = c.longitude;
    }
    const center = { latitude: (latMin + latMax) / 2, longitude: (lonMin + lonMax) / 2 };
    setMapCommand({
      center,
      zoom: 9,
      bounds: [[latMin, lonMin], [latMax, lonMax]],
      id: Date.now(),
    });
  }, [isAutoConnected, followRouteState?.routeId]);

  useEffect(() => {
    if (!followActive) return;
    // When Android Auto owns the navigation, the phone stays in browse mode —
    // don't force-close sidebar/sheet/list views. The car is the nav surface.
    if (isAutoConnected) return;

    setSidebarExpanded(false);    // Skjul sidebar under navigasjon (uansett enhet)
    setMobileView('map');         // Lukk listevisning på mobil under navigasjon
    setShowMobileSheet(false);    // Lukk bunnarket
    setSidebarMode('spots');

    if (!followTargetSpot) {
      setSelectedSpotId(null);
      return;
    }

    if (selectedSpotId !== followTargetSpot.id) {
      if (isDesktop) {
        setSelectedSpotId(followTargetSpot.id);  // Desktop: bare track, overlay viser info
      } else {
        handleSelectSpotDetails(followTargetSpot, { openMobileSheet: false }).catch(() => {});
      }
    }
  }, [
    followActive,
    isDesktop,
    followTargetSpot,
    selectedSpotId,
    handleSelectSpotDetails,
    setSidebarExpanded,
    setSidebarMode,
    setSelectedSpotId,
    setShowMobileSheet,
  ]);

  // Push data to Android Auto / CarPlay for Premium users
  useCarScreen({
    user: currentUser,
    spots,
    userPosition,
    userHeading: activeHeading,
    activeRoute: null,
  });

  // Shared helper: parse GPX, build approach route, collect POIs, start navigation
  const buildRouteNavigation = useCallback(async (
    route: GpxRoute,
    fromCoords: Coordinates,
    mode: 'from_start' | 'from_nearest'
  ) => {
    addToast('Beregner rute...', 'info');

    // 1. Parse GPX
    const gpxText = await fetchGpxWithProxy(route.gpxUrl);
    const parser = new DOMParser();
    const xml = parser.parseFromString(gpxText, 'application/xml');
    const trkpts = Array.from(xml.querySelectorAll('trkpt'));
    const gpxCoords: Coordinates[] = trkpts
      .map(pt => ({
        latitude: parseFloat(pt.getAttribute('lat') ?? ''),
        longitude: parseFloat(pt.getAttribute('lon') ?? ''),
      }))
      .filter(c => Number.isFinite(c.latitude) && Number.isFinite(c.longitude));

    if (gpxCoords.length < 2) { setNavigationBuilding(false); return; }

    // 2. Find target point on GPX route
    let targetIdx = 0;
    if (mode === 'from_nearest') {
      let nearestDist = Infinity;
      for (let i = 0; i < gpxCoords.length; i++) {
        const d = getDistance(fromCoords.latitude, fromCoords.longitude,
                             gpxCoords[i].latitude, gpxCoords[i].longitude);
        if (d < nearestDist) { nearestDist = d; targetIdx = i; }
      }
    }
    const targetPoint = gpxCoords[targetIdx];

    // 3. Fetch OSRM approach route from user to target point
    const approach = await fetchDrivingRoute(fromCoords, targetPoint);
    const approachCoords: Coordinates[] = approach.coordinates.map(([lon, lat]: [number, number]) => ({
      latitude: lat, longitude: lon,
    }));

    // 4. Combine: approach + remaining GPX route
    const remainingGpx = gpxCoords.slice(targetIdx);
    const routeCoords = [...approachCoords, ...remainingGpx];

    // 5. Calculate total distance
    let totalM = 0;
    for (let i = 1; i < routeCoords.length; i++) {
      totalM += getDistance(routeCoords[i - 1].latitude, routeCoords[i - 1].longitude,
                           routeCoords[i].latitude, routeCoords[i].longitude);
    }

    // 6. Collect POIs along the combined route
    const pois: RouteFollowPoi[] = [];
    if (route.routeWaypoints) {
      for (const wp of route.routeWaypoints) {
        const wpCoord = { latitude: wp.latitude, longitude: wp.longitude };
        if (!isPointNearPolyline(wpCoord, routeCoords, 1500)) continue;
        pois.push({
          id: `wp-${wp.name}-${wp.latitude}`,
          name: wp.name, category: 'waypoint',
          location: wpCoord, icon: wp.icon,
          distanceAlongRoute: findDistanceAlong(wpCoord, routeCoords),
        });
      }
    }
    if (route.wikiSpots) {
      for (const ws of route.wikiSpots) {
        if (!isPointNearPolyline(ws.location, routeCoords, 1500)) continue;
        pois.push({
          id: `wiki-${ws.pageid}`,
          name: ws.title, category: 'attraction',
          location: ws.location,
          distanceAlongRoute: findDistanceAlong(ws.location, routeCoords),
        });
      }
    }
    const routeSpots = spots.filter(s => s.location && isPointNearPolyline(s.location, routeCoords, 1500));
    for (const s of routeSpots) {
      const cat = s.spotType === 'rasteplass' ? 'rest'
        : s.spotType === 'commercial_campsite' ? 'campsite' : 'wildspot';
      pois.push({
        id: `spot-${s.id}`, name: s.name || cat,
        category: cat as RouteFollowPoi['category'],
        location: s.location,
        distanceAlongRoute: findDistanceAlong(s.location, routeCoords),
      });
    }
    pois.sort((a, b) => a.distanceAlongRoute - b.distanceAlongRoute);

    // 7. Preflight → start navigation
    const routeBounds = getBoundsForCoords(routeCoords);
    openFollowPreflight({
      mode: 'route_follow',
      title: route.name,
      bounds: routeBounds,
      route,
      continueAction: () => {
        setFollowRouteState({
          routeId: `${mode === 'from_start' ? 'follow' : 'nav-to'}-${route.id}-${Date.now()}`,
          routeName: route.name,
          routeCoords,
          routeTotalDistanceM: totalM,
          pois,
        });
        setActiveRouteIds(new Set([route.id]));
        setSelectedRouteId(null);
        setMobileView('map');
        startTracking();
        logFeatureUsage(mode === 'from_start' ? 'follow_route' : 'navigate_to_route');
      },
    });
  }, [spots, startTracking, addToast, getBoundsForCoords, openFollowPreflight, findDistanceAlong]);

  // Resolve user position with GPS fallback, then call action
  const resolvePositionAndRun = useCallback(async (
    action: (coords: Coordinates) => void,
    fallbackCoord?: Coordinates | null
  ) => {
    if (userPosition) { action(userPosition); return; }
    if ('geolocation' in navigator) {
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          gatedGetCurrentPosition(resolve, reject, {
            enableHighAccuracy: true, timeout: 8000, maximumAge: 30000,
          });
        });
        action({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
        return;
      } catch { /* fall through */ }
    }
    if (fallbackCoord) action(fallbackCoord);
    else addToast('Kunne ikke finne din posisjon', 'error');
  }, [userPosition, addToast]);

  const handleStartFollowRoute = useCallback(async (route: GpxRoute) => {
    if (!route.gpxUrl) return;
    // Lock sidebar closed immediately — before any async work
    setNavigationBuilding(true);
    setSidebarExpanded(false); setMobileView('map'); setShowMobileSheet(false);
    try {
      await resolvePositionAndRun(
        (fromCoords) => buildRouteNavigation(route, fromCoords, 'from_start'),
        route.startCoords || route.center
      );
    } catch (err) {
      console.warn('Kunne ikke starte rutenavigasjon:', err);
      addToast('Kunne ikke starte rutenavigasjon', 'error');
      setNavigationBuilding(false);
    }
  }, [resolvePositionAndRun, buildRouteNavigation, addToast, setSidebarExpanded, setShowMobileSheet]);

  // Navigate TO a route: OSRM approach from user → nearest GPX point, then continue along remaining GPX
  const handleNavigateToRoute = useCallback(async (route: GpxRoute) => {
    if (!route.gpxUrl) return;
    setNavigationBuilding(true);
    setSidebarExpanded(false); setMobileView('map'); setShowMobileSheet(false);
    try {
      await resolvePositionAndRun(
        (fromCoords) => buildRouteNavigation(route, fromCoords, 'from_nearest'),
        route.startCoords || route.center
      );
    } catch (err) {
      console.warn('Kunne ikke beregne rute:', err);
      addToast('Kunne ikke beregne kjørerute til ruten', 'error');
      setNavigationBuilding(false);
    }
  }, [resolvePositionAndRun, buildRouteNavigation, addToast, setSidebarExpanded, setShowMobileSheet]);

  // Navigate to a spot: fetch OSRM route, collect POIs, enter follow mode
  const buildAndStartNavigation = useCallback(async (fromCoords: Coordinates, spot: CampingSpot) => {
    setNavigationBuilding(true);
    setSidebarExpanded(false); setMobileView('map'); setShowMobileSheet(false);
    try {
      addToast('Beregner kjørerute...', 'info');

      const result = await fetchDrivingRoute(fromCoords, {
        latitude: spot.location.latitude,
        longitude: spot.location.longitude,
      });

      // Convert OSRM [lon,lat] to Coordinates
      const routeCoords: Coordinates[] = result.coordinates.map(([lon, lat]) => ({
        latitude: lat,
        longitude: lon,
      }));

      // Calculate total route distance
      let totalM = 0;
      for (let i = 1; i < routeCoords.length; i++) {
        totalM += getDistance(
          routeCoords[i - 1].latitude, routeCoords[i - 1].longitude,
          routeCoords[i].latitude, routeCoords[i].longitude
        );
      }

      // Collect POIs: campsites, rest areas near route
      const pois: RouteFollowPoi[] = [];
      const nearbySpots = spots.filter(s => {
        if (!s.location || s.id === spot.id) return false;
        return isPointNearPolyline(s.location, routeCoords, 1500);
      });
      for (const s of nearbySpots) {
        const cat = s.spotType === 'rasteplass' ? 'rest'
          : s.spotType === 'commercial_campsite' ? 'campsite'
          : 'wildspot';
        pois.push({
          id: `spot-${s.id}`,
          name: s.name || cat,
          category: cat as RouteFollowPoi['category'],
          location: s.location,
          distanceAlongRoute: findDistanceAlong(s.location, routeCoords),
        });
      }

      // Add destination spot as final POI
      pois.push({
        id: `spot-${spot.id}`,
        name: spot.name || 'Destinasjon',
        category: spot.spotType === 'rasteplass' ? 'rest'
          : spot.spotType === 'commercial_campsite' ? 'campsite'
          : 'wildspot',
        location: spot.location,
        distanceAlongRoute: totalM,
      });

      pois.sort((a, b) => a.distanceAlongRoute - b.distanceAlongRoute);

      const routeBounds = getBoundsForCoords(routeCoords);
      const routeId = `nav-${spot.id}-${Date.now()}`;

      openFollowPreflight({
        mode: 'route_follow',
        title: spot.name || 'Navigasjon',
        bounds: routeBounds,
        continueAction: () => {
          setFollowRouteState({
            routeId,
            routeName: spot.name || 'Navigasjon',
            routeCoords,
            routeTotalDistanceM: totalM,
            routeTotalDurationS: result.durationSeconds || 0,
            pois,
          });
          setMobileView('map');
          startTracking();
          logFeatureUsage('navigate_to_spot');
        },
      });
    } catch (err) {
      console.warn('Kunne ikke beregne kjørerute:', err);
      addToast('Kunne ikke beregne kjørerute', 'error');
      setNavigationBuilding(false);
    }
  }, [spots, startTracking, addToast, getBoundsForCoords, openFollowPreflight, findDistanceAlong]);

  // Entry point from mini-cards: try GPS first, fallback to route planning dialog
  const handleNavigateToSpot = useCallback(async (spot: CampingSpot) => {
    if (!spot?.location) return;

    // If we already have user position, start immediately
    if (userPosition) {
      buildAndStartNavigation(userPosition, spot);
      return;
    }

    // Try to get position via browser geolocation
    if ('geolocation' in navigator) {
      try {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          gatedGetCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 8000,
            maximumAge: 30000,
          });
        });
        const coords: Coordinates = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        };
        buildAndStartNavigation(coords, spot);
        return;
      } catch {
        // Geolocation denied or timed out — fall through to route planning dialog
      }
    }

    // Fallback: open existing route planning dialog where user picks start point
    setRouteStartMode('address');
    setRouteAddressQuery('');
    setRouteAddressResults([]);
    setRouteAddressStart(null);
    setRouteAddressLabel('');
    setRouteAddressLockedQuery('');
    setRouteAddressConfirmed(false);
    setRouteMapStart(null);
    setRouteMapStartLabel('');
    setIsPickingRouteStartOnMap(false);
    setRoutePendingSpotForMapPick(null);
    setRoutePlanningSpot(spot);
  }, [userPosition, buildAndStartNavigation]);

  // Snow check when starting GPS tracking
  const handleStartTrackingWithSnowCheck = useCallback(() => {
    openFollowPreflight({
      mode: 'free_drive_follow',
      title: 'Kjorevisning',
      bounds: currentViewBounds,
      continueAction: () => {
        startTracking();
        if ('geolocation' in navigator) {
          gatedGetCurrentPosition(async (pos) => {
            try {
              const { fetchSnowDepth } = await import('./services/snowService');
              const snow = await fetchSnowDepth(pos.coords.latitude, pos.coords.longitude);
              if (snow && snow.depthCm > 10) {
                addToast(t('tracking.snow_warning', { depth: Math.round(snow.depthCm) }), 'warning');
              }
            } catch { /* ignore */ }
          }, () => {}, { enableHighAccuracy: false, timeout: 5000 });
        }
      }
    });
  }, [currentViewBounds, startTracking, addToast, t, openFollowPreflight]);

  const WAYPOINT_LABELS: Record<string, string> = {
    camp: 'Leir', parking: 'Parkering', water: 'Vann', viewpoint: 'Utsikt',
    danger: 'Fare', food: 'Mat', star: 'Favoritt', pin: 'Punkt',
  };

  const handleAddTrackWaypoint = (icon: WaypointIcon) => {
    const label = WAYPOINT_LABELS[icon] || 'Punkt';
    addTrackWaypoint(icon, label);
    // Also save as a persistent user waypoint i Mitt innhold
    const lastPt = trackPoints[trackPoints.length - 1];
    if (lastPt && currentUser?.uid) {
      const color = getWaypointMeta(icon).defaultColor;
      addWaypointContent(currentUser.uid, lastPt.latitude, lastPt.longitude, label, icon, color).catch(() => {});
    }
    addToast(t('tracking.waypoint_added', label));
  };

  const [isUploadingWaypointPhoto, setIsUploadingWaypointPhoto] = useState(false);
  const [contextualAlerts, setContextualAlerts] = useState<ContextualAlert[]>([]);
  const [showAARouteSheet, setShowAARouteSheet] = useState(false);

  const handleAddPhotoWaypoint = async () => {
    if (!currentUser?.uid) { addToast('Logg inn for å legge til waypoints', 'warning'); return; }
    const lastPt = trackPoints[trackPoints.length - 1];
    if (!lastPt) { addToast('Venter på GPS-posisjon...', 'warning'); return; }

    const file = await capturePhoto();
    if (!file) return;

    setIsUploadingWaypointPhoto(true);
    try {
      // Last opp bilde med en stabil filnavn-id (uavhengig av Firestore doc-id)
      const photoFileId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const photoUrl = await uploadWaypointPhoto(currentUser.uid, photoFileId, file.file);
      const color = getWaypointMeta('pin').defaultColor;
      await addWaypointContent(
        currentUser.uid, lastPt.latitude, lastPt.longitude, 'Foto', 'pin', color,
        undefined, undefined, photoUrl
      );
      addTrackWaypoint('pin', 'Foto');
      addToast('Foto-waypoint lagret');
    } catch (e) {
      console.error('Photo waypoint failed:', e);
      addToast('Kunne ikke lagre foto', 'error');
    } finally {
      setIsUploadingWaypointPhoto(false);
    }
  };

  const handleStopAndSaveTrack = async (name: string) => {
    stopTracking();
    const followedRouteId = followRouteState?.routeId ?? undefined;
    setFollowRouteState(null);
    if (!currentUser?.uid || trackPoints.length < 2) {
      resetTracking();
      return;
    }
    setTrackSaveError(null);
    try {
      const savedTrack = await saveTrack(currentUser.uid, name, trackPoints, trackStats, trackWaypoints, followedRouteId);
      resetTracking();
      addToast(t('toast.track_saved'));
      // Konverter til rute og åpne rute-kortet
      if (currentUser) {
        try {
          const routeId = await convertTrackToRoute(savedTrack, currentUser);
          if (routeId) {
            await loadRoutesList();
            setRouteReturnContext(null);
            setSelectedRouteId(routeId);
            setActiveRouteIds(new Set([routeId]));
            setSidebarMode('routes');
            expandSidebar();
          }
        } catch {
          // Konvertering feilet — turen er likevel lagret
        }
      }
    } catch {
      setTrackSaveError('Kunne ikke lagre turen. Sjekk internettforbindelsen og prøv igjen.');
      addToast(t('toast.track_save_error'), 'error');
      resetTracking();
    }
  };

  const shouldFetchBackground = !selectedRouteId && !previewRegion;
  // When the Fricamp spots layer is active, other layers wait until spots have loaded first.
  // This ensures Fricamp spots always appear before any secondary layer data.
  const layersUnblocked = !layers.algorithmic || spotsReady;

  const { isLoading: loadingRest, error: errorRest } = useRestAreas(layers.restAreas && shouldFetchBackground && layersUnblocked, mapZoom, mapCenter, currentViewBounds, (newSpots) => {
    setSpots(prev => mergeSpotsByIdentity(prev, newSpots));
  }, effectiveNordicConfig?.sweden, effectiveNordicConfig?.denmark, effectiveNordicConfig?.finland);

  // Batched: amenities + dump stations + playgrounds in a single Overpass query.
  // Kommersielle campingplasser hentes IKKE lenger fra live-Overpass her — de
  // serveres nå fra `cached_spots` via getSpotMarkers (seedet DB-cache, se
  // scripts/seed-osm-campsites.mjs) og filtreres klientsidig på commercialCampsites/
  // commercialRvParks-laget. Dette dreper 429/spinner-problemet på campingplass-laget.
  const { isLoading: loadingPOIBatch, errorOsm, errorDump, errorCamp, errorPlaygrounds } = useOverpassPOIBatch(
    layers.osmAmenities && shouldFetchBackground && layersUnblocked,
    (!!currentUser?.needsEmptying || layers.dumpStations) && shouldFetchBackground && layersUnblocked,
    false,
    layers.playgrounds && shouldFetchBackground && layersUnblocked,
    mapZoom,
    currentViewBounds,
    (newSpots) => { setSpots(prev => mergeSpotsByIdentity(prev, newSpots)); }
  );

  const { isLoading: loadingCoastal, error: errorCoastal } = useCoastalSpots(layers.coastal && shouldFetchBackground && layersUnblocked, mapZoom, currentViewBounds, (newSpots) => {
    setSpots(prev => mergeSpotsByIdentity(prev, newSpots));
  });

  const appErrorState = useMemo(() => ({
    restAreas: errorRest,
    osmAmenities: errorOsm,
    dumpStations: errorDump,
    commercialCampsites: errorCamp,
    coastal: errorCoastal,
    playgrounds: errorPlaygrounds,
  }), [errorRest, errorOsm, errorDump, errorCamp, errorCoastal, errorPlaygrounds]);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 640;
      setIsDesktop(desktop);
      if (!desktop) setIs3DMode(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Ensure web starts in news feed mode by default.
    if (window.innerWidth >= 640) {
      setSidebarMode('news');
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem('fricamp_sidebar_width', String(sidebarWidth));
  }, [sidebarWidth]);

  useEffect(() => {
    window.localStorage.setItem('fricamp_rail_expanded', railExpanded ? '1' : '0');
  }, [railExpanded]);

  // Fetch news count for rail badge
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [spots, reviews] = await Promise.all([
          fetchRecentVerifiedSpots(20),
          fetchRecentReviews(20),
        ]);
        if (cancelled) return;
        const visibleSpots = spots.filter(s => isSpotInEnabledCountry(s, effectiveNordicConfig));
        const newItems = [...visibleSpots, ...reviews].filter(item => {
          const ts = (item as any).verifiedAt?.toMillis?.() || (item as any).createdAt?.toMillis?.() || (item as any).verifiedAt || (item as any).createdAt || 0;
          return ts > newsLastSeen;
        });
        setNewsCount(newItems.length);
      } catch { /* silent */ }
    };
    load();
    const interval = setInterval(load, 5 * 60 * 1000); // refresh every 5 min
    return () => { cancelled = true; clearInterval(interval); };
  }, [newsLastSeen, effectiveNordicConfig]);

  // Mark news as seen when user views news tab (desktop: utvidet sidebar,
  // mobil: full-liste-visning — sidebarExpanded er alltid false på mobil).
  useEffect(() => {
    if (isNewsFeedVisible(sidebarMode, sidebarExpanded, mobileView)) {
      const now = Date.now();
      setNewsLastSeen(now);
      setNewsCount(0);
      window.localStorage.setItem('fricamp_news_last_seen', String(now));
    }
  }, [sidebarMode, sidebarExpanded, mobileView]);

  // «Nytt i Mitt»: samarbeidsmapper der ANDRE medlemmer har lagt til innhold (plasser/
  // ruter/pakkelister) eller sendt chat siden sist. Vises som tall-badge på Mitt i både
  // desktop-rail og mobil-bunnmeny.
  const myContentBadgeCount = useMemo(() => {
    const uid = currentUser?.uid;
    if (!uid) return 0;
    return collaborativeMaps.filter((m) => hasUnseenCollabContent(m, uid) || hasUnreadCollabChat(m, uid)).length;
  }, [collaborativeMaps, currentUser?.uid]);

  // NB: innhold markeres som sett når brukeren åpner DEN ENKELTE mappa (MyContentInner
  // → markCollabContentSeen), ikke ved panel-åpning — ellers forsvinner «Nytt»-merket
  // på mappe-raden før brukeren rekker å se hvilken mappe det gjaldt.

  useEffect(() => {
    if (!routeCreationStatus || routeCreationStatus.type === 'loading') return;
    const timer = window.setTimeout(() => setRouteCreationStatus(null), 4500);
    return () => window.clearTimeout(timer);
  }, [routeCreationStatus]);

  useEffect(() => {
    if (!routePlanningSpot) return;
    const hintKey = `fricamp_hint_routeplanner_v1_${currentUser?.uid || 'anon'}`;
    const dismissed = window.localStorage.getItem(hintKey) === '1';
    setShowRoutePlannerHint(!dismissed);
  }, [routePlanningSpot, currentUser?.uid]);

  const dismissRoutePlannerHint = () => {
    const hintKey = `fricamp_hint_routeplanner_v1_${currentUser?.uid || 'anon'}`;
    window.localStorage.setItem(hintKey, '1');
    setShowRoutePlannerHint(false);
  };

  // Auto-collapse sidebar when route planning starts
  useEffect(() => {
    if (routePlan && sidebarExpanded) {
      setSidebarExpanded(false);
    }
  }, [!!routePlan]);

  // Auto-fetch route when routePlan changes (new dest, stops added/removed)
  useEffect(() => {
    if (!routePlan) return;
    const from = routePlan.fromCoords || userPosition;
    if (!from) {
      setRoutePlanError(t('routePlanner.no_gps', 'Kan ikke planlegge rute uten GPS-posisjon'));
      return;
    }
    const { toCoords, waypoints } = routePlan;
    let cancelled = false;

    const doFetch = async () => {
      setRoutePlanFetching(true);
      setRoutePlanError(null);
      try {
        // Reverse-geocode start for display name (only when using GPS)
        if (!routePlan.fromName) {
          try {
            const startName = await reverseGeocode(from);
            if (!cancelled) setRoutePlanFromName(startName && !isCoordinateLabel(startName) ? startName : 'Min posisjon');
          } catch { if (!cancelled) setRoutePlanFromName('Min posisjon'); }
        } else {
          if (!cancelled) setRoutePlanFromName(routePlan.fromName);
        }

        let alts: RoutePlanAlternative[];
        if (waypoints.length === 0) {
          alts = await fetchRouteAlternatives(from, toCoords);
        } else {
          alts = await fetchMultiStopRoute(from, waypoints.map(w => w.coords), toCoords);
        }
        if (!cancelled) {
          setRoutePlanAlternatives(alts);
          setRoutePlanSelectedIndex(alts.length > 0 ? 0 : null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setRoutePlanError(t('routePlanner.fetch_error', 'Kunne ikke hente rute'));
          setRoutePlanAlternatives([]);
          setRoutePlanSelectedIndex(null);
        }
      } finally {
        if (!cancelled) setRoutePlanFetching(false);
      }
    };

    doFetch();
    return () => { cancelled = true; };
  }, [routePlan, userPosition]);

  // Reise-copilot: setter KUN routePlan (samme kodesti som RoutePlannerModal) — den
  // eksisterende auto-fetch-effekten over tegner multi-stopp-ruta. Ingen konkurrerende
  // skriving av routePlanAlternatives.
  const handleCopilotDrawRoute = useCallback((result: PlanTripSuccess) => {
    setRoutePlan({
      fromCoords: result.fromCoords,
      fromName: result.resolvedOriginName || 'Start',
      toCoords: result.toCoords,
      toName: result.resolvedDestName || 'Mål',
      waypoints: result.stops.map((s) => ({ coords: s.location, name: stopLabel(s) })),
    });
  }, []);
  const handleCopilotClearRoute = useCallback(() => {
    setRoutePlan(null);
    setRoutePlanAlternatives([]);
    setRoutePlanSelectedIndex(null);
  }, []);
  // Copiloten: DESKTOP = venstre sidepanel (sidebarMode). MOBIL = dra-bart bunn-ark over
  // kartet (copilotSheetOpen) så kartet forblir synlig. To layouts, én hook.
  const [copilotSheetOpen, setCopilotSheetOpen] = useState(false);
  const [copilotMobileExpanded, setCopilotMobileExpanded] = useState(false); // mobil-ark i full/half
  const [copilotExpandSignal, setCopilotExpandSignal] = useState(0); // «Utforsk ruten» → utvid mobil-ark
  const copilotOpen = isDesktop ? (sidebarMode === 'copilot' && sidebarExpanded) : copilotSheetOpen;
  const openCopilot = useCallback(() => {
    if (isDesktop) { setSidebarMode('copilot'); setSidebarExpanded(true); }
    else {
      // Mobil: åpne arket OG be det utvide seg — arket kan stå usynlig i «pille»-modus
      // (planlagt fase), og da må COPILOT-fanen alltid hente chatten frem igjen.
      setCopilotSheetOpen(true);
      setCopilotExpandSignal((n) => n + 1);
    }
  }, [isDesktop]);
  // «Utforsk ruten» (RoutePlanBar, copilot-ruter): åpne copilotens kuraterte innhold
  // (dag-plan/severdigheter/vær) i stedet for å starte navigasjon.
  const exploreCopilotRoute = useCallback(() => {
    if (isDesktop) { setSidebarMode('copilot'); setSidebarExpanded(true); }
    else { setCopilotSheetOpen(true); setCopilotExpandSignal((n) => n + 1); }
  }, [isDesktop]);
  // Rik copilot-kontekst: kjøretøy/hjem/tier + «Mitt»-data (favoritter, besøkt, kartlag,
  // innhold). Kompakt (navn/tellinger) for AI-en; favoritt-plassene m/ koordinater gis
  // separat til planleggeren for å favorisere dem som stopp langs ruten.
  const copilotFavoriteSpots = useMemo(
    () => spots.filter((s) => favoriteSpotIds.includes(s.id)),
    [spots, favoriteSpotIds],
  );
  const copilotContext = useMemo(() => {
    if (!currentUser) return null;
    return {
      vehicleCategory: currentUser.activeVehicleCategory,
      vehicleLength: currentUser.activeVehicleLength,
      terrainPrefs: currentUser.activeVehicleTerrainPrefs,
      homeCounty: currentUser.homeCounty,
      homeMunicipality: currentUser.homeMunicipality,
      tier: currentUser.tier,
      favorites: copilotFavoriteSpots.slice(0, 20).map((s) => ({
        name: s.name, latitude: s.location.latitude, longitude: s.location.longitude,
      })),
      visitedCount: visits.length,
      recentVisited: visits.slice(-12).reverse().map((v) => v.spotName).filter(Boolean),
      activeLayers: Object.entries(layers).filter(([, v]) => v === true).map(([k]) => k).slice(0, 12),
      myContentCounts: { folders: myContent.folders?.length || 0, items: myContent.content?.length || 0 },
    } as Record<string, unknown>;
  }, [currentUser, copilotFavoriteSpots, visits, layers, myContent]);
  const copilot = useTripCopilot({
    user: currentUser,
    open: copilotOpen,
    onDrawRoute: handleCopilotDrawRoute,
    onClearRoute: handleCopilotClearRoute,
    currentPosition: userPosition,
    context: copilotContext,
    favoriteSpots: copilotFavoriteSpots,
  });
  // Total lengde/tid for copilot-ruten (fra tegnet alternativ) + «utforsk stopp».
  const copilotRouteStats = useMemo(() => {
    const alt = routePlanAlternatives[routePlanSelectedIndex ?? 0];
    if (!alt) return null;
    return { km: Math.round((alt.distanceMeters || 0) / 1000), durationText: formatDuration(alt.durationSeconds || 0) };
  }, [routePlanAlternatives, routePlanSelectedIndex]);
  // NB: onExploreStop wires inline i JSX (onMobileSelectSpot defineres lenger ned).

  // Delelenke: ?copilotTrip=<shareId> → last delt tur + åpne copiloten (én gang).
  const sharedTripHandledRef = useRef(false);
  useEffect(() => {
    if (sharedTripHandledRef.current) return;
    const shareId = new URLSearchParams(window.location.search).get('copilotTrip');
    if (!shareId) return;
    sharedTripHandledRef.current = true;
    (async () => {
      const shared = await loadSharedTrip(shareId);
      if (shared) {
        copilot.openSharedSpec(shared.tripSpec, shared.title);
        openCopilot();
      }
      // Rydd param så refresh ikke laster på nytt.
      const url = new URL(window.location.href);
      url.searchParams.delete('copilotTrip');
      window.history.replaceState({}, '', url.toString());
    })();
  }, [copilot, openCopilot]);

  const handleAddRouteStop = useCallback(async (coords: Coordinates) => {
    if (!routePlan) return;
    let name = `${coords?.latitude?.toFixed(4) || '0'}, ${coords?.longitude?.toFixed(4) || '0'}`;
    try {
      const geo = await reverseGeocode(coords);
      if (geo) name = geo;
    } catch { /* keep coord name */ }
    setRoutePlan(prev => prev ? {
      ...prev,
      waypoints: [...prev.waypoints, { coords, name }]
    } : prev);
  }, [routePlan]);

  const handleRemoveRouteStop = useCallback((index: number) => {
    setRoutePlan(prev => prev ? {
      ...prev,
      waypoints: prev.waypoints.filter((_, i) => i !== index)
    } : prev);
  }, []);

  const handleCloseRoutePlan = useCallback(() => {
    setRoutePlan(null);
    setRoutePlanAlternatives([]);
    setRoutePlanSelectedIndex(null);
    setRoutePlanFetching(false);
    setRoutePlanFromName('');
    setRoutePlanError(null);
    setShowRoutePlanContent(false);
    setRoutePreviewWebcams([]);
  }, []);

  useEffect(() => {
    if (routeStartMode !== 'address' || !routePlanningSpot) return;
    if (routeAddressConfirmed) return;
    const q = routeAddressQuery.trim();
    const locked = routeAddressLockedQuery.trim();
    if (locked && q === locked) {
      setRouteAddressResults([]);
      setRouteAddressLoading(false);
      return;
    }
    if (q.length < 3) {
      setRouteAddressResults([]);
      setRouteAddressLoading(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setRouteAddressLoading(true);
      try {
        const searchCodes = ['no'];
        if (effectiveNordicConfig?.sweden || searchBypassGating) searchCodes.push('se');
        if (effectiveNordicConfig?.denmark || searchBypassGating) searchCodes.push('dk');
        if (effectiveNordicConfig?.finland) searchCodes.push('fi');
        if (effectiveNordicConfig?.iceland) searchCodes.push('is');
        const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=8&q=${encodeURIComponent(q)}&countrycodes=${searchCodes.join(',')}`;
        const [photonResults, nominatimResults] = await Promise.all([
          searchPlace(q).catch(() => []),
          fetch(nominatimUrl, { headers: { Accept: 'application/json', 'Accept-Language': 'no,nb,nn,en' } })
            .then((r) => (r.ok ? r.json() : []))
            .catch(() => [])
        ]);

        const normalizedNominatim = (Array.isArray(nominatimResults) ? nominatimResults : []).map((r: any) => ({
          display_name: r.display_name,
          lat: r.lat,
          lon: r.lon,
          type: r.type || 'address',
          source: 'nominatim'
        }));

        const merged = [...(photonResults || []), ...normalizedNominatim];
        const dedup = new Map<string, any>();
        merged.forEach((r: any) => {
          const key = `${Number(r.lat).toFixed(5)},${Number(r.lon).toFixed(5)}`;
          if (!dedup.has(key)) dedup.set(key, r);
        });

        if (!cancelled) setRouteAddressResults(Array.from(dedup.values()).slice(0, 10));
      } catch {
        if (!cancelled) setRouteAddressResults([]);
      } finally {
        if (!cancelled) setRouteAddressLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [routeAddressQuery, routeAddressLockedQuery, routeAddressConfirmed, routeStartMode, routePlanningSpot]);


  // Show premium nudge when free user zooms past algorithmic threshold
  // Wait for _firestoreReady to avoid flashing nudge before user data (isTester) is loaded
  useEffect(() => {
    if (!authReady) return;
    if (currentUser && !currentUser._firestoreReady) return;
    const isPaidUserLocal = hasPaidAccess(currentUser);
    if (!isPaidUserLocal && mapZoom > FREE_ZOOM_MAX && layers.algorithmic) {
      nudgeRef.current?.showNudge('zoom_algorithmic');
    }
  }, [authReady, currentUser, mapZoom, layers.algorithmic]);

  useEffect(() => {
    if (didStartupLocateRef.current) return;
    if (parsedSharedMap) return;
    didStartupLocateRef.current = true;

    const applyPosition = (lat: number, lon: number, z: number) => {
      const center = { latitude: lat, longitude: lon };
      setMapCenter(center);
      setMapZoom(z);
      setCurrentViewBounds({ latMin: lat - 0.06, latMax: lat + 0.06, lonMin: lon - 0.1, lonMax: lon + 0.1 });
      setMapCommand({ center, zoom: z, id: Date.now() });
    };

    const restoreLastPosition = (): boolean => {
      try {
        const saved = localStorage.getItem('fricamp_last_position');
        if (!saved) return false;
        const { latitude, longitude, zoom } = JSON.parse(saved);
        if (typeof latitude !== 'number' || typeof longitude !== 'number') return false;
        applyPosition(latitude, longitude, typeof zoom === 'number' ? zoom : 14);
        return true;
      } catch { return false; }
    };

    const ipGeolocate = async () => {
      try {
        const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(5000) });
        if (!res.ok) return;
        const data = await res.json();
        if (typeof data.latitude === 'number' && typeof data.longitude === 'number') {
          applyPosition(data.latitude, data.longitude, 10);
        }
      } catch { /* IP geolocation failed – stay on default center */ }
    };

    const fallback = () => {
      if (!restoreLastPosition()) {
        ipGeolocate();
      }
    };

    if (!navigator.geolocation) { fallback(); return; }

    gatedGetCurrentPosition(
      (pos) => applyPosition(pos.coords.latitude, pos.coords.longitude, 14),
      () => fallback(),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 600000 }
    );
  }, [parsedSharedMap]);

  useEffect(() => {
    if (!isDesktop && showMobileSheet) {
      setMobileView('map');
    }
  }, [isDesktop, showMobileSheet]);

  useEffect(() => {
    // Hard guard: mobile list and mobile bottom sheet must never be visible together.
    if (!isDesktop && mobileView === 'list' && showMobileSheet) {
      setShowMobileSheet(false);
    }
  }, [isDesktop, mobileView, showMobileSheet]);

  useEffect(() => {
    if (currentUser && currentUser._firestoreReady && currentUser.subscriptionStatus === undefined) {
      setShowOnboarding(true);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!forcedLanguageParam) return;
    if (forcedLanguageParam !== i18n.language) {
      i18n.changeLanguage(forcedLanguageParam);
    }
    try {
      window.localStorage.setItem('i18nextLng', forcedLanguageParam);
    } catch {
      // Ignore localStorage write issues.
    }
  }, [forcedLanguageParam]);

  useEffect(() => {
    // When a shared/deep link explicitly carries language, honor that over profile language.
    if (forcedLanguageParam) return;
    if (currentUser?.language && currentUser.language !== i18n.language) {
      i18n.changeLanguage(currentUser.language);
    }
  }, [currentUser?.language, forcedLanguageParam]);

  // Sync theme from Firestore profile when user logs in
  useEffect(() => {
    if (currentUser?.themePreference) {
      setAppTheme(currentUser.themePreference);
    }
  }, [currentUser?.themePreference, setAppTheme]);

  // Load map layer preferences from Firestore when user logs in.
  // localStorage represents the most recent on-device choice and always wins over
  // Firestore for conflicting keys — Firestore is only consulted to fill in keys
  // localStorage lacks (typically first sync to a new device). This prevents a
  // stale Firestore value from clobbering an uncheck the user just made if the
  // debounced save hadn't yet completed last session.
  const layersLoadedFromFirestore = useRef(false);
  useEffect(() => {
    if (currentUser?.mapLayers && !layersLoadedFromFirestore.current) {
      layersLoadedFromFirestore.current = true;
      let localStorageLayers: Record<string, boolean> | null = null;
      try {
        const saved = window.localStorage.getItem('fricamp_map_layers');
        if (saved) localStorageLayers = JSON.parse(saved);
      } catch { /* ignore */ }
      setLayers(prev => migrateLayers({
        ...prev,
        ...currentUser.mapLayers,
        ...(localStorageLayers || {}),
      }));
    }
  }, [currentUser?.mapLayers]);

  // Sync map layers with vehicle road filters when the user actively changes them
  // during the session. We deliberately skip the first real Firestore fire so we
  // don't override the user's saved layer toggles every login.
  const prevRoadFiltersRef = useRef<string | null>(null);
  const offroadFiltersInitialized = useRef(false);
  useEffect(() => {
    const rf = currentUser?.offroadRoadFilters;
    if (!rf) return;
    if (!currentUser?._firestoreReady) return; // ignore preliminary callback
    const key = JSON.stringify(rf);
    if (!offroadFiltersInitialized.current) {
      offroadFiltersInitialized.current = true;
      prevRoadFiltersRef.current = key;
      return; // first real fire: record baseline, don't apply to layers
    }
    if (prevRoadFiltersRef.current === key) return;
    prevRoadFiltersRef.current = key;

    const wantForest = !!rf.unclassified;
    const wantTractor = !!rf.track;
    const wantService = !!rf.service;
    setLayers((prev: typeof layers) => ({
      ...prev,
      forestRoads: wantForest,
      tractorRoads: wantTractor,
      serviceRoads: wantService,
      barriers: (wantForest || wantTractor || wantService) ? true : false,
    }));
  }, [currentUser?.offroadRoadFilters, currentUser?._firestoreReady]);

  useEffect(() => {
    if (hasBathingLayerAccess || !layers.bathingPlaces) return;
    setLayers(prev => ({ ...prev, bathingPlaces: false }));
  }, [hasBathingLayerAccess, layers.bathingPlaces]);

  // Persist layer changes to localStorage + Firestore.
  // Short debounce coalesces React-batched updates within a single user action,
  // then a pagehide/visibilitychange flush guarantees the save survives reloads.
  const layerSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingLayerSave = useRef<{ uid: string; layers: typeof layers } | null>(null);
  const isFirstLayerRender = useRef(true);
  useEffect(() => {
    if (isFirstLayerRender.current) { isFirstLayerRender.current = false; return; }
    try { window.localStorage.setItem('fricamp_map_layers', JSON.stringify(layers)); } catch { /* ignore */ }
    if (currentUser?.uid) {
      const uid = currentUser.uid;
      pendingLayerSave.current = { uid, layers };
      if (layerSaveTimer.current) clearTimeout(layerSaveTimer.current);
      layerSaveTimer.current = setTimeout(() => {
        const pending = pendingLayerSave.current;
        layerSaveTimer.current = null;
        pendingLayerSave.current = null;
        if (pending) {
          updateUserSettings(pending.uid, { mapLayers: pending.layers })
            .catch(err => console.warn('[mapLayers] save failed:', err));
        }
      }, 200);
    }
    // No cleanup that clears the timer — letting it fire after unmount is safe and
    // ensures the save reaches Firestore even if the component tears down quickly.
  }, [layers, currentUser?.uid]);

  // Flush any pending layer save when the tab is hidden or about to unload,
  // so a quick reload after toggling layers doesn't lose the write.
  useEffect(() => {
    const flush = () => {
      const pending = pendingLayerSave.current;
      if (!pending) return;
      if (layerSaveTimer.current) {
        clearTimeout(layerSaveTimer.current);
        layerSaveTimer.current = null;
      }
      pendingLayerSave.current = null;
      updateUserSettings(pending.uid, { mapLayers: pending.layers })
        .catch(err => console.warn('[mapLayers] flush save failed:', err));
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  useEffect(() => {
    loadRoutesList();
    loadSystemConfig();

    const params = new URLSearchParams(window.location.search);
    const reviewId = params.get('openReview');
    if (reviewId) {
      setSharedReviewId(reviewId);
    }
    if (parsedSharedMap) {
      setMapCommand({
        center: parsedSharedMap.center,
        zoom: parsedSharedMap.zoom,
        id: Date.now()
      });
    }
  }, []);

  useEffect(() => {
    if (!collabInviteParam || !collabTokenParam || !currentUser) return;
    joinMapByToken(collabInviteParam, collabTokenParam, currentUser).then(result => {
      if (result === 'ok' || result === 'already_member') {
        setActiveCollabMapId(collabInviteParam);
        setAddToMapSpotId(null);
        setShowCollabMapModal(true);
        logFeatureUsage('collab_map_view');
      }
    }).catch(console.error);
  }, [currentUser?.uid]);

  const loadSystemConfig = async () => {
    const safe = async (key: string) => {
      try { return await fetchSystemConfig(key); } catch { return null; }
    };
    const [config, vippsConfig, nordicCfg] = await Promise.all([
      safe('config'),
      safe('vipps_config'),
      safe('nordic_config'),
    ]);
    if (config) {
      setTesterApplicationsEnabled(config.tester_applications_enabled !== false);
    }
    setVippsPaymentEnabled(vippsConfig?.enabled === true);

    // MISLYKKET LESING ER IKKE «INGEN LAND ER AKTIVERT».
    //
    // Før satte vi alle fire til false når `nordic_config` ikke kunne leses, og
    // skrev det til localStorage. Da forsvant hver eneste svenske, danske og
    // finske plass fra kartet — stille, uten feilmelding, fordi
    // land-filteret i useSpotManager bare ser «ikke aktivert».
    //
    // Målt 15.08.2026: en nettleserutvidelse blokkerte firestore.googleapis.com
    // (`ERR_BLOCKED_BY_CLIENT`), og da traff nettopp dette. Verre: den falske
    // verdien ble cachet, så neste oppstart begynte allerede blind. Samme skjer
    // ved dårlig nett eller tracking-prevention som bremser IndexedDB.
    //
    // Nå: kunne vi ikke lese, beholder vi forrige kjente verdi (state → cache),
    // og markerer først `ready` når vi faktisk VET noe. Alt-av brukes bare når
    // vi aldri har sett en konfigurasjon.
    if (nordicCfg) {
      const parsed = {
        sweden: nordicCfg.sweden === true,
        denmark: nordicCfg.denmark === true,
        finland: nordicCfg.finland === true,
        iceland: nordicCfg.iceland === true,
      };
      setNordicConfig(parsed);
      setNordicConfigReady(true);
      try { localStorage.setItem('fricamp_nordic_config', JSON.stringify(parsed)); } catch {}
      return;
    }

    console.warn('[nordic_config] kunne ikke leses — beholder forrige kjente verdi. '
      + 'Sjekk om noe blokkerer firestore.googleapis.com (annonseblokker/VPN).');
    setNordicConfig((forrige) => (forrige && Object.keys(forrige).length
      ? forrige
      : { sweden: false, denmark: false, finland: false, iceland: false }));
    setNordicConfigReady(true);
  };

  useEffect(() => {
    if (!showAdmin) {
      loadSystemConfig();
    }
  }, [showAdmin]);

  // Real-time admin badge: count unread tester messages
  const isCurrentUserAdmin = isAdminUser(currentUser);
  useEffect(() => {
    if (!isCurrentUserAdmin) { setAdminBadgeCount(0); return; }
    const unsub = subscribeToTesterThreadsForAdmin((threads) => {
      const unreadMessages = threads.reduce((sum, t) => sum + (t.unreadForAdmin || 0), 0);
      setAdminBadgeCount(unreadMessages);
    });
    return unsub;
  }, [isCurrentUserAdmin]);

  useEffect(() => {
    if (selectedRouteId) {
      const route = routes.find(r => r.id === selectedRouteId);
      if (route && route.wikiSpots) {
        setWikiSpots(route.wikiSpots);
      } else {
        setWikiSpots([]);
      }
    } else {
      setWikiSpots([]);
    }
  }, [selectedRouteId, routes]);

  // Deep link: when /app/route/{id} is opened, select the route and zoom to it
  useEffect(() => {
    if (!pendingRouteId || routes.length === 0) return;
    const route = routes.find(r => r.id === pendingRouteId);
    if (route) {
      setActiveRouteIds(new Set([route.id]));
      setSelectedRouteId(route.id);
      setSidebarMode('routes');
      expandSidebar();
      setPendingRouteId(null);
      window.history.replaceState({}, '', '/');
    }
  }, [pendingRouteId, routes]);

  // Deep link: /app/open/spot/{id} should open main app, focus the spot and optionally auto-favorite.
  useEffect(() => {
    if (!pendingOpenSpotId) return;

    const spot = spots.find(s => s.id === pendingOpenSpotId);
    if (spot) {
      if (!pendingOpenSpotSelectionRef.current.has(pendingOpenSpotId)) {
        pendingOpenSpotSelectionRef.current.add(pendingOpenSpotId);
        handleSelectSpot(spot);
        setSidebarMode('spots');
        setSidebarExpanded(true);
      }

      if (!pendingOpenSpotAutoFavorite) {
        setPendingOpenSpotId(null);
        window.history.replaceState({}, '', '/');
      }
      return;
    }

    if (pendingOpenSpotFetchRef.current.has(pendingOpenSpotId)) return;
    pendingOpenSpotFetchRef.current.add(pendingOpenSpotId);

    fetchSpotsByIds([pendingOpenSpotId])
      .then((loaded) => {
        if (!loaded || loaded.length === 0) {
          setPendingOpenSpotAutoFavorite(false);
          setPendingOpenSpotId(null);
          window.history.replaceState({}, '', '/');
          return;
        }
        setSpots(prev => mergeSpotsByIdentity(prev, loaded));
      })
      .catch((e) => console.warn('Failed to load deep-linked spot:', e));
  }, [pendingOpenSpotId, pendingOpenSpotAutoFavorite, spots, handleSelectSpot, setSpots]);

  useEffect(() => {
    if (!pendingOpenSpotId || !pendingOpenSpotAutoFavorite) return;
    if (!spots.some(s => s.id === pendingOpenSpotId)) return;

    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }

    const requestKey = `${currentUser.uid}:${pendingOpenSpotId}`;
    if (pendingOpenSpotFavoriteRef.current.has(requestKey)) return;
    pendingOpenSpotFavoriteRef.current.add(requestKey);

    if (favoriteSpotIds.includes(pendingOpenSpotId)) {
      setPendingOpenSpotAutoFavorite(false);
      setPendingOpenSpotId(null);
      window.history.replaceState({}, '', '/');
      return;
    }

    toggleFavoriteSpot(pendingOpenSpotId, currentUser.uid, false)
      .then(() => addToast(t('toast.favorite_added')))
      .catch((err) => {
        console.error('Kunne ikke autofavoritt-merke plass', err);
        addToast(t('toast.error_generic'), 'error');
      })
      .finally(() => {
        setPendingOpenSpotAutoFavorite(false);
        setPendingOpenSpotId(null);
        window.history.replaceState({}, '', '/');
      });
  }, [pendingOpenSpotId, pendingOpenSpotAutoFavorite, spots, currentUser, favoriteSpotIds, addToast, t]);

  const loadRoutesList = async () => {
    try {
      const allRoutes = await fetchAllRoutes();
      setRoutes(allRoutes);
      return allRoutes;
    } catch (e) {
      console.error("Failed to load routes");
      return routes;
    }
  };

  const visibleRoutes = useMemo(() => {
    const base = routes.filter(r =>
      r.status === 'public' || (currentUser && r.createdBy === currentUser.uid)
    );
    if (!currentUser?.activeVehicleCategory || routeVehicleFilterOff) return base;
    return base.filter(r => {
      if (!r.suitableCategories?.length) return true;
      if (r.suitableCategories.includes(currentUser.activeVehicleCategory!)) return true;
      if (currentUser.activeVehicleEquipment?.four_wheel_drive && r.suitableCategories.includes('offroad')) return true;
      return false;
    });
  }, [routes, currentUser, routeVehicleFilterOff]);

  const visibleRoutesWithSelection = useMemo(() => {
    // Force-include both the selected route AND any active route (shown on map).
    // Without the active-route guarantee, tapping "Vis i kart" clears selectedRouteId
    // and a vehicle-filtered route would drop out of the list — leaving activeRouteIds
    // pointing at a route the map can no longer find, so no polyline is drawn and the
    // card can't be reopened by tapping the line.
    const present = new Set(visibleRoutes.map((route) => route.id));
    const mustInclude = new Set<string>();
    if (selectedRouteId && !present.has(selectedRouteId)) mustInclude.add(selectedRouteId);
    activeRouteIds.forEach((id) => { if (!present.has(id)) mustInclude.add(id); });
    if (mustInclude.size === 0) return visibleRoutes;
    const extra = Array.from(mustInclude)
      .map((id) => routes.find((route) => route.id === id))
      .filter((route): route is GpxRoute => !!route);
    return extra.length ? [...extra, ...visibleRoutes] : visibleRoutes;
  }, [routes, selectedRouteId, activeRouteIds, visibleRoutes]);

  const markLocalNotifAsRead = (id: string) => {
    setReadLocalNotifIds(prev => {
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      window.localStorage.setItem('fricamp_read_local_notifs', JSON.stringify(next));
      return next;
    });
  };

  const handleMarkNotificationAsRead = async (notif: AppNotification) => {
    if (notif.id.startsWith('system-') || notif.id.startsWith('varsom-')) {
      markLocalNotifAsRead(notif.id);
    } else if (currentUser) {
      // It's a Firestore notification
      await markNotificationAsRead(currentUser.uid, notif.id);
    }
  };

  const handleMarkAllNotificationsAsRead = async () => {
    if (currentUser) {
      const firestoreIds = notifications.filter(n => !n.isRead).map(n => n.id);
      if (firestoreIds.length > 0) {
        await markAllNotificationsAsRead(currentUser.uid, firestoreIds);
      }
    }

    // Also mark local ones as read
    const unreadLocalIds = mergedNotifications
      .filter(n => !n.isRead && (n.id.startsWith('system-') || n.id.startsWith('varsom-')))
      .map(n => n.id);

    if (unreadLocalIds.length > 0) {
      setReadLocalNotifIds(prev => {
        const next = Array.from(new Set([...prev, ...unreadLocalIds]));
        window.localStorage.setItem('fricamp_read_local_notifs', JSON.stringify(next));
        return next;
      });
    }
  };

  const mergedNotifications = useMemo(() => {
    const out = [...notifications];

    // Add winter season notification
    if (isWinterSeason) {
      out.unshift({
        id: 'system-winter',
        type: 'system_winter',
        title: t('sidebar.winter_season'),
        body: t('common.legal_disclaimer'), // We can use the legal disclaimer as body or custom text
        createdAt: Date.now(),
        isRead: readLocalNotifIds.includes('system-winter'),
      });
    }

    // Add Varsom warnings
    varsomWarnings.forEach((w, i) => {
      const id = `varsom-${w.type}-${i}`;
      out.unshift({
        id,
        type: 'varsom',
        title: w.mainText,
        body: t('varsom.warning_desc', { type: w.type, level: w.level }),
        createdAt: Date.now(),
        isRead: readLocalNotifIds.includes(id),
      });
    });

    return out.sort((a, b) => b.createdAt - a.createdAt);
  }, [notifications, isWinterSeason, varsomWarnings, t, readLocalNotifIds]);

  const mergedUnreadCount = useMemo(() => {
    return mergedNotifications.filter(n => !n.isRead).length;
  }, [mergedNotifications]);

  const handleReanalyzeRoute = async (route: GpxRoute) => {
    if (!currentUser) return;
    setAnalyzingRouteIds(prev => new Set(prev).add(route.id));

    // Listen to Firestore for progressive updates during analysis
    const unsubscribe = subscribeToRouteUpdates(route.id, (updated) => {
      setRoutes(prev => prev.map(r => r.id === route.id ? { ...r, stats: updated.stats, wikiSpots: updated.wikiSpots, analyzeProgress: updated.analyzeProgress } : r));
    });

    try {
      await analyzeRouteServer(route.id);
    } catch (e) {
      console.error("Re-analyze failed", e);
      alert("Kunne ikke analysere ruten på nytt.");
    } finally {
      unsubscribe();
      // Final reload to ensure clean state
      await loadRoutesList();
      setAnalyzingRouteIds(prev => {
        const next = new Set(prev);
        next.delete(route.id);
        return next;
      });
    }
  };

  const handleReanalyzeRouteLayer = async (route: GpxRoute, layer: string) => {
    if (!currentUser) return;
    setRouteFacilityLoading(prev => ({
      ...prev,
      routeId: route.id,
      [layer]: true
    }));

    const unsubscribe = subscribeToRouteUpdates(route.id, (updated) => {
      setRoutes(prev => prev.map(r => r.id === route.id
        ? { ...r, stats: updated.stats, wikiSpots: updated.wikiSpots }
        : r
      ));
    });

    try {
      await analyzeRouteServer(route.id, [layer]);
    } catch (e) {
      console.error("Re-analyze layer failed", e);
    } finally {
      unsubscribe();
      await loadRoutesList();
      setRouteFacilityLoading(prev => ({
        ...prev,
        [layer]: false,
        ...(Object.values({ ...prev, [layer]: false }).every(v => typeof v !== 'boolean' || !v) ? { routeId: null } : {})
      }));
    }
  };

  const handleMapMove = useCallback((coords: Coordinates, zoom: number, bounds: MapBounds, userDragged?: boolean) => {
    // Må settes FØR setCurrentViewBounds, siden useSpotManagers effekt leser refen
    // synkront når utsnittet endrer seg.
    userDraggedRef.current = userDragged === true;
    const shareViewLocked = !!parsedSharedMap && Date.now() < sharedViewLockUntilRef.current;
    if (shareViewLocked) {
      setCurrentViewBounds(bounds);
      return;
    }
    setMapCenter(coords);
    setMapZoom(zoom);
    checkNordic(coords);
    // Throttled save of last position to localStorage (every 3s)
    if (!lastPosSaveTimerRef.current) {
      lastPosSaveTimerRef.current = setTimeout(() => {
        lastPosSaveTimerRef.current = null;
        try { localStorage.setItem('fricamp_last_position', JSON.stringify({ latitude: coords.latitude, longitude: coords.longitude, zoom })); } catch {}
      }, 3000);
    }
    // Only update bounds state when they changed meaningfully.
    // Under auto-nav («kjøremodus») følger kartet GPS-prikken kontinuerlig, så
    // bounds endrer seg på hver fix. Vi hever da terskelen kraftig (~1,2 km) slik at
    // bounds-drevet nettverks-fetch (spots/rest/coastal/discover) ikke maser mens
    // kartet flyter — tiles påvirkes ikke av dette (de styres av Leaflet direkte).
    const prev = prevBoundsRef.current;
    const THRESHOLD = autoNavActiveRef.current ? 0.012 : 0.001; // ~1,2 km under auto-nav, ellers ~100 m
    if (!prev ||
      Math.abs(bounds.latMin - prev.latMin) > THRESHOLD ||
      Math.abs(bounds.latMax - prev.latMax) > THRESHOLD ||
      Math.abs(bounds.lonMin - prev.lonMin) > THRESHOLD ||
      Math.abs(bounds.lonMax - prev.lonMax) > THRESHOLD) {
      prevBoundsRef.current = bounds;
      setCurrentViewBounds(bounds);
    }
  }, [parsedSharedMap, checkNordic]);

  const handleMapClick = (coords: Coordinates | null) => {
    if (followActive && coords) {
      setPendingAnalysisCoords(null);
      return;
    }
    if (isPickingRouteStartOnMap && coords) {
      setRouteMapStart(coords);
      reverseGeocode(coords)
        .then((label) => setRouteMapStartLabel(label || `${coords?.latitude?.toFixed(5)}, ${coords?.longitude?.toFixed(5)}`))
        .catch(() => setRouteMapStartLabel(`${coords?.latitude?.toFixed(5)}, ${coords?.longitude?.toFixed(5)}`));
      setIsPickingRouteStartOnMap(false);
      if (routePendingSpotForMapPick) {
        setRoutePlanningSpot(routePendingSpotForMapPick);
        setRoutePendingSpotForMapPick(null);
      }
      setRouteCreationStatus({ type: 'success', text: 'Avreisepunkt valgt i kartet' });
      return;
    }
    if (coords) {
      const preserveSelection =
        isDesktop &&
        (Date.now() < preserveSelectionUntilRef.current || (!!selectedSpotId && !!activeHikingTrailId));
      if (preserveSelection) {
        // Ignore transient map clicks during programmatic fit/zoom on desktop.
        return;
      }
      setPendingAnalysisCoords(coords);
      setSelectedSpotId(null);
      setShowMobileSheet(false);
      setHighlightedRoad(null);
      setCoordinatePin(null);
      setBarrierHighlightSpot(null);
      setDiscoverHighlight(null);
      setDiscoverSelectedId(null);
      if (selectedRouteId) setSelectedRouteId(null);
      // If user has pre-selected photo files (from "create from photo" without EXIF GPS),
      // open AddSpotModal immediately at the tapped location
      if (preSelectedFiles.length > 0) {
        if (!currentUser) { setIsAuthOpen(true); return; }
        setEditingSpot(undefined);
        setShowAddModal(true);
      }
    } else {
      setPendingAnalysisCoords(null);
      setHighlightedRoad(null);
      const preserveSelection =
        isDesktop ||
        Date.now() < preserveSelectionUntilRef.current ||
        (!!selectedSpotId && !!activeHikingTrailId);
      if (!preserveSelection) {
        setSelectedSpotId(null);
        setSelectedRouteId(null);
        setActiveRouteIds(new Set());
        setShowMobileSheet(false);
      }
    }
  };

  const clearRoutePlanDraftState = () => {
    setShowRoutePlanContent(false);
    setRoutePlanWildlifeCount(0);
    setRoutePreviewWebcams([]);
    setRouteFacilityLoading({
      routeId: null,
      fricamp: false,
      restAreas: false,
      campsites: false,
      attractions: false,
      chargers: false,
      wildlife: false
    });
  };

  const handleRouteClick = (routeId: string) => {
    if (!isPaidUser) {
      nudgeRef.current?.showNudge('routes_gpx');
      return;
    }
    clearRoutePlanDraftState();
    setRouteReturnContext(null);
    setSelectedRouteId(routeId);
    setActiveRouteIds(new Set([routeId]));
    setSidebarMode('routes');
    expandSidebar();
    const route = routes.find(r => r.id === routeId);
    if (route && (!route.stats || route.stats.totalSpots === 0)) {
      handleReanalyzeRoute(route);
    }
    // Cached spots are loaded automatically via fetchSpotsForBounds when bounds change.
    // Don't trigger a full Overpass scanArea here — large routes cause massive queries that hang.
  };

  const handleOpenMyContentTrackAsRoute = async (
    track: TrackContent,
    options: { closeDesktopSidebar?: boolean; switchMobileToMap?: boolean } = {}
  ) => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    if (!isPaidUser) {
      nudgeRef.current?.showNudge('routes_gpx');
      return;
    }

	    const realId = track.id.replace(/^legacy_track_/, '');
	    const isLegacyUserTrack = track.id.startsWith('legacy_track_');
	    let routeId = track.linkedRouteId || routes.find((r) => (r as any).sourceTrackId === realId)?.id;

	    if (!routeId) {
	      const refreshedRoutes = await loadRoutesList();
	      routeId = refreshedRoutes.find((r) => (r as any).sourceTrackId === realId)?.id;
	    }

	    if (!routeId) {
	      try {
	        const trackDoc = await getDoc(doc(db, isLegacyUserTrack ? 'user_tracks' : 'user_content', realId));
	        const linkedRouteId = trackDoc.exists() ? trackDoc.data()?.linkedRouteId : null;
	        if (linkedRouteId) routeId = String(linkedRouteId);
	      } catch (error) {
	        console.warn('[handleOpenMyContentTrackAsRoute] linkedRoute lookup failed', error);
	      }
    }

    if (!routeId && track.gpxUrl) {
      addToast(t('toast.converting_track'), 'info');
      const minTrack: SavedTrack = {
        id: realId,
        userId: currentUser.uid,
        name: track.name,
        createdAt: track.createdAt,
        distance: track.distance,
        duration: track.duration,
        elevationGain: track.elevationGain,
        elevationLoss: track.elevationLoss,
        pointCount: track.pointCount,
        gpxUrl: track.gpxUrl,
        startLocation: track.startLocation || { latitude: mapCenter.latitude, longitude: mapCenter.longitude },
        bounds: track.bounds || {
          latMin: track.startLocation?.latitude ?? mapCenter.latitude,
          latMax: track.startLocation?.latitude ?? mapCenter.latitude,
          lonMin: track.startLocation?.longitude ?? mapCenter.longitude,
          lonMax: track.startLocation?.longitude ?? mapCenter.longitude
        },
      };

      try {
        routeId = await convertTrackToRoute(minTrack, currentUser) ?? undefined;
      } catch (error) {
        console.warn('[handleOpenMyContentTrackAsRoute] convert failed', error);
        addToast('Kunne ikke konvertere turen til privat rute nå.', 'error');
        return;
      }
    }

    if (!routeId) {
      addToast('Fant ingen rute for denne turen.', 'warning');
      if (track.bounds) {
        setMapCommand({
          center: track.startLocation,
          zoom: 14,
          bounds: [[track.bounds.latMin, track.bounds.lonMin], [track.bounds.latMax, track.bounds.lonMax]],
          id: Date.now()
        });
        if (options.switchMobileToMap) setMobileView('map');
      }
      return;
    }

    const refreshedRoutes = await loadRoutesList();
    let routeExists = refreshedRoutes.some((route) => route.id === routeId);
    if (!routeExists) {
      try {
        const routeSnap = await getDoc(doc(db, 'routes', routeId));
        if (routeSnap.exists()) {
          const routeData = { id: routeSnap.id, ...routeSnap.data() } as GpxRoute;
          setRoutes((prev) => prev.some((route) => route.id === routeData.id) ? prev : [routeData, ...prev]);
          routeExists = true;
        }
      } catch (error) {
        console.warn('[handleOpenMyContentTrackAsRoute] direct route fetch failed', error);
      }
    }

	    if (!routeExists) {
	      addToast('Ruten ble opprettet, men kunne ikke åpnes ennå. Prøv igjen om noen sekunder.', 'warning');
	      return;
	    }

	    if (!track.linkedRouteId && routeId) {
	      try {
	        await updateDoc(
	          doc(db, isLegacyUserTrack ? 'user_tracks' : 'user_content', realId),
	          {
	            linkedRouteId: routeId,
	            ...(isLegacyUserTrack ? {} : { updatedAt: Date.now() }),
	          }
	        );
	      } catch (error) {
	        console.warn('[handleOpenMyContentTrackAsRoute] Could not persist linkedRouteId on track', error);
	      }
	    }

    const trackCenter = track.bounds
      ? {
          latitude: (track.bounds.latMin + track.bounds.latMax) / 2,
          longitude: (track.bounds.lonMin + track.bounds.lonMax) / 2,
        }
      : track.startLocation || mapCenter;
    const fallbackRouteName = track.name?.trim() || 'Min tur';
    const fallbackCreatorName = currentUser.displayName?.trim() || currentUser.email || 'Min profil';
    const hasAnonymousCreator = (name?: string | null) => {
      const normalized = String(name || '').trim().toLowerCase();
      return !normalized || normalized === 'anonym' || normalized === 'anonymous';
    };

    setRoutes((prev) =>
      prev.map((route) => {
        if (route.id !== routeId) return route;
        const nextStats = route.stats || {
          wildSpotsCount: 0,
          campsitesCount: 0,
          restAreasCount: 0,
          attractionsCount: Array.isArray(route.wikiSpots) ? route.wikiSpots.length : 0,
          chargersCount: 0,
          wildlifeCount: 0,
          totalSpots: Array.isArray(route.wikiSpots) ? route.wikiSpots.length : 0,
        };

        return {
          ...route,
          name: route.name?.trim() || fallbackRouteName,
          description: route.description || `Privat rute fra turen "${fallbackRouteName}".`,
          gpxUrl: route.gpxUrl || track.gpxUrl,
          createdBy: route.createdBy || currentUser.uid,
          createdByName:
            route.createdBy === currentUser.uid && hasAnonymousCreator(route.createdByName)
              ? fallbackCreatorName
              : (route.createdByName?.trim() || fallbackCreatorName),
          createdByPhoto: route.createdByPhoto || currentUser.photoURL || undefined,
          center: route.center || trackCenter,
          startCoords: route.startCoords || track.startLocation || trackCenter,
          endCoords: route.endCoords || trackCenter,
          distance: route.distance || track.distance || 0,
          estimatedTime: route.estimatedTime || (
            track.duration
              ? `${Math.floor(track.duration / 60)}t ${Math.max(0, Math.round(track.duration % 60))}m`
              : undefined
          ),
          elevationProfile: route.elevationProfile || [],
          associatedSpotIds: Array.isArray(route.associatedSpotIds) ? route.associatedSpotIds : [],
          wikiSpots: Array.isArray(route.wikiSpots) ? route.wikiSpots : [],
          stats: nextStats,
          sourceTrackId: (route as any).sourceTrackId || realId,
        } as GpxRoute;
      })
    );

    clearRoutePlanDraftState();
    setRouteReturnContext(null);
    setSelectedRouteId(routeId);
    setActiveRouteIds(new Set([routeId]));
    setSidebarMode('routes');
    expandSidebar();
    const bounds = track.bounds;
    if (bounds) {
      setMapCommand({
        center: {
          latitude: (bounds.latMin + bounds.latMax) / 2,
          longitude: (bounds.lonMin + bounds.lonMax) / 2,
        },
        zoom: 13,
        bounds: [[bounds.latMin, bounds.lonMin], [bounds.latMax, bounds.lonMax]],
        id: Date.now(),
      });
    }
    if (options.switchMobileToMap) setMobileView('list');
    if (options.closeDesktopSidebar) setShowMyContent(false);
  };

  const handleShowRoad = async () => {
    if (!pendingAnalysisCoords || !currentViewBounds) return;
    const fallbackName = t('geocoding.selected_point');
    setIsLoadingRoad(true);
    try {
      const [roadData, geoName] = await Promise.all([
        fetchRoadFromClick(pendingAnalysisCoords, currentViewBounds),
        reverseGeocode(pendingAnalysisCoords).catch(() => ''),
      ]);
      const roadName = roadData?.features?.[0]?.properties?.name || roadData?.features?.[0]?.properties?.ref || '';
      const usableGeoName = geoName && !isCoordinateLabel(geoName) ? geoName : '';
      setNavChoiceTarget({
        coords: pendingAnalysisCoords,
        name: usableGeoName || roadName || fallbackName,
      });
      if (roadData) {
        setHighlightedRoad(roadData);
      } else {
        console.debug("Ingen vei funnet her.");
      }
    } catch (e) {
      console.error("Feil ved henting av vei", e);
    } finally {
      setIsLoadingRoad(false);
    }
  };

  const handlePlanRouteToMapClick = () => {
    if (!navChoiceTarget) return;
    setRoutePlan({
      toCoords: navChoiceTarget.coords,
      toName: navChoiceTarget.name,
      waypoints: []
    });
    setRoutePlanFromName('');
    setRoutePlanError(null);
    setNavChoiceTarget(null);
    setHighlightedRoad(null);
    setPendingAnalysisCoords(null);
    setSelectedRouteId(null);
    setPreviewRegion(null);
    setActiveRouteIds(new Set());
    setMobileView('map');
  };

  const handleNativeNavToMapClick = () => {
    if (!navChoiceTarget) return;
    openExternalNavigation(navChoiceTarget.coords.latitude, navChoiceTarget.coords.longitude);
    setNavChoiceTarget(null);
  };

  const handleCopyCoordsClick = async () => {
    if (!navChoiceTarget) return;
    const { latitude, longitude } = navChoiceTarget.coords;
    const text = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    try {
      await navigator.clipboard.writeText(text);
      addToast(t('navigation.coords_copied', 'Koordinater kopiert: {{coords}}', { coords: text }), 'success');
    } catch {
      addToast(t('navigation.coords_copy_failed', 'Kunne ikke kopiere koordinater'), 'error');
    }
    setNavChoiceTarget(null);
  };

  const handleAddToRouteBuilder = () => {
    if (highlightedRoad) {
      setRouteBuilderSegments(prev => [...prev, highlightedRoad]);
      setHighlightedRoad(null);
      setPendingAnalysisCoords(null);
    }
  };

  const handleCreateFromBuilder = () => {
    if (routeBuilderSegments.length === 0) return;
    const gpxString = geoJsonToGpx(routeBuilderSegments);
    const file = new File([gpxString], "custom_route.gpx", { type: "application/gpx+xml" });
    setPreloadedRouteFile(file);
    setShowRouteUpload(true);
    setRouteBuilderSegments([]);
  };

  const handleSaveRoutePlanAlternative = useCallback((alternative: RoutePlanAlternative, name: string) => {
    const coords = alternative.fullPolyline?.length
      ? alternative.fullPolyline
      : alternative.coordinates.map(([longitude, latitude]) => ({ latitude, longitude }));
    if (coords.length < 2) {
      addToast('Fant ikke nok rutepunkter til å lagre ruten.', 'error');
      return;
    }

    const gpxName = (name || routePlan?.toName || 'planlagt_rute').trim() || 'planlagt_rute';
    const gpxFeature = {
      type: 'Feature',
      properties: { name: gpxName },
      geometry: {
        type: 'LineString',
        coordinates: coords.map((c) => [c.longitude, c.latitude]),
      },
    };
    const gpxString = geoJsonToGpx([gpxFeature], gpxName);
    const fileName = `${gpxName.replace(/[^a-z0-9æøåÆØÅ_-]+/gi, '_')}.gpx`;

    setEditingRoute(null);
    setPreloadedRouteFile(new File([gpxString], fileName, { type: 'application/gpx+xml' }));
    setPreloadedRouteMetadata({
      name: gpxName,
      description: `Planlagt rute fra ${routePlanFromName || routePlan?.fromName || 'Min posisjon'} til ${routePlan?.toName || gpxName}.`,
    });
    setShowRouteUpload(true);
  }, [addToast, routePlan?.fromName, routePlan?.toName, routePlanFromName]);

  const handleNavigateRoutePlanAlternative = useCallback((alternative: RoutePlanAlternative) => {
    const routeCoords = alternative.fullPolyline?.length
      ? alternative.fullPolyline
      : alternative.coordinates.map(([longitude, latitude]) => ({ latitude, longitude }));
    if (routeCoords.length < 2 || !routePlan) {
      addToast('Fant ikke nok rutepunkter til å starte navigering.', 'error');
      return;
    }

    const totalM = alternative.distanceMeters;
    const routeName = routePlan.toName || alternative.label || 'Planlagt rute';
    const routeBounds = getBoundsForCoords(routeCoords);
    const routePois: RouteFollowPoi[] = [];

    spots.forEach((spot) => {
      if (!spot.location || spot.isDeleted || spot.isInvalid) return;
      if (!isPointNearPolyline(spot.location, routeCoords, 1500)) return;
      routePois.push({
        id: `plan-spot-${spot.id}`,
        name: spot.name || 'Sted langs ruten',
        category: spot.spotType === 'rasteplass'
          ? 'rest'
          : spot.spotType === 'commercial_campsite'
            ? 'campsite'
            : 'wildspot',
        location: spot.location,
        distanceAlongRoute: findDistanceAlong(spot.location, routeCoords),
      });
    });

    routePlan.waypoints.forEach((waypoint, index) => {
      routePois.push({
        id: `plan-stop-${index}`,
        name: waypoint.name || `Stopp ${index + 1}`,
        category: 'waypoint',
        location: waypoint.coords,
        icon: 'pin',
        distanceAlongRoute: findDistanceAlong(waypoint.coords, routeCoords),
      });
    });

    routePois.push({
      id: `plan-destination-${Date.now()}`,
      name: routePlan.toName || 'Destinasjon',
      category: 'wildspot',
      location: routeCoords[routeCoords.length - 1],
      distanceAlongRoute: totalM,
    });
    routePois.sort((a, b) => a.distanceAlongRoute - b.distanceAlongRoute);

    openFollowPreflight({
      mode: 'route_follow',
      title: routeName,
      bounds: routeBounds,
      continueAction: () => {
        setFollowRouteState({
          routeId: `route-plan-${Date.now()}`,
          routeName,
          routeCoords,
          routeTotalDistanceM: totalM,
          routeTotalDurationS: alternative.durationSeconds || 0,
          pois: routePois,
        });
        setRoutePlan(null);
        setRoutePlanAlternatives([]);
        setRoutePlanSelectedIndex(null);
        setRoutePlanError(null);
        setShowRoutePlanContent(false);
        setSidebarExpanded(false);
        setMobileView('map');
        setShowMobileSheet(false);
        startTracking();
        logFeatureUsage('route_plan_navigate');
      },
    });
  }, [addToast, findDistanceAlong, getBoundsForCoords, openFollowPreflight, routePlan, spots, startTracking]);

  const getSuggestedRouteStart = (): Promise<Coordinates> => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(mapCenter);
        return;
      }
      gatedGetCurrentPosition(
        (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
        () => resolve(mapCenter),
        { enableHighAccuracy: true, timeout: 7000, maximumAge: 180000 }
      );
    });
  };

  const resolveRouteStart = async (): Promise<Coordinates> => {
    if (routeStartMode === 'address') {
      if (!routeAddressStart) throw new Error('missing_address_start');
      return routeAddressStart;
    }
    if (routeStartMode === 'map_pick') {
      if (!routeMapStart) throw new Error('missing_map_start');
      return routeMapStart;
    }
    return getSuggestedRouteStart();
  };

  const createRouteFromSpotNow = async (spot: CampingSpot) => {
    setIsCreatingSpotRoute(true);
    setCreatingRouteSpotId(spot.id);
    setRouteCreationStatus({ type: 'loading', text: 'Beregner veirute...' });

    try {
      const start = await resolveRouteStart();
      const destination = spot.location;

      const routed = await fetchDrivingRoute(start, destination);
      logFeatureUsage('route_plan');
      const draftFeature = {
        type: 'Feature',
        properties: {
          source: 'spot-quick-route',
          routeSource: routed.source,
          spotId: spot.id,
          spotName: spot.name || 'Lokasjon',
          from: start,
          to: destination,
          distanceMeters: routed.distanceMeters,
          durationSeconds: routed.durationSeconds
        },
        geometry: {
          type: 'LineString',
          coordinates: routed.coordinates
        }
      };

      setRouteBuilderSegments([draftFeature as any]);
      setPreloadedRouteMetadata({
        distanceKm: Number((routed.distanceMeters / 1000).toFixed(1)),
        estimatedTime: formatDuration(routed.durationSeconds)
      });
      setRouteCreationStatus({
        type: 'success',
        text: `Rute klar: ${(routed.distanceMeters / 1000).toFixed(1)} km · ${formatDuration(routed.durationSeconds)}`
      });
      setRoutePlanningSpot(null);
      setRoutePendingSpotForMapPick(null);
      setHighlightedRoad(null);
      setPendingAnalysisCoords(null);
      setSelectedRouteId(null);
      setPreviewRegion(null);
      setActiveRouteIds(new Set());
      setSidebarMode('routes');
      setMobileView('map');
      setMapCommand({
        center: {
          latitude: (start.latitude + destination.latitude) / 2,
          longitude: (start.longitude + destination.longitude) / 2
        },
        zoom: 11,
        id: Date.now()
      });
    } catch (err) {
      console.warn('Route routing failed, using direct line fallback', err);
      const errMsg = String((err as Error)?.message || '');
      if (errMsg.includes('missing_address_start')) {
        setRouteCreationStatus({ type: 'error', text: 'Velg en adresse for avreise først.' });
        return;
      }
      if (errMsg.includes('missing_map_start')) {
        setRouteCreationStatus({ type: 'error', text: 'Velg et avreisepunkt i kartet først.' });
        return;
      }
      try {
        const start = await resolveRouteStart();
        const destination = spot.location;
        const fallbackFeature = {
          type: 'Feature',
          properties: { source: 'spot-quick-route-fallback', spotId: spot.id, spotName: spot.name || 'Lokasjon' },
          geometry: {
            type: 'LineString',
            coordinates: [
              [start.longitude, start.latitude],
              [destination.longitude, destination.latitude]
            ]
          }
        };
        setRouteBuilderSegments([fallbackFeature as any]);
        setRoutePlanningSpot(null);
        setRoutePendingSpotForMapPick(null);
        setHighlightedRoad(null);
        setPendingAnalysisCoords(null);
        setSelectedRouteId(null);
        setPreviewRegion(null);
        setActiveRouteIds(new Set());
        setSidebarMode('routes');
        setMobileView('map');
        setRouteCreationStatus({
          type: 'error',
          text: 'Fant ikke veirute nå. Viser foreløpig direkte linje.'
        });
      } catch {
        setRouteCreationStatus({ type: 'error', text: 'Kunne ikke beregne rute. Prøv igjen.' });
      }
    } finally {
      setIsCreatingSpotRoute(false);
      setCreatingRouteSpotId(null);
    }
  };

  const handleCreateRouteFromSpot = async (spot: CampingSpot) => {
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    if (!isPaidUser) {
      nudgeRef.current?.showNudge('routes_gpx');
      return;
    }
    if (!spot?.location) return;

    setRouteStartMode('current');
    setRouteAddressQuery('');
    setRouteAddressResults([]);
    setRouteAddressStart(null);
    setRouteAddressLabel('');
    setRouteAddressLockedQuery('');
    setRouteAddressConfirmed(false);
    setRouteMapStart(null);
    setRouteMapStartLabel('');
    setIsPickingRouteStartOnMap(false);
    setRoutePendingSpotForMapPick(null);
    setRoutePlanningSpot(spot);
  };

  const handleClearBuilder = () => {
    setRouteBuilderSegments([]);
    setPreloadedRouteMetadata(undefined);
  };

  const handleNavigateFromBuilder = useCallback(() => {
    if (routeBuilderSegments.length === 0) return;
    const seg = routeBuilderSegments[0];
    const coords: Coordinates[] = (seg.geometry?.coordinates || []).map(([lon, lat]: [number, number]) => ({
      latitude: lat,
      longitude: lon,
    }));
    if (coords.length < 2) return;

    // Calculate total distance
    let totalM = 0;
    for (let i = 1; i < coords.length; i++) {
      totalM += getDistance(coords[i - 1].latitude, coords[i - 1].longitude, coords[i].latitude, coords[i].longitude);
    }

    // Collect nearby POIs
    const destCoord = coords[coords.length - 1];
    const pois: RouteFollowPoi[] = [];
    const nearbySpots = spots.filter(s => {
      if (!s.location) return false;
      if (s.id === seg.properties?.spotId) return false;
      return isPointNearPolyline(s.location, coords, 1500);
    });
    for (const s of nearbySpots) {
      const cat = s.spotType === 'rasteplass' ? 'rest'
        : s.spotType === 'commercial_campsite' ? 'campsite'
        : 'wildspot';
      pois.push({
        id: `spot-${s.id}`,
        name: s.name || cat,
        category: cat as RouteFollowPoi['category'],
        location: s.location,
        distanceAlongRoute: findDistanceAlong(s.location, coords),
      });
    }
    // Destination POI
    const destName = seg.properties?.spotName || 'Destinasjon';
    pois.push({
      id: `dest-${Date.now()}`,
      name: destName,
      category: 'wildspot',
      location: destCoord,
      distanceAlongRoute: totalM,
    });
    pois.sort((a, b) => a.distanceAlongRoute - b.distanceAlongRoute);

    const routeBounds = getBoundsForCoords(coords);
    const routeId = `nav-builder-${Date.now()}`;

    openFollowPreflight({
      mode: 'route_follow',
      title: destName,
      bounds: routeBounds,
      continueAction: () => {
        setFollowRouteState({
          routeId,
          routeName: destName,
          routeCoords: coords,
          routeTotalDistanceM: totalM,
          routeTotalDurationS: seg.properties?.durationSeconds || 0,
          pois,
        });
        setRouteBuilderSegments([]);
        setPreloadedRouteMetadata(undefined);
        setMobileView('map');
        startTracking();
        logFeatureUsage('navigate_from_builder');
      },
    });
  }, [routeBuilderSegments, spots, startTracking, getBoundsForCoords, openFollowPreflight, findDistanceAlong]);

  const handleEditSpot = (s: CampingSpot) => {
    if (!currentUser) { setIsAuthOpen(true); return; }
    setEditingSpot(s);
    setPendingAnalysisCoords(s.location);
    setShowAddModal(true);
  };

  /** Create spot from photo: extract EXIF → check for nearby existing spots → open modal */
  const handleCreateFromPhoto = async (files: File[], nativeCoords?: Coordinates | [number, number] | null) => {
    if (files.length === 0) return;
    if (!currentUser) { setIsAuthOpen(true); return; }

    // 1. Try native coords first (from Android ExifImagePicker), then JS ExifReader fallback.
    // Guard with Number.isFinite — corrupt EXIF can return NaN, and `!= null` lets it through.
    let photoCoords: Coordinates | null = null;
    if (Array.isArray(nativeCoords)) {
      if (Number.isFinite(nativeCoords[0]) && Number.isFinite(nativeCoords[1])) {
        photoCoords = { latitude: nativeCoords[0], longitude: nativeCoords[1] };
      }
    } else if (nativeCoords && Number.isFinite(nativeCoords.latitude) && Number.isFinite(nativeCoords.longitude)) {
      photoCoords = nativeCoords;
    }
    if (!photoCoords) {
      for (const file of files) {
        try {
          const tags = await ExifReader.load(file, { expanded: true });
          const lat = tags.gps?.Latitude;
          const lon = tags.gps?.Longitude;
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            photoCoords = { latitude: lat as number, longitude: lon as number };
            console.log('[EXIF] GPS found in:', file.name, (lat as number).toFixed(6), (lon as number).toFixed(6));
            break;
          }
        } catch (exifErr) {
          console.warn('[EXIF] Failed to parse:', file.name, exifErr);
        }
      }
    }

    // 2. Compress all images before opening modal to save memory
    const { files: compressedFiles, skipped } = await compressImagesDetailed(files);
    if (skipped.length > 0) {
      // Kamera-RAW (.dng o.l.) kan ikke tegnes av nettleseren — avvis før opplasting.
      addToast(t('toast.photo_unsupported_format', 'Kan ikke bruke {{files}} — formatet vises ikke i appen. Velg JPG eller PNG.', { files: skipped.join(', ') }), 'error');
      if (compressedFiles.length === 0) return;
    }

    // 3. No EXIF GPS — save files and ask user to tap the map
    if (!photoCoords) {
      setPreSelectedFiles(compressedFiles);
      setEditingSpot(undefined);
      addToast(t('toast.no_exif_coords', 'Fant ikke koordinater i bildene. Trykk i kartet for å velge posisjon.'), 'warning');
      return;
    }

    // 3. Deduplication: check if a spot already exists within ~100m
    const DEDUP_RADIUS_M = 100;
    let nearestSpot: CampingSpot | null = null;
    let nearestDist = Infinity;
    for (const spot of spots) {
      if (!spot.location) continue;
      const dLat = (spot.location.latitude - photoCoords.latitude) * 111320;
      const dLon = (spot.location.longitude - photoCoords.longitude) * 111320 * Math.cos(photoCoords.latitude * Math.PI / 180);
      const dist = Math.sqrt(dLat * dLat + dLon * dLon);
      if (dist < DEDUP_RADIUS_M && dist < nearestDist) {
        nearestDist = dist;
        nearestSpot = spot;
      }
    }

    // 4. Open modal — as edit/verify if nearby spot found, else as new
    setPreSelectedFiles(compressedFiles);
    if (nearestSpot) {
      setEditingSpot(nearestSpot);
      setPendingAnalysisCoords(nearestSpot.location);
      addToast(t('toast.existing_spot_found', `Fant eksisterende plass "${nearestSpot.name}" — åpner for verifisering`), 'info');
    } else {
      setEditingSpot(undefined);
      setPendingAnalysisCoords(photoCoords);
    }
    setShowAddModal(true);
  };

  const handleToggleFavoriteSpot = async (e: React.MouseEvent, spotId: string) => {
    e.stopPropagation();
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    const isRemoving = favoriteSpotIds.includes(spotId);
    try {
      await toggleFavoriteSpot(spotId, currentUser.uid, isRemoving);
      if (!isRemoving) logFeatureUsage('favorite_add');
      addToast(isRemoving ? t('toast.favorite_removed') : t('toast.favorite_added'));
    } catch (err) {
      console.error('Kunne ikke oppdatere favoritt', err);
      addToast(t('toast.error_generic'), 'error');
    }
  };

  const shouldPromptVerificationAfterVisit = useCallback((spot: CampingSpot) => {
    if (spot.isVerified) return false;
    if (['commercial_campsite', 'rasteplass', 'osm_amenity', 'coastal', 'forbidden_area'].includes(spot.spotType || '')) return false;
    return true;
  }, []);

  // Anmeldelse-prompt etter innsjekk på plasser som IKKE trenger verifisering
  // (typisk allerede verifiserte). Maks én gang per plass per enhet, og aldri
  // hvis brukeren allerede har anmeldt plassen. Dette er hovedkilden til nytt
  // innhold i news-feedens anmeldelse-fane.
  const maybePromptReviewAfterVisit = useCallback(async (spot: CampingSpot) => {
    if (!currentUser?.uid) return;
    // Kommersielle campingplasser har egen ratingmodal; rasteplasser og
    // forbudsområder skal ikke anmeldes.
    if (['commercial_campsite', 'rasteplass', 'forbidden_area'].includes(spot.spotType || '')) return;
    const promptedKey = 'fricamp_review_prompted';
    let prompted: string[] = [];
    try { prompted = JSON.parse(localStorage.getItem(promptedKey) || '[]'); } catch {}
    if (prompted.includes(spot.id)) return;
    if (await hasUserReviewedSpot(spot.id, currentUser.uid)) return;
    try { localStorage.setItem(promptedKey, JSON.stringify([...prompted, spot.id].slice(-100))); } catch {}
    setReviewPromptSpot(spot);
  }, [currentUser]);

  const autoPlaceSticker = useCallback(async (spotId: string, spot?: CampingSpot) => {
    if (!currentUser?.uid) return;
    const cleanSpotId = unwrapSyntheticSpotId(spotId);

    // Resolve spot using passed parameter or by searching current spots list
    const resolvedSpot = spot || spots.find(s => s.id === cleanSpotId || s.id === spotId);
    if (!resolvedSpot) {
      // If the spot is not found in memory, don't auto-place to be safe
      return;
    }

    // Only place stickers for fricamp or commercial campsites
    if (!['confirmed', 'wildspot', 'algorithmic', 'commercial_campsite'].includes(resolvedSpot.spotType)) {
      return;
    }

    const currentLayout = currentUser.stickerLayout || {};
    if (!currentLayout[cleanSpotId]) {
      const nextLayout = {
        ...currentLayout,
        [cleanSpotId]: {
          x: Number((15 + Math.random() * 70).toFixed(1)),
          y: Number((15 + Math.random() * 70).toFixed(1)),
          rotation: Number(((Math.random() * 30) - 15).toFixed(1)),
          scale: 1.1,
        }
      };
      try {
        await updateUserSettings(currentUser.uid, { stickerLayout: nextLayout });
      } catch (err) {
        console.warn('Failed to auto-place sticker:', err);
      }
    }
  }, [currentUser, spots]);

  const autoRemoveSticker = useCallback(async (spotId: string) => {
    if (!currentUser?.uid) return;
    const cleanSpotId = unwrapSyntheticSpotId(spotId);
    const currentLayout = currentUser.stickerLayout || {};
    if (currentLayout[cleanSpotId]) {
      const nextLayout = { ...currentLayout };
      delete nextLayout[cleanSpotId];
      try {
        await updateUserSettings(currentUser.uid, { stickerLayout: nextLayout });
      } catch (err) {
        console.warn('Failed to auto-remove sticker:', err);
      }
    }
  }, [currentUser]);


  const handleToggleVisit = async (e: React.MouseEvent, spotId: string) => {
    e.stopPropagation();
    if (!currentUser) {
      setIsAuthOpen(true);
      return;
    }
    const spot = spots.find(s => s.id === spotId);
    if (!spot) return;
    // Auto-persist transient external spots on first interaction
    if (spot.externalId) handleEnsureSpotPersisted(spotId);
    const isRemoving = visitedSpotIds?.includes(spotId) ?? false;
    // Resolve county if missing on spot
    let county = spot.county;
    if (!county && !isRemoving) {
      try {
        const region = await reverseGeocodeRegion(spot.location);
        county = region?.county;
      } catch {}
    }
    try {
      const result = await toggleVisitSpot(
        spotId, spot.name,
        spot.location.latitude, spot.location.longitude,
        currentUser.uid, visits,
        county,
        spot.photoUrls?.[0],
        currentUser.activeVehicleCategory,
        currentUser.activeVehicleLength,
        currentUser.verifiedSpotCount || 0,
      );

      if (isRemoving) {
        await autoRemoveSticker(spotId);
      } else {
        await autoPlaceSticker(spotId, spot);
      }

	      if (result.levelUp && result.newTier) {
	        setLevelUpTier(result.newTier);
	      }

	      if (isRemoving) {
	        addToast(t('toast.visit_removed'));
	      } else {
	        showContributionFeedback(
	          'checkin',
	          spot,
	          `Bra innsjekk. Du hjelper andre å se at ${spot.name || 'plassen'} faktisk brukes, og bygger nivået ditt videre.`,
	          20,
	          result
	        );
	      }

	      // Prompt verification for unverified spots on new visit
	      if (!isRemoving && shouldPromptVerificationAfterVisit(spot)) {
	        setVerifyPromptSpot(spot);
	      } else if (!isRemoving) {
	        void maybePromptReviewAfterVisit(spot);
	      }
    } catch (err) {
      console.error('Kunne ikke oppdatere besøk', err);
      addToast(t('toast.error_generic'), 'error');
    }
  };

  // Fase 2: etterregistrer ETT atskilt opphold (fra–til) brukeren glemte å sjekke inn på.
  // Hvert opphold er sin egen checkin_log-rad; settet re-utledes som konvolutt.
  const handleAddPastStay = async (spotId: string, checkInAt: number, checkedOutAt?: number) => {
    if (!currentUser) { setIsAuthOpen(true); return; }
    const spot = spots.find(s => s.id === spotId);
    if (!spot) return;
    if (spot.externalId) handleEnsureSpotPersisted(spotId);
    let county = spot.county;
    if (!county) {
      try {
        const region = await reverseGeocodeRegion(spot.location);
        county = region?.county;
      } catch {}
    }
    try {
      const result = await addPastStay(
        spotId, spot.name,
        spot.location.latitude, spot.location.longitude,
        currentUser.uid,
        checkInAt, checkedOutAt,
        county,
        spot.photoUrls?.[0],
        currentUser.activeVehicleCategory,
        currentUser.activeVehicleLength,
        currentUser.verifiedSpotCount || 0,
        currentUser.activeVehiclePhotoUrl,
        currentUser.shareVehicleOnSpots !== false,
      );
      await autoPlaceSticker(spotId, spot);
      if (result.levelUp && result.newTier) {
        setLevelUpTier(result.newTier);
      }
      addToast(t('toast.past_visit_added', 'Tidligere besøk registrert'));
    } catch (err) {
      console.error('Kunne ikke registrere opphold', err);
      addToast(t('toast.error_generic'), 'error');
    }
  };

  // Fase 2: slett ett opphold. Forsvinner siste opphold, fjernes plassen fra settet.
  const handleDeletePastStay = async (spotId: string, checkInAt: number) => {
    if (!currentUser) return;
    try {
      await deletePastStay(currentUser.uid, spotId, checkInAt, currentUser.verifiedSpotCount || 0);
      // StickerBoard rendrer kun stickers for plasser i settet, så en plass som forsvant
      // slutter å vises automatisk — ingen egen sticker-opprydding nødvendig.
      addToast(t('toast.past_visit_deleted', 'Opphold slettet'));
    } catch (err) {
      console.error('Kunne ikke slette opphold', err);
      addToast(t('toast.error_generic'), 'error');
    }
  };

  const handleMarkOccupied = async (spotId: string, days: number, tags: ConditionTag[]) => {
    if (!currentUser) { setIsAuthOpen(true); return; }
    const spot = spots.find(s => s.id === spotId);
    if (!spot) return;
    if (spot.externalId) handleEnsureSpotPersisted(spotId);
    try {
      // Auto-utsjekk: hvis brukeren allerede er sjekket inn på en annen plass, rydd
      // opp der før vi sjekker inn her (man kan bare stå ett sted om gangen).
      const autoCheckedOut = await autoCheckOutPrevious(currentUser.currentCheckIn, spotId);
      if (autoCheckedOut) {
        await recordCheckOut(currentUser.uid, autoCheckedOut.spotId, visits).catch(() => {});
        const prevSpot = spots.find(s => s.id === autoCheckedOut.spotId);
        if (prevSpot) updateSpotLocally({ ...prevSpot, occupiedBy: undefined, occupiedByName: undefined, occupiedUntil: undefined });
        addToast(t('toast.auto_checked_out', { name: autoCheckedOut.spotName, defaultValue: `Sjekket ut fra ${autoCheckedOut.spotName}` }));
      }
      const isRasteplass = spot.spotType === 'rasteplass';
      const rasteplassUntil = isRasteplass ? calculateRasteplassOccupancyUntil() : undefined;
      const wasVerified = !!spot.isVerified;
      const checkedInAt = Date.now();
      const occupiedUntil = await markSpotOccupied(spotId, currentUser.uid, currentUser.displayName || 'Ukjent', days, { explicitOccupiedUntil: rasteplassUntil, alreadyVerified: wasVerified, userPhoto: currentUser.photoURL || null });
      await setCurrentCheckIn(currentUser.uid, { spotId, spotName: spot.name, occupiedUntil, checkedInAt }).catch(() => {});
      await writeSpotVehicleVisit({
        userId: currentUser.uid,
        spotId,
        checkInAt: checkedInAt,
        vehicleCategory: currentUser.activeVehicleCategory,
        vehicleLengthCm: currentUser.activeVehicleLength,
        photoUrl: currentUser.activeVehiclePhotoUrl,
        nights: days,
        source: 'live',
        shareOnSpots: currentUser.shareVehicleOnSpots !== false,
      }).catch(() => {});
      const isAlreadyVisited = visitedSpotIds?.includes(spotId) ?? false;
      if (!isAlreadyVisited) {
        let county = spot.county;
        if (!county) { try { county = (await reverseGeocodeRegion(spot.location))?.county; } catch {} }
	        const result = await toggleVisitSpot(spotId, spot.name, spot.location.latitude, spot.location.longitude, currentUser.uid, visits, county, spot.photoUrls?.[0], currentUser.activeVehicleCategory, currentUser.activeVehicleLength, currentUser.verifiedSpotCount || 0);
	        await autoPlaceSticker(spotId, spot);
	        if (result.levelUp && result.newTier) setLevelUpTier(result.newTier);
	        showContributionFeedback(
	          'checkin',
	          spot,
	          `Turen er logget, og innsjekken teller mot neste nivå. Takk for at du bidrar til bedre plassdata.`,
	          20,
	          result
	        );
	        if (spot.spotType === 'commercial_campsite') {
	          setCampingRatingSpot(spot);
	        } else if (shouldPromptVerificationAfterVisit(spot)) {
	          setVerifyPromptSpot(spot);
	        } else {
	          void maybePromptReviewAfterVisit(spot);
	        }
	      }
	      for (const tag of tags) {
	        await addConditionReport(spotId, currentUser.uid, currentUser.displayName || 'Ukjent', tag);
	      }
	      if (tags.length > 0) {
	        showContributionFeedback(
	          'report_condition',
	          spot,
	          'Bra rapportert. Slike oppdateringer hjelper andre å vurdere forholdene før de drar.',
	          10
	        );
	      }
      updateSpotLocally({ ...spot, occupiedBy: currentUser.uid, occupiedByName: currentUser.displayName || 'Ukjent', occupiedUntil, isVerified: true, verifiedAt: wasVerified ? spot.verifiedAt : Date.now() });
      if (isRasteplass) {
        const endTime = new Date(occupiedUntil).toLocaleTimeString(getDateLocale(), { hour: '2-digit', minute: '2-digit' });
        addToast(t('toast.rasteplass_checked_in', { time: endTime }));
      } else {
        const endDate = new Date(occupiedUntil).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' });
        addToast(t('toast.occupied_marked', { days, date: endDate }));
      }
    } catch (err) {
      console.error('Failed to mark occupied', err);
      addToast(t('toast.error_generic'), 'error');
    }
  };

  // Forleng et pågående opphold (samme innsjekk) med `days` netter fra i dag.
  // Brukes når brukeren alt står på plassen og bare vil bli værende lenger.
  const handleExtendOccupancy = async (spotId: string, days: number) => {
    if (!currentUser) { setIsAuthOpen(true); return; }
    // Merk: plassen kan mangle i `spots` (utenfor kartutsnittet) selv om brukeren
    // er sjekket inn her — pillen drives av currentCheckIn. Ikke returnér tidlig.
    const spot = spots.find(s => s.id === spotId);
    if (spot?.externalId) handleEnsureSpotPersisted(spotId);
    try {
      const occupiedUntil = await extendStay(spotId, days);
      const spotName = spot?.name || currentUser.currentCheckIn?.spotName || '';
      const checkedInAt = currentUser.currentCheckIn?.spotId === spotId
        ? currentUser.currentCheckIn.checkedInAt
        : Date.now();
      await setCurrentCheckIn(currentUser.uid, { spotId, spotName, occupiedUntil, checkedInAt }).catch(() => {});
      if (spot) updateSpotLocally({ ...spot, occupiedBy: currentUser.uid, occupiedByName: currentUser.displayName || 'Ukjent', occupiedUntil });
      const endDate = new Date(occupiedUntil).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short' });
      addToast(t('toast.occupancy_extended', { date: endDate, defaultValue: `Oppholdet forlenget til ${endDate}` }));
    } catch (err) {
      console.error('Failed to extend occupancy', err);
      addToast(t('toast.error_generic'), 'error');
    }
  };

  const handleClearOccupancy = async (spotId: string) => {
    // Ikke returnér tidlig når plassen mangler i `spots`: pillen (og currentCheckIn)
    // kan be om utsjekk selv om plassen er utenfor kartutsnittet. Da ville «Sjekk ut»
    // se ut som en død knapp. Rydd Firestore + innsjekk-pekeren uansett.
    const spot = spots.find(s => s.id === spotId);
    // Rekkefølgen er bevisst: fjern DIN egen innsjekk-peker FØRST. Det er den som
    // driver pillen (`currentCheckIn`), og utsjekk må aldri henge på at den delte
    // plass-skrivingen lykkes — ellers strander pillen «for alltid» hvis den delte
    // skrivingen feiler (manglende dok / forbigående nettverksglipp).
    let anyFailure = false;
    if (currentUser?.currentCheckIn?.spotId === spotId) {
      await clearCurrentCheckIn(currentUser.uid).catch(() => { anyFailure = true; });
    }
    // Rydd opptatt-status på plassen (best effort). Logg utsjekktidspunkt på besøket
    // (grunnlag for reiserute/blogg).
    await clearSpotOccupancy(spotId).catch(() => { anyFailure = true; });
    if (currentUser) {
      await recordCheckOut(currentUser.uid, spotId, visits).catch(() => {});
    }
    if (spot) updateSpotLocally({ ...spot, occupiedBy: undefined, occupiedByName: undefined, occupiedUntil: undefined });
    addToast(anyFailure ? t('toast.error_generic') : t('toast.occupied_cleared'), anyFailure ? 'error' : undefined);
  };

  const handleAddSpotToMap = (e: React.MouseEvent, spotId: string) => {
    e.stopPropagation();
    if (!currentUser) { setIsAuthOpen(true); return; }
    const spot = spots.find(s => s.id === spotId);
    if (spot) {
      setSaveToMyContentSpot(spot);
      setSaveToMyContentRoute(null);
    }
  };

  const handleAddRouteToMap = (routeId: string) => {
    if (!currentUser) { setIsAuthOpen(true); return; }
    const route = routes.find(r => r.id === routeId);
    if (route) {
      setSaveToMyContentRoute(route);
      setSaveToMyContentSpot(null);
    }
  };

  const handleEnsureSpotPersisted = useCallback(async (spotId: string) => {
    const spot = spots.find(s => s.id === spotId);
    if (!spot || !spot.externalId) return;
    const persisted = { ...spot, isVerified: true };
    await upsertSpotsToFirebase([persisted]);
    updateSpotLocally(persisted);
  }, [spots]);

  const handlePromoteBathingPlace = useCallback(async (bp: BathingPlace) => {
    if (!hasBathingLayerAccess) {
      setShowUpgradeReminder(true);
      return;
    }
    const { promoteBathingPlace } = await import('./services/spotPromotionService');
    const spot = await promoteBathingPlace(bp);
    setSpots(prev => mergeSpotsByIdentity(prev, [spot]));
    setSelectedSpotId(spot.id);
  }, [hasBathingLayerAccess]);

  // Reise-copilot: severdighet-markør → EKTE plasskort (samme promoterings-mønster som
  // badeplasser: spot-form + merge + velg → kortet henter bilder via resolveren).
  // Mobil: åpne BUNNARK-kortet — setSelectedSpotId alene er liste-panel-flyten som
  // ligger under alt på mobil (brukeren så bare tooltip). Trykk på punkt = kort, alltid.
  const handleRouteAttractionTap = useCallback(async (a: import('./services/tripPlannerService').RouteAttraction) => {
    const { promoteRouteAttraction } = await import('./services/spotPromotionService');
    const spot = await promoteRouteAttraction(a);
    setSpots(prev => mergeSpotsByIdentity(prev, [spot]));
    if ((import.meta as any).env?.DEV) console.info('[COPILOT-EDIT] åpner severdighet-kort', spot.id, spot.name || '');
    if (!isDesktop) handleSelectSpotDetails(spot, { openMobileSheet: true });
    else setSelectedSpotId(spot.id);
  }, [isDesktop, handleSelectSpotDetails]);

  const handleAddSpotSubmit = async (
    data: Partial<CampingSpot>,
    files: File[],
    options?: { shareWithCommunity: boolean; saveToMyContent: boolean }
  ) => {
    const hasExistingId = !!data.id;
    const spotId = data.id || `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Default: today's behavior (share, do not auto-save to My Content). New modal opt-in's into both.
    const shareWithCommunity = options?.shareWithCommunity ?? true;
    const saveToMyContent = options?.saveToMyContent ?? false;

    // Image upload moved to background — don't block the modal
    const uploadedUrls: string[] = [];

    const requestedLocation = data.location || pendingAnalysisCoords || mapCenter;
    const normalizedFeatures = Array.isArray(data.features) ? data.features.map(f => String(f).toUpperCase()) : [];
    const isBarrierSpot = normalizedFeatures.includes('BOM') || normalizedFeatures.includes('SPERRING') || normalizedFeatures.includes('BOMMER');
    const isBathingSpot = normalizedFeatures.includes('BADEPLASS') || normalizedFeatures.includes('BADEPLASSER');
    const isForbiddenArea = data.spotType === 'forbidden_area';
    // Amenity/coastal punkter som ikke er knyttet til veiforbindelse
    const isNonRoadAmenity = data.spotType === 'osm_amenity' || data.spotType === 'coastal' || isBathingSpot || isForbiddenArea;
    let finalLocation = requestedLocation;

    if (isBarrierSpot && currentViewBounds && requestedLocation) {
      const snap = await snapToNearestRoad(requestedLocation, currentViewBounds, 250);
      if (snap.snapped) {
        finalLocation = snap.location;
      }
    }

    const mergedSpotBase: CampingSpot = {
      id: spotId,
      source: data.source || getSpotSource(data) || 'user',
      externalId: data.externalId,
      importVersion: data.importVersion || (data.source ? `${data.source}-manual` : 'user-manual'),
      lastSyncedAt: new Date().toISOString(),
      name: data.name || 'Ny plass',
      description: data.description || '',
      location: finalLocation,
      score: data.score ?? 8,
      spotType: data.spotType || 'confirmed',
      sourceLevel: data.sourceLevel ?? 4,
      suitabilityColor: data.suitabilityColor || '#10b981',
      features: Array.from(new Set([...(data.features || []), 'BRUKERBIDRAG'])),
      reasoning: data.reasoning || (isNonRoadAmenity ? 'Fasilitets-/servicepunkt registrert av bruker.' : 'Brukeropprettet plass.'),
      trafficRisk: data.trafficRisk || (isNonRoadAmenity ? 'N/A' : 'Ukjent'),
      buildingProximity: data.buildingProximity || (isNonRoadAmenity ? 'N/A' : 'Ukjent'),
      // IKKE 999. Det tallet var en plassholder som leste som en måling.
      //
      // Målt i produksjon 17.08.2026: 23 av 55 brukeropprettede danske plasser
      // hadde `nearestHouse: 999` uten at noe var målt. Kombinert med
      // `sourceLevel: 4` og `spotType: 'confirmed'` gjorde det plassen immun mot
      // alle tre vaktene samtidig — 150 m-regelen så 999 og slapp den gjennom,
      // opprydningen hopper over nivå 4, og kartet rangerte den OVER de
      // maskinsjekkede plassene (score 8 mot 5). En plass satt i et jordekryss
      // framsto dermed som befart og ren.
      //
      // `null` betyr «ikke målt», og det er sant. Klientens 150 m-regel bryr seg
      // bare om tall, så synligheten er uendret — men nå kan en måling fylle
      // inn ekte verdi etterpå, og «ukjent» kan vises som ukjent.
      nearestHouse: data.nearestHouse ?? null,
      highway: data.highway || (isNonRoadAmenity ? 'N/A' : 'Ukjent'),
      surfaceType: data.surfaceType || (isNonRoadAmenity ? 'N/A' : 'Ukjent'),
      isPublicAccess: data.isPublicAccess ?? true,
      legal: data.legal || {
        isUtmark: true,
        inProtectedArea: false,
        hasBarrier: false,
        // Samme grunn: vi har ikke målt avstanden til dyrket mark her.
        distToFarmland: null,
        nearMajorRoad: false
      },
      createdBy: data.createdBy || currentUser?.uid,
      createdByName: data.createdByName || currentUser?.displayName || 'Anonym',
      createdByPhoto: data.createdByPhoto || currentUser?.photoURL || undefined,
      ...(currentUser && !data.createdBy ? (() => {
        const vSnap = buildVehicleSnapshot(currentUser);
        return vSnap ? {
          creatorVehicleCategory: vSnap.category,
          creatorVehicleLength: vSnap.length,
          creatorVehicleIs4wd: vSnap.is4wd,
        } : {};
      })() : {}),
      createdAt: data.createdAt || Date.now(),
      isVerified: data.isVerified || files.length > 0,
      verifiedAt: (data.isVerified || files.length > 0) ? (data.verifiedAt || Date.now()) : data.verifiedAt,
      isInvalid: isForbiddenArea ? true : data.isInvalid,
      invalidReason: isForbiddenArea ? 'camping_forbudt' : data.invalidReason,
      forbiddenAreaMetadata: isForbiddenArea ? {
        reason: data.description || '',
        confirmed: false,
        reportedBy: currentUser?.uid,
        reportedAt: Date.now(),
      } : data.forbiddenAreaMetadata,
      coverage: data.coverage,
      mapUrl: data.mapUrl,
      accessRoute: data.accessRoute,
      weather: data.weather,
      warnings: data.warnings,
      reviews: data.reviews,
      photoUrls: Array.from(new Set([...(data.photoUrls || []), ...uploadedUrls])),
      property: data.property,
      rasteplassMetadata: data.rasteplassMetadata,
      commercialMetadata: data.commercialMetadata,
      roadMetadata: data.roadMetadata,
      barrierMetadata: data.barrierMetadata,
      aiResult: data.aiResult,
      nearestCharger: data.nearestCharger,
      hikingTrails: data.hikingTrails,
      publicRoadConnection: data.publicRoadConnection,
      visitCount: data.visitCount,
      webcamUrl: data.webcamUrl,
      videoUrl: data.videoUrl,
      website: data.website,
      visibility: data.visibility,
      vehicleTags: data.vehicleTags,
      vehicleTagConfidence: data.vehicleTagConfidence,
      vehicleAnalyzedAt: data.vehicleAnalyzedAt,
      attractionCategory: data.attractionCategory,
    };
    // Vis spottet på kartet umiddelbart (før enrichment som kan ta tid pga. nettverkskall) —
    // kun når brukeren faktisk deler. Privat-only blir lagret som waypoint i Mitt innhold.
    if (shareWithCommunity) {
      if (hasExistingId) {
        updateSpotLocally(mergedSpotBase);
      } else {
        setSpots(prev => {
          if (prev.some(s => s.id === mergedSpotBase.id)) return prev;
          return [mergedSpotBase, ...prev];
        });
      }
      addToast(navigator.onLine ? t('toast.spot_saved') : t('toast.spot_saved_offline', 'Lagret lokalt — synkroniseres når nett er tilgjengelig'), navigator.onLine ? 'success' : 'info');
      setSelectedSpotId(mergedSpotBase.id);
    } else {
      addToast(t('toast.private_waypoint_saved', 'Privat punkt lagret i Mitt innhold'), 'success');
    }

    // Lukk modalen umiddelbart
    setShowAddModal(false);
    setEditingSpot(undefined);
    setPreSelectedFiles([]);

    // Flytt kartet til posisjonen
    if (mergedSpotBase.location) {
      setMapCommand({ center: mergedSpotBase.location, zoom: 16, id: Date.now() });
    }

    // Mirror til Mitt innhold som privat waypoint hvis bruker ber om det.
    // Persisterer features + spotMetadata slik at private waypoints kan rendres
    // som proper layer-spots (Bom-ikon, riktig kort, osv.).
    if (saveToMyContent && currentUser?.uid && !hasExistingId) {
      const primaryFeature = mergedSpotBase.features.find(f => f !== 'BRUKERBIDRAG');
      const wpIcon = spotFeatureToWaypointIcon(primaryFeature);
      const wpColor = mergedSpotBase.suitabilityColor || getWaypointMeta(wpIcon).defaultColor;
      const existingPhoto = mergedSpotBase.photoUrls?.find(u => u && !u.startsWith('blob:'));
      const persistedFeatures = mergedSpotBase.features.filter(f => f !== 'BRUKERBIDRAG');
      const persistedMeta: Record<string, any> = {
        spotType: mergedSpotBase.spotType,
        suitabilityColor: mergedSpotBase.suitabilityColor,
      };
      if (mergedSpotBase.barrierMetadata) persistedMeta.barrierMetadata = mergedSpotBase.barrierMetadata;
      if (mergedSpotBase.attractionCategory) persistedMeta.attractionCategory = mergedSpotBase.attractionCategory;
      if (mergedSpotBase.description) persistedMeta.description = mergedSpotBase.description;
      if (mergedSpotBase.website) persistedMeta.website = mergedSpotBase.website;
      if (mergedSpotBase.videoUrl) persistedMeta.videoUrl = mergedSpotBase.videoUrl;
      try {
        await addWaypointContent(
          currentUser.uid,
          mergedSpotBase.location.latitude,
          mergedSpotBase.location.longitude,
          mergedSpotBase.name,
          wpIcon,
          wpColor,
          mergedSpotBase.description,
          null,
          existingPhoto || null,
          shareWithCommunity ? mergedSpotBase.id : null,
          persistedFeatures.length > 0 ? persistedFeatures : null,
          Object.keys(persistedMeta).length > 0 ? persistedMeta : null
        );
      } catch (err) {
        console.warn('[handleAddSpotSubmit] Mirror to user_content failed:', err);
      }
    }

    // Hvis brukeren valgte privat-only: ikke kjør backend enrichment/upsert. Stop her.
    if (!shareWithCommunity) return;

    // Enrich med geodata og lagre i bakgrunnen (blokkerer ikke UI)
    void (async () => {
      let spotWithPhotos = mergedSpotBase;
      try {
        // Upload images — offline-first: queue immediately if offline, try direct upload if online
        if (files.length > 0 && currentUser?.uid) {
          if (!navigator.onLine) {
            // Offline: queue directly, don't attempt upload
            try {
              const { queueUploads } = await import('./services/offlineUploadQueue');
              await queueUploads(spotId, currentUser.uid, files);
              refreshPendingCount();
              addToast(t('toast.images_queued', 'Bilder lagret lokalt — lastes opp når du er online'), 'info');
            } catch (queueErr) {
              console.warn('[handleAddSpotSubmit] Could not queue images offline:', queueErr);
            }
          } else {
            // Online: try direct upload, fall back to queue on failure
            try {
              const urls = await uploadSpotImages(spotId, currentUser.uid, files);
	              if (urls.length > 0) {
	                const primaryThumb = spotWithPhotos.ugcThumbnailUrl || urls[0];
	                spotWithPhotos = {
	                  ...mergedSpotBase,
	                  photoUrls: Array.from(new Set([...(mergedSpotBase.photoUrls || []), ...urls])),
	                  ugcThumbnailUrl: primaryThumb,
	                };
	                updateSpotLocally(spotWithPhotos);
	                showContributionFeedback(
	                  'add_photo',
	                  spotWithPhotos,
	                  'Bra bildebidrag. Bilder gjør det enklere for andre å vurdere adkomst, underlag og stemning på plassen.',
	                  15
	                );
	              }
            } catch (uploadErr) {
              console.warn('[handleAddSpotSubmit] Image upload failed, queuing:', uploadErr);
              try {
                const { queueUploads } = await import('./services/offlineUploadQueue');
                await queueUploads(spotId, currentUser.uid, files);
                refreshPendingCount();
                addToast(t('toast.images_queued', 'Bilder lagret lokalt — lastes opp automatisk'), 'info');
              } catch (queueErr) {
                console.warn('[handleAddSpotSubmit] Could not queue images:', queueErr);
                addToast('Bilder kunne ikke lastes opp. Prøv igjen senere.', 'warning');
              }
            }
          }
        }

        let mergedSpot = await enrichSpotWithGeoData(spotWithPhotos);

        // For Swedish spots without classification, look up nearest Trafikverket road
        // and set roadAccessClass / accessRoadType so layer-gating filters work.
        const spotCountry = mergedSpot.location ? detectCountry(mergedSpot.location.latitude, mergedSpot.location.longitude) : null;
        if (spotCountry === 'SE' && (!mergedSpot.roadAccessClass || mergedSpot.roadAccessClass === 'unknown')) {
          try {
            const nearest = await findNearestSwedishRoad(mergedSpot.location, 80);
            if (nearest && nearest.accessClass !== 'service_road') {
              const accessRoadType =
                nearest.accessClass === 'forest_road' ? 'skogsbilveg' :
                nearest.accessClass === 'tractor_road' ? 'traktorveg' : 'public';
              mergedSpot = {
                ...mergedSpot,
                roadAccessClass: nearest.accessClass as CampingSpot['roadAccessClass'],
                accessRoadType: accessRoadType as CampingSpot['accessRoadType'],
                roadAccessSource: nearest.properties.source === 'osm' ? 'osm' : 'trafikverket_nvdb',
                accessRoadDistance: Math.round(nearest.distance),
              };
            }
          } catch (err) {
            console.warn('[handleAddSpotSubmit] Swedish road classification failed:', err);
          }
        }

        updateSpotLocally(mergedSpot);
        console.log('[handleAddSpotSubmit] Saving to Firestore:', mergedSpot.id, 'type:', mergedSpot.spotType, 'loc:', mergedSpot.location?.latitude, mergedSpot.location?.longitude);
        if (mergedSpot.visibility === 'private' && currentUser?.uid) {
          await upsertPrivateSpot(currentUser.uid, mergedSpot);
        } else {
          await upsertSpotsToFirebase([mergedSpot]);
        }
        console.log('[handleAddSpotSubmit] Firestore save OK:', mergedSpot.id);

        // Gjennomgått auto-nav-kandidat → fjern den fra «Til gjennomgang» nå som den er lagret.
        if (reviewingCandidateIdRef.current && currentUser?.uid) {
          const candId = reviewingCandidateIdRef.current;
          reviewingCandidateIdRef.current = null;
          deleteAutonavCandidate(currentUser.uid, candId).catch(err =>
            console.warn('[handleAddSpotSubmit] Kunne ikke slette gjennomgått kandidat:', err));
        }

        // Subtil synk-bekreftelse
        if (!navigator.onLine) {
          // Firestore håndterer offline-kø internt, vis bekreftelse når vi er tilbake online
          const onOnline = () => { addToast(t('toast.spot_synced', 'Synkronisert med skyen'), 'success'); window.removeEventListener('online', onOnline); };
          window.addEventListener('online', onOnline);
        }

        const shouldAnalyzeVehicles =
          mergedSpot.isVerified === true &&
          Array.isArray(mergedSpot.photoUrls) &&
          mergedSpot.photoUrls.length > 0;

        if (shouldAnalyzeVehicles) {
          try {
            const result = await analyzeVerifiedSpotVehicles(mergedSpot.id);
            if (result?.ok) {
              const nextSpot: CampingSpot = {
                ...mergedSpot,
                vehicleTags: Array.isArray(result.tags) ? result.tags : [],
                vehicleTagConfidence: result.confidence || {},
                vehicleAnalyzedAt: new Date().toISOString()
              };
              updateSpotLocally(nextSpot);
            }
          } catch (err) {
            console.warn('Vehicle analysis after verification failed', err);
          }
        }
      } catch (err) {
        console.error('[handleAddSpotSubmit] Background enrichment/save failed:', err);
        addToast(t('toast.spot_save_error', 'Kunne ikke lagre til sky — prøver på nytt...'), 'warning');
        // Spottet er allerede synlig på kartet — prøv å lagre uenrichet versjon
        try {
          console.log('[handleAddSpotSubmit] Retrying unenriched save for:', spotWithPhotos.id);
          if (spotWithPhotos.visibility === 'private' && currentUser?.uid) {
            await upsertPrivateSpot(currentUser.uid, spotWithPhotos);
          } else {
            await upsertSpotsToFirebase([spotWithPhotos]);
          }
          console.log('[handleAddSpotSubmit] Retry save OK:', spotWithPhotos.id);
        } catch (retryErr) {
          console.error('[handleAddSpotSubmit] Retry save also failed:', retryErr);
        }
      }
    })();
  };

  const expandSidebar = () => {
    if (navigationBuilding || followPreflight) return;
    if (isDesktop) setSidebarExpanded(true);
    else setMobileView('list');
  };

  const handleBackToRouteFocus = useCallback(() => {
    if (!routeReturnContext) return;

    const ids = new Set(routeReturnContext.routeIds);
    if (routeReturnContext.selectedRouteId) ids.add(routeReturnContext.selectedRouteId);
    if (ids.size === 0) return;

    const primaryRouteId = routeReturnContext.selectedRouteId || Array.from(ids)[0] || null;
    const targetRoute = primaryRouteId ? routes.find((r) => r.id === primaryRouteId) : null;

    setSelectedSpotId(null);
    setShowMobileSheet(false);
    setSidebarMode('routes');
    setPreviewRegion(routeReturnContext.previewRegion);
    setActiveRouteIds(ids);
    setSelectedRouteId(primaryRouteId);
    if (!isDesktop) setMobileView('map');
    if (targetRoute?.center) {
      setMapCommand({ center: targetRoute.center, zoom: 9, id: Date.now() });
    }
    setRouteReturnContext(null);
    expandSidebar();
  }, [routeReturnContext, routes, isDesktop, setSelectedSpotId, setShowMobileSheet]);

  const handleBackToCollabMap = useCallback(() => {
    if (!collabReturnContext) return;
    setActiveCollabMapId(collabReturnContext.mapId);
    setAddToMapSpotId(null);
    setAddToMapRouteId(null);
    setShowCollabMapModal(true);
    setCollabReturnContext(null);
  }, [collabReturnContext]);

  const onMobileSelectSpot = (s: CampingSpot) => {
    if (!s) return;
    if (!isPaidUser && s.spotType === 'algorithmic') {
      nudgeRef.current?.showNudge('algorithmic_spot');
      return;
    }
    if (activeRouteIds.size > 0 || selectedRouteId) {
      setRouteReturnContext({
        routeIds: Array.from(activeRouteIds),
        selectedRouteId,
        previewRegion
      });
    }
    handleCancelScan();
    setSelectedRouteId(null);
    setPreviewRegion(null);
    setActiveRouteIds(new Set());
    setSidebarMode('spots');
    setShowMobileSheet(false);
    handleSelectSpot(s);
    expandSidebar();
  };

  const onMobileSelectNewsSpot = (newsSpot: CampingSpot) => {
    if (!newsSpot) return;
    setSpots(prev => (prev.some(s => s.id === newsSpot.id) ? prev : [newsSpot, ...prev]));
    onMobileSelectSpot(newsSpot);
  };

  // Reise-copilot: åpne plasskortet for et dag-stopp. Stoppene kommer fra korridor-søket
  // og ligger ikke nødvendigvis i spots-state — og kortet slår opp via selectedSpotId i
  // spots, så uten merge blir trykket en stille no-op. Samme mønster som news-spots over.
  // id-guard: enkelte seedede cached_spots-docs mangler id-feltet (backfill pågår).
  const onSelectCopilotStop = (raw: CampingSpot) => {
    if (!raw || !raw.location) return;
    const s = raw.id ? raw : { ...raw, id: `poi-stop-${raw.location.latitude.toFixed(5)}x${raw.location.longitude.toFixed(5)}` };
    if ((import.meta as any).env?.DEV) console.info('[COPILOT-EDIT] åpner stopp-kort', s.id, s.name || '');
    setSpots(prev => (prev.some(p => p.id === s.id) ? prev : [s, ...prev]));
    setMapCommand({ center: s.location, zoom: 12, id: Date.now() });
    // Mobil: åpne BUNNARK-kortet (synlig over kartet; chatten tvinges ned av spotCardOpen).
    // onMobileSelectSpot åpner liste-panel-flyten — den ligger UNDER copilot-chatten på
    // mobil og så ut som «ingenting skjer». Desktop: sidepanel-flyten som før.
    if (!isDesktop) handleSelectSpotDetails(s, { openMobileSheet: true });
    else onMobileSelectSpot(s);
  };

  const onMobileToggleRoute = (r: GpxRoute) => {
    if (!isPaidUser) {
      nudgeRef.current?.showNudge('routes_gpx');
      return;
    }
    setRouteReturnContext(null);
    setActiveRouteIds(prev => {
      const next = new Set(prev);
      if (next.has(r.id)) {
        next.delete(r.id);
      } else {
        next.clear(); // Keep it single-select for mobile if that was the intent, or just add it
        next.add(r.id);
      }
      return next;
    });
    setPreviewRegion(null);
    setSelectedRouteId(null);
    if (isDesktop) {
      // Don't switch view on desktop
    } else {
      setMobileView('map');
    }
  };

  const handleSetPreviewRegion = (region: RouteRegion | null) => {
    setPreviewRegion(region);
    if (region && mobileView === 'list') {
      setMobileView('map');
    }
  };

  const selectedRoute = selectedRouteId ? routes.find(r => r.id === selectedRouteId) : null;
  const isAdmin = isAdminUser(currentUser);
  const showAdminReevaluateCta = false;
  // Keep firebaseService's client-side admin gate in sync. Non-admins are
  // blocked by firestore.rules from curating shared spots (isInvalid / spots
  // mirror); this lets upsertSpotsToFirebase / markSpotAsInvalid skip those
  // doomed writes instead of throwing permission-denied.
  useEffect(() => {
    setClientAdminStatus(isAdmin);
  }, [isAdmin]);
  // Welcome sheet for first-time visitors (skip for shared map links)
  useEffect(() => {
    if (currentUser) return;
    if (parsedSharedMap) return;
    if (!localStorage.getItem('fricamp_welcome_shown')) {
      setShowWelcomeSheet(true);
    }
  }, []);

  // Suggest county download for new premium users
  useEffect(() => {
    if (!isPaidUser || !userPosition) return;
    if (localStorage.getItem('fricamp_county_download_shown')) return;
    const timer = setTimeout(() => setShowCountyDownload(true), 5000);
    return () => clearTimeout(timer);
  }, [isPaidUser, userPosition]);

  // Performance monitoring: suggest offline download when tiles load slowly during navigation
  useEffect(() => {
    if (!followActive) { resetPerfMetrics(); return; }
    const interval = setInterval(() => {
      if (shouldRecommendDownload()) {
        markRecommendationShown();
        addToast(
          isPaidUser
            ? 'Kartet laster tregt — last ned området for offline bruk'
            : 'Kartet laster tregt — oppgrader for offline kart',
          'warning'
        );
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [followActive, isPaidUser]);

  // Show vehicle prompt after first spot interaction (for users who haven't completed vehicle onboarding)
  useEffect(() => {
    if (!selectedSpotId || !currentUser) return;
    if (currentUser.onboarding_vehicle_done) return;
    if (showVehiclePrompt) return;
    const timer = setTimeout(() => setShowVehiclePrompt(true), 2000);
    return () => clearTimeout(timer);
  }, [selectedSpotId, currentUser?.uid]);

  // Event-based upgrade reminder: after 5 favorites (skip for paid/admin/tester)
  useEffect(() => {
    if (!currentUser || !currentUser._firestoreReady || hasPaidAccess(currentUser)) return;
    if (favoriteSpotIds.length !== 5) return;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('fricamp_upgrade_fav5_shown')) return;
    const timer = setTimeout(() => {
      setShowUpgradeReminder(true);
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('fricamp_upgrade_fav5_shown', '1');
    }, 1500);
    return () => clearTimeout(timer);
  }, [currentUser?.uid, favoriteSpotIds.length, currentUser?.subscriptionStatus]);

  // Event-based upgrade reminder: after 3 visits (skip for paid/admin/tester)
  useEffect(() => {
    if (!currentUser || !currentUser._firestoreReady || hasPaidAccess(currentUser)) return;
    if (visits.length !== 3) return;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('fricamp_upgrade_visit3_shown')) return;
    const timer = setTimeout(() => {
      setShowUpgradeReminder(true);
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem('fricamp_upgrade_visit3_shown', '1');
    }, 1500);
    return () => clearTimeout(timer);
  }, [currentUser?.uid, visits.length, currentUser?.subscriptionStatus]);

  // Stable boolean – only changes when zoom crosses the free-tier threshold,
  // preventing visibleSpots from recalculating on every zoom increment.
  const isAboveFreeZoom = mapZoom > FREE_ZOOM_MAX;
  // Camping forbudt-markeringer vises kun ved nær zoom (≥ 16) for å unngå
  // visuell støy på lavere zoom-nivå.
  const isCloseZoom = mapZoom >= 16;

	  const visibleSpots = useMemo(() => {
	    const normalizeRoad = (road?: string) => (road || '').toLowerCase().trim();
	    const isFricampSpot = (s: CampingSpot) => s.spotType === 'algorithmic' || s.spotType === 'confirmed';
    const hasBarrierFeature = (s: CampingSpot) => {
      const f = Array.isArray(s.features) ? s.features.map(x => String(x).toUpperCase()) : [];
      return f.includes('BOM') || f.includes('SPERRING') || f.includes('BOMMER');
    };
    const hasAmenityFeature = (s: CampingSpot) => {
      const f = Array.isArray(s.features) ? s.features.map(x => String(x).toUpperCase()) : [];
      return f.includes('PICNIC') || f.includes('GAPAHUK') || f.includes('BÅLPLASS') || f.includes('UTSIKT') || f.includes('UTKIKK') || f.includes('FJELLTOPP');
    };
    const offroadFilters = {
      track: !!currentUser?.offroadRoadFilters?.track,
      unclassified: !!currentUser?.offroadRoadFilters?.unclassified,
      service: !!currentUser?.offroadRoadFilters?.service,
      residential: !!currentUser?.offroadRoadFilters?.residential,
    };
    const isBathingSpotRecord = (spot: CampingSpot) => {
      const upperF = Array.isArray(spot.features) ? spot.features.map(f => String(f).toUpperCase()) : [];
      return upperF.includes("BADEPLASS") || upperF.includes("BADEPLASSER");
    };

    const filteredAndMapped: CampingSpot[] = [];
    spots.forEach(s => {
      if (s.isDeleted) return false;

      // Camping forbudt-markeringer: vises kun hvis verifisert med bilde, og
      // enten valgt eller ved zoom ≥ 16. Hopper over isInvalid-filteret under
      // (forbidden_area lagres med isInvalid: true, men vi vil vise dem som
      // advarsel når brukeren har zoomet tett nok inn).
      if (s.spotType === 'forbidden_area') {
        const verifiedWithPhoto = s.isVerified === true
          && Array.isArray(s.photoUrls)
          && s.photoUrls.length > 0;
        if (!verifiedWithPhoto) return false;
        if (s.id === selectedSpotId || isCloseZoom) {
          filteredAndMapped.push(s);
        }
        return;
      }

      if (s.isInvalid) return false;
      if (s.id === selectedSpotId) {
        if (isBathingSpotRecord(s) && !hasBathingLayerAccess) return false;
        filteredAndMapped.push(s);
        return;
      }
      // Global 150m-regel: Bekreftede/verifiserte plasser røres aldri.
      // For alle andre (inkl. OSM-amenities, attractions, rasteplasser, fricamp-spots,
      // bruker-bidrag) gjelder 150m-regelen hvis nearestHouse-feltet er satt.
      // Dette fanger urbane spots som ikke er algoritmiske, f.eks. OSM Attractions
      // i tette boligområder.
      if (s.isVerified !== true
          && typeof s.nearestHouse === 'number'
          && s.nearestHouse < 150
          && s.spotType !== 'commercial_campsite'
          && s.spotType !== 'rasteplass'
          && s.source !== 'user'
          && !s.id?.startsWith('manual-')
          && !s.features?.includes('BRUKERBIDRAG')) {
        return false;
      }
      const upperF = Array.isArray(s.features) ? s.features.map(f => String(f).toUpperCase()) : [];
      // Anviste autocamperpladser hører til bobilplass-laget selv om de er lagret
      // som `spotType: 'algorithmic'` — se utils/autocamperSpots.ts. Uten unntaket i
      // fricamp-grenen under ville de vist seg på BEGGE lagene.
      const isAutocamper = isAutocamperSpot(s);

      let matchesActiveLayer = false;

      if ((s.spotType === 'algorithmic' || s.spotType === 'confirmed') && !isAutocamper && layers.algorithmic) {
        matchesActiveLayer = true;
      }
      if (isAutocamper && layers.commercialRvParks) {
        matchesActiveLayer = true;
      }
      if ((s.spotType === 'osm_amenity' && (upperF.includes("BADEPLASS") || upperF.includes("BADEPLASSER"))) || upperF.includes("BADEPLASS") || upperF.includes("BADEPLASSER")) {
        if (hasBathingLayerAccess && layers.bathingPlaces) matchesActiveLayer = true;
      }
      if (upperF.includes("DUMP_STATION")) {
        if (layers.dumpStations || currentUser?.needsEmptying) matchesActiveLayer = true;
      }
      if ((s.spotType === 'osm_amenity' && !upperF.includes("DUMP_STATION") && !upperF.includes("BADEPLASS") && !upperF.includes("BADEPLASSER")) ||
          upperF.includes("TOALETT") || upperF.includes("BÅLPLASS") || upperF.includes("PICNIC") || upperF.includes("GAPAHUK") || upperF.includes("UTSIKT") || upperF.includes("VANN")) {
        if (layers.osmAmenities) matchesActiveLayer = true;
      }
      if (s.spotType === 'commercial_campsite') {
        const isRv = s.commercialType === 'rv_park';
        if (isRv ? layers.commercialRvParks : layers.commercialCampsites) matchesActiveLayer = true;
      }
      if (s.spotType === 'rasteplass') {
        const isSnuplass = upperF.includes("SNUPLASS");
        if (isSnuplass && layers.algorithmic) {
          const snuScore = s.aiResult?.score ?? s.score;
          if (typeof snuScore === 'number' && snuScore < 4) return false;
          matchesActiveLayer = true;
        }
        if (!isSnuplass && layers.restAreas) matchesActiveLayer = true;
      }
      if (s.spotType === 'coastal' && layers.coastal) {
        matchesActiveLayer = true;
      }
      if (hasBarrierFeature(s) && layers.barriers) {
        matchesActiveLayer = true;
      }

      if (!matchesActiveLayer) return false;

	      // Autocamperpladser hopper over hele fricamp-blokken: innmark, 150 m-regelen,
	      // score >= 4 og premium-gating er kriterier for VILLMARKSPLASSER. En anvist
	      // bobilplass er ingen av delene, og `!layers.algorithmic`-returen under ville
	      // dessuten skjult den med fricamp-laget av.
	      if (s.spotType === 'algorithmic' && !isAutocamper) {
        const isConfirmed = s.sourceLevel === 4 || s.isVerified === true;
        const isUserRequested = (Array.isArray(s.features) && s.features.includes('MANUELT')) || s.id?.startsWith('manual-');
        if (!isConfirmed && s.isInnmark === true) return false;
        // User-requested spots are exempt: nearestHouse is shown as a warning (score capped at 4) instead of disqualifying
        if (!isConfirmed && !isUserRequested && typeof s.nearestHouse === 'number' && s.nearestHouse < 150) return false;
        if (!layers.algorithmic) return false;
        // Gating: Free users only see spots at low zoom
        if (!isPaidUser && isAboveFreeZoom) {
          const grid = 0.2;
          const fakeLat = Math.round(s.location.latitude / grid) * grid;
          const fakeLng = Math.round(s.location.longitude / grid) * grid;
          filteredAndMapped.push({ ...s, isPremiumGated: true, location: { latitude: fakeLat, longitude: fakeLng } });
          return;
        }

        const score = s.aiResult?.score ?? s.score;
        if (score < 4) return false;
	      }

		      // Samme grunn: veityper (traktorvei/skogsbilvei/servicevei) og
		      // kjøretøylengde-filtrene gjelder fremkommelighet til villmarksplasser.
		      if (isFricampSpot(s) && !isAutocamper) {
		        const highway = normalizeRoad(s.highway);
		        const roadAccessClass = deriveRoadAccessClass(s).roadAccessClass;
		        const isTractorRoadSpot = roadAccessClass === 'tractor_road';
		        const isForestRoadSpot = roadAccessClass === 'forest_road';
		        const isServiceRoadSpot = roadAccessClass === 'service_road';

		        // User-requested spots (MANUELT) bypass road layer toggle filters —
		        // they should always be visible since the user explicitly analysed them.
		        const isUserRequestedFricamp = (Array.isArray(s.features) && s.features.includes('MANUELT')) || s.id?.startsWith('manual-');
		        if (isUserRequestedFricamp) { filteredAndMapped.push(s); return; }

		        if (isTractorRoadSpot && !layers.tractorRoads) return false;
		        if (isForestRoadSpot && !layers.forestRoads) return false;
		        if (isServiceRoadSpot && !layers.serviceRoads) return false;

		        // Manual activation of these map layers should always reveal matching spots.
		        if (isTractorRoadSpot && layers.tractorRoads) {
		          filteredAndMapped.push(s);
		          return;
		        }
		        if (isForestRoadSpot && layers.forestRoads) {
		          filteredAndMapped.push(s);
		          return;
		        }
		        if (isServiceRoadSpot && layers.serviceRoads) {
		          filteredAndMapped.push(s);
		          return;
		        }

	        if (layers.vehicleFiltered) {
	          filteredAndMapped.push(s);
	          return;
	        }

	        // Road type filtering: uses vehicle road_filters (via offroadFilters) or legacy profile settings
		        if (isTractorRoadSpot && !offroadFilters.track) return false;
		        if (isForestRoadSpot && !offroadFilters.unclassified) return false;
		        if (isServiceRoadSpot && !offroadFilters.service) return false;
	        if (highway === 'residential' && !offroadFilters.residential) return false;

        // Length-based filtering: stricter rules for large vehicles
	        const vLen = currentUser?.activeVehicleLength;
	        if (vLen && vLen > 700) {
		          // 7m+: hide track, service roads, and forest road (skogsbilvei) spots
		          if (isTractorRoadSpot) return false;
		          if (isServiceRoadSpot) return false;
		          if (isForestRoadSpot) return false;
	        }
	        if (vLen && vLen > 1000) {
	          // 10m+: also hide unclassified and private road spots
	          if (isForestRoadSpot) return false;
	          if (s.publicRoadConnection?.category === 'P') return false;
	        }
	      }

      if (filterVerified && !s.isVerified) return false;

      // Quick-filters (also affect map markers)
      if (activeFilters.size > 0) {
        const featureStrings = Array.isArray(s.features) ? s.features.map((ft) => String(ft).toUpperCase()) : [];
        const hasFeatureToken = (...tokens: string[]) =>
          featureStrings.some((ft) => tokens.some((token) => ft.includes(token)));

        for (const f of activeFilters) {
          if (f === 'fri_tilgang' && s.legal?.hasBarrier) return false;
          if (f === 'verifisert' && !s.isVerified) return false;
          if (f === 'strand' && s.spotType !== 'coastal' &&
            !hasFeatureToken('STRAND', 'KYST', 'SJØ', 'SJO', 'BEACH', 'COAST', 'FJORD')) return false;
          if (f === 'toalett' &&
            !s.rasteplassMetadata?.toalett &&
            !hasFeatureToken('TOALETT', 'TOALETTER', 'TOILET', 'RESTROOM', 'WC')) return false;
          if (f === 'god_dekning' &&
            s.coverage !== 'God' &&
            !hasFeatureToken('GOD DEKNING', 'GOOD COVERAGE', 'STERK DEKNING', '4G', '5G', 'FULL DEKNING')) return false;
          if (f === 'ikke_besøkt' && visitedSpotIds.includes(s.id)) return false;
          // «Beste plasser»: kun høyeste stjerne-tier (utledet av bayesiansk vektet snitt).
          // Plasser uten nok anmeldelser har ingen tier → naturlig ekskludert her.
          if (f === 'beste' && s.ratingTier !== 3) return false;
        }
      }

      filteredAndMapped.push(s);
    });
    return filteredAndMapped;
  }, [spots, selectedSpotId, layers, isPaidUser, hasBathingLayerAccess, isAboveFreeZoom, isCloseZoom, currentUser, filterVerified, activeFilters, visitedSpotIds]);

  // Hvor mange fricamp-spots i nåværende viewport blir skjult kun fordi
  // tractor/forest/service-road-laget er av? Brukes til å vise et banner som
  // foreslår å aktivere lagene i skogkommuner (jf. Aremark-problemet).
  const offroadHiddenStats = useMemo(() => {
    const stats = { tractor: 0, forest: 0, service: 0, total: 0 };
    for (const s of spots) {
      if (s.isInvalid || s.isDeleted) continue;
      if (s.spotType !== 'algorithmic' && s.spotType !== 'confirmed') continue;
      const isConfirmed = s.sourceLevel === 4 || s.isVerified === true;
      if (!isConfirmed && s.isInnmark === true) continue;
      if (!isConfirmed && typeof s.nearestHouse === 'number' && s.nearestHouse < 150) continue;
      const cls = deriveRoadAccessClass(s).roadAccessClass;
      if (cls === 'tractor_road' && !layers.tractorRoads) { stats.tractor++; stats.total++; }
      else if (cls === 'forest_road' && !layers.forestRoads) { stats.forest++; stats.total++; }
      else if (cls === 'service_road' && !layers.serviceRoads) { stats.service++; stats.total++; }
    }
    return stats;
  }, [spots, layers.tractorRoads, layers.forestRoads, layers.serviceRoads]);

  /**
   * Er de skjulte plassene i praksis «Danmark»?
   *
   * Markvei er hovedregelen der, ikke unntaket: 9 902 av 10 150 gyldige danske
   * plasser (97,6 %) ligger på kjørespor mellom jorder, og tre av fire står
   * inntil dyrket mark. Da er «(traktorveg av)» en misvisende parentes — den
   * leses som en detalj, mens den i Danmark betyr «du ser nesten ingenting».
   *
   * Kriteriet er hvor brukeren SER (kartmidten), ikke land-konfigurasjonen —
   * den sier hva som er lansert, ikke hva som er på skjermen.
   */
  const offroadHiddenIsDanishTractor = useMemo(() => {
    if (!currentViewBounds) return false;
    const lat = (currentViewBounds.latMin + currentViewBounds.latMax) / 2;
    const lon = (currentViewBounds.lonMin + currentViewBounds.lonMax) / 2;
    if (detectCountry(lat, lon) !== 'DK') return false;
    return offroadHiddenStats.tractor >= offroadHiddenStats.total * 0.8;
  }, [currentViewBounds, offroadHiddenStats]);

  const [offroadBannerDismissed, setOffroadBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem('fricamp_offroad_banner_dismissed') === '1';
    } catch {
      return false;
    }
  });

  // Show the offroad banner every other qualifying session, max 2 times total.
  // null = decision not yet made this session; true/false = decision locked in.
  const [offroadBannerShowThisSession, setOffroadBannerShowThisSession] = useState<boolean | null>(null);

  const [paidBannerVisible, setPaidBannerVisible] = useState(false);
  const paidBannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const active = !isPaidUser && mapZoom >= 14 && layers.algorithmic && !isAutoConnected;
    if (active) {
      setPaidBannerVisible(true);
      if (paidBannerTimerRef.current) clearTimeout(paidBannerTimerRef.current);
      paidBannerTimerRef.current = setTimeout(() => setPaidBannerVisible(false), 3000);
    } else {
      setPaidBannerVisible(false);
    }
    return () => { if (paidBannerTimerRef.current) clearTimeout(paidBannerTimerRef.current); };
  }, [isPaidUser, mapZoom, layers.algorithmic, isAutoConnected]);

  const handleDismissOffroadBanner = () => {
    try {
      localStorage.setItem('fricamp_offroad_banner_dismissed', '1');
    } catch {}
    setOffroadBannerDismissed(true);
  };

  // Decide once per session whether the offroad banner should appear: show on
  // qualifying session 1, skip session 2, show session 3, then permanently
  // dismiss. The decision is locked in sessionStorage so navigation within the
  // session doesn't flip it.
  useEffect(() => {
    if (offroadBannerDismissed) return;
    if (offroadBannerShowThisSession !== null) return;
    const eligible = spotsReady && mapZoom >= 9 && offroadHiddenStats.total >= 30 && layers.algorithmic && !isAutoConnected;
    if (!eligible) return;

    try {
      let decision = sessionStorage.getItem('fricamp_offroad_banner_session_decision');
      if (decision !== 'show' && decision !== 'skip') {
        const shownCount = parseInt(localStorage.getItem('fricamp_offroad_banner_shown_count') || '0', 10);
        const skipNext = localStorage.getItem('fricamp_offroad_banner_skip_next') === '1';

        if (shownCount >= 2) {
          // Limit reached — convert to permanent dismissal so we never re-evaluate.
          localStorage.setItem('fricamp_offroad_banner_dismissed', '1');
          setOffroadBannerDismissed(true);
          decision = 'skip';
        } else if (skipNext) {
          decision = 'skip';
          localStorage.setItem('fricamp_offroad_banner_skip_next', '0');
        } else {
          decision = 'show';
          localStorage.setItem('fricamp_offroad_banner_shown_count', String(shownCount + 1));
          localStorage.setItem('fricamp_offroad_banner_skip_next', '1');
        }
        sessionStorage.setItem('fricamp_offroad_banner_session_decision', decision);
      }
      setOffroadBannerShowThisSession(decision === 'show');
    } catch {
      setOffroadBannerShowThisSession(false);
    }
  }, [offroadBannerDismissed, offroadBannerShowThisSession, spotsReady, mapZoom, offroadHiddenStats.total, layers.algorithmic, isAutoConnected]);

  // Route context bypasser lag-toggles for spotType (en bruker som
  // planlegger en rute skal se fricamp-plasser langs ruten selv om laget
  // er av), men vei-tilgang er kjøretøy-egnethet og må alltid respekteres
  // — ellers blir brukeren foreslått plasser hen ikke kan kjøre til.
  // During active navigation (followActive): ALWAYS filter to spots along route corridor.
  // For non-follow route viewing with long routes (100km+): also filter to avoid overloading.
  const mapSpots = useMemo(() => {
    if (activeRouteIds.size > 0) {
      const passesRoadAccess = (s: CampingSpot): boolean => {
        if (s.spotType !== 'algorithmic' && s.spotType !== 'confirmed') return true;
        const cls = deriveRoadAccessClass(s).roadAccessClass;
        if (cls === 'tractor_road' && !layers.tractorRoads) return false;
        if (cls === 'forest_road' && !layers.forestRoads) return false;
        if (cls === 'service_road' && !layers.serviceRoads) return false;
        return true;
      };
      const allValid = spots.filter((s) => !s.isDeleted && !s.isInvalid && passesRoadAccess(s));
      const routeCoords = followRouteState?.routeCoords;
      // During active navigation: always filter to route corridor only
      if (followActive && routeCoords && routeCoords.length > 1) {
        return allValid.filter(s => s.location && isPointNearPolyline(s.location, routeCoords, 2000));
      }
      // For long routes (100km+) in non-follow mode, filter to avoid overloading
      const routeDistM = followRouteState?.routeTotalDistanceM ?? 0;
      const activeRoute = routes.find(r => activeRouteIds.has(r.id));
      const routeDistKm = routeDistM > 0 ? routeDistM / 1000 : (activeRoute?.distance ?? 0);
      if (routeDistKm >= 100 && routeCoords && routeCoords.length > 1) {
        return allValid.filter(s => s.location && isPointNearPolyline(s.location, routeCoords, 2000));
      }
      return allValid;
    }
    return visibleSpots;
  }, [activeRouteIds, spots, visibleSpots, followRouteState, followActive, routes, layers.tractorRoads, layers.forestRoads, layers.serviceRoads]);

  const selectedRoutePlanAlternative = useMemo(() => {
    if (routePlanSelectedIndex === null) return null;
    return routePlanAlternatives[routePlanSelectedIndex] || null;
  }, [routePlanAlternatives, routePlanSelectedIndex]);

  const routePlanSidebarSpots = useMemo(() => {
    if (!showRoutePlanContent || !routePlan || !selectedRoutePlanAlternative?.fullPolyline?.length) {
      return visibleSpots;
    }
    const routeSpots = visibleSpots.filter((spot) =>
      !spot.isDeleted &&
      !spot.isInvalid &&
      spot.location &&
      isPointNearPolyline(spot.location, selectedRoutePlanAlternative.fullPolyline, 2500)
    );
    if (selectedSpotId && !routeSpots.some((spot) => spot.id === selectedSpotId)) {
      const selected = visibleSpots.find((spot) => spot.id === selectedSpotId && !spot.isDeleted && !spot.isInvalid);
      if (selected) return [selected, ...routeSpots];
    }
    return routeSpots;
  }, [routePlan, selectedRoutePlanAlternative, selectedSpotId, showRoutePlanContent, visibleSpots]);

  // Syntetiske CampingSpots avledet fra private waypoints med en gjenkjennelig
  // lag-kategori (Bom, Teltplass, Vann osv.). Dette lar oss rendere brukerens
  // egne loggede plasser med samme ikon og kort som de offentlige lag-spottene,
  // selv om de aldri er delt med fellesskapet.
  //
  // Generiske pin/star-waypoints (uten kategori-match) renders fortsatt via
  // UserContentLayer som personlige punkter — de er ikke "lag-bare".
  const syntheticLayerSpots = useMemo(() => {
    // Mapping av kjente spots — brukes til å skippe visits hvis underliggende
    // spot allerede er lastet (det rendres naturlig av layer-systemet).
    const spotsById = new Map(spots.map(s => [s.id, s]));
    const result: CampingSpot[] = [];

    for (const w of myContent.waypoints) {
      if (w.linkedSpotId) continue;

      const isVisit = w.id.startsWith('visit_');
      if (isVisit) {
        // Visit — synthesiseres så de får proper layer-stil. Hvis underliggende
        // spot er lastet i spots-state, skipp (real spot rendres på sin egen posisjon).
        const visitedId = w.id.slice('visit_'.length);
        if (spotsById.has(visitedId)) continue;
        // Både kategori-matchede og generiske visits synthesiseres — generiske
        // får createCustomIcon-fallback (rosa user-check-ikon), bedre enn småboble.
        result.push(synthesizeSpotFromWaypoint(w));
        continue;
      }

      // Manuelt registrert waypoint — kun de med kategori-match synthesiseres,
      // generiske pin/star vises i UserContentLayer som personlige punkter.
      if (waypointHasLayerCategory(w)) {
        result.push(synthesizeSpotFromWaypoint(w));
      }
    }
    return result;
  }, [myContent.waypoints, spots]);

  const mapDisplaySpots = useMemo(() => {
    const base = showRoutePlanContent ? routePlanSidebarSpots : mapSpots;
    // Eksisterende `wpcard-` IDs i `spots`-state (registrert ved klikk på mini-card)
    // dedupliseres mot syntheticLayerSpots — kilde-of-truth er waypoint-collection.
    // id-guard: id-løse spots (historisk fra seedede cached_spots uten id-felt)
    // krasjet her — «undefined is not an object (evaluating 'me.id.startsWith')»,
    // error_reports v1.0.247 2026-07-16. Kildeguarden i mergeSpotsByIdentity skal
    // stoppe dem, men dette er selve krasjstedet — behold beltet og bukseselene.
    const baseFiltered = base.filter(s => !s.id?.startsWith('wpcard-'));
    return [...baseFiltered, ...syntheticLayerSpots];
  }, [showRoutePlanContent, routePlanSidebarSpots, mapSpots, syntheticLayerSpots]);

  // Slår opp et spot-id i kjent state. wpcard-IDs (syntetiske layer-spots fra
  // private waypoints) registreres lazily i `spots`-state ved første oppslag,
  // slik at MobileSpotSheet og AppOverlays — som bruker `spots.find(...)` —
  // også finner dem.
  const resolveSpotById = useCallback((id: string): CampingSpot | undefined => {
    const fromState = spots.find(s => s.id === id);
    if (fromState) return fromState;
    if (id.startsWith('wpcard-')) {
      const synthetic = syntheticLayerSpots.find(s => s.id === id);
      if (synthetic) {
        setSpots(prev => prev.some(s => s.id === id) ? prev : [synthetic, ...prev]);
        return synthetic;
      }
    }
    return undefined;
  }, [spots, syntheticLayerSpots]);

  /**
   * Trykk på en plassmarkør i kartet. STABIL IDENTITET er et krav, ikke pynt:
   * lå tidligere som inline-arrow på <MapComponent>, som slo ut
   * React.memo(ClusteredSpotMarkers) og dermed fikk react-leaflet til å kalle
   * setIcon() + setLatLng() på hver markør ved hver panorering. Se
   * components/map/markerIconCache.ts.
   */
  const handleMarkerSelectSpot = useEventCallback((id: string | null) => {
    if (!id) {
      setSelectedSpotId(null);
      setShowMobileSheet(false);
      return;
    }
    const s = resolveSpotById(id);
    if (!s) return;
    // Under navigasjon: vis spot i overlay uten å endre kart-zoom/fokus
    if (followActive) {
      if (!isPaidUser && s.spotType === 'algorithmic') {
        nudgeRef.current?.showNudge('algorithmic_spot');
        return;
      }
      setFollowOverrideSpotId(id);
      if (followOverrideTimerRef.current) clearTimeout(followOverrideTimerRef.current);
      followOverrideTimerRef.current = setTimeout(() => {
        setFollowOverrideSpotId(null);
      }, 60_000);
      return;
    }
    if (isDesktop && sidebarMode === 'routes' && selectedRouteId) {
      if (!isPaidUser && s.spotType === 'algorithmic') {
        nudgeRef.current?.showNudge('algorithmic_spot');
        return;
      }
      handleSelectSpot(s);
    } else {
      onMobileSelectSpot(s);
    }
  });

  const draftRouteDetail = useMemo<GpxRoute | null>(() => {
    if (!showRoutePlanContent || !routePlan || !selectedRoutePlanAlternative) return null;

    const routeCoords = selectedRoutePlanAlternative.fullPolyline?.length
      ? selectedRoutePlanAlternative.fullPolyline
      : selectedRoutePlanAlternative.coordinates.map(([longitude, latitude]) => ({ latitude, longitude }));

    const centerPoint = routeCoords[Math.floor(routeCoords.length / 2)] || routePlan.toCoords;
    const wildSpotsCount = routePlanSidebarSpots.filter((spot) => spot.spotType === 'algorithmic' || spot.spotType === 'confirmed' || spot.spotType === 'coastal').length;
    const campsitesCount = routePlanSidebarSpots.filter((spot) => spot.spotType === 'commercial_campsite').length;
    const restAreasCount = routePlanSidebarSpots.filter((spot) => spot.spotType === 'rasteplass').length;
    const chargersCount = routePlanSidebarSpots.filter((spot) =>
      Array.isArray(spot.features) && spot.features.some((feature) => String(feature).toUpperCase().includes('LADING'))
    ).length;
    const attractionsCount = routePlanSidebarSpots.filter((spot) => spot.spotType === 'attraction').length;

    return {
      id: `draft-route-${selectedRoutePlanAlternative.index}`,
      name: routePlan.toName,
      description: `Planlagt rute fra ${routePlanFromName || routePlan.fromName || 'Min posisjon'} til ${routePlan.toName}. Ruten er ikke lagret ennå.`,
      difficulty: 'Easy',
      gpxUrl: '',
      createdBy: currentUser?.uid || 'draft-route',
      createdByName: currentUser?.displayName || 'Fricamp',
      createdByPhoto: currentUser?.photoURL || undefined,
      createdAt: Date.now(),
      status: 'private',
      estimatedTime: formatDuration(selectedRoutePlanAlternative.durationSeconds),
      distance: selectedRoutePlanAlternative.distanceMeters / 1000,
      stats: {
        wildSpotsCount,
        campsitesCount,
        restAreasCount,
        chargersCount,
        attractionsCount,
        wildlifeCount: routePlanWildlifeCount,
        totalSpots: routePlanSidebarSpots.length,
      },
      associatedSpotIds: routePlanSidebarSpots.map((spot) => spot.id),
      center: centerPoint,
      startPlace: routePlanFromName || routePlan.fromName || 'Min posisjon',
      endPlace: routePlan.toName,
      startCoords: routePlan.fromCoords || userPosition || routeCoords[0] || routePlan.toCoords,
      endCoords: routePlan.toCoords,
      suitableCategories: currentUser?.activeVehicleCategory ? [currentUser.activeVehicleCategory] : undefined,
      routeWaypoints: routePlan.waypoints.map((waypoint) => ({
        name: waypoint.name,
        latitude: waypoint.coords.latitude,
        longitude: waypoint.coords.longitude,
        icon: 'pin',
      })),
    } as GpxRoute;
  }, [currentUser, routePlan, routePlanFromName, routePlanSidebarSpots, routePlanWildlifeCount, selectedRoutePlanAlternative, showRoutePlanContent, userPosition]);

  useEffect(() => {
    if (!showRoutePlanContent || !selectedRoutePlanAlternative?.fullPolyline?.length || !draftRouteDetail?.id) {
      setRoutePlanWildlifeCount(0);
      return;
    }

    const routePath = selectedRoutePlanAlternative.fullPolyline.filter(
      (coords) => Number.isFinite(coords.latitude) && Number.isFinite(coords.longitude)
    );
    if (routePath.length < 2) {
      setRoutePlanWildlifeCount(0);
      return;
    }

    handleRouteActivated(routePath, {
      routeId: draftRouteDetail.id,
      expectedWildSpots: 24,
    });

    let cancelled = false;
    setRouteFacilityLoading((prev) => ({
      ...prev,
      routeId: draftRouteDetail.id,
      wildlife: true,
    }));

    fetchWildlifeForRoute(routePath)
      .then((observations) => {
        if (cancelled) return;
        setRoutePlanWildlifeCount(
          observations.filter((obs) => isPointNearPolyline(obs.location, routePath, 300)).length
        );
      })
      .catch((error) => {
        console.warn('Failed to load planned-route wildlife:', error);
        if (!cancelled) setRoutePlanWildlifeCount(0);
      })
      .finally(() => {
        if (cancelled) return;
        setRouteFacilityLoading((prev) => (
          prev.routeId === draftRouteDetail.id
            ? { ...prev, wildlife: false }
            : prev
        ));
      });

    return () => {
      cancelled = true;
    };
  }, [draftRouteDetail?.id, handleRouteActivated, selectedRoutePlanAlternative?.fullPolyline, showRoutePlanContent]);

  // Mini-cards: viewport-filtered spots for horizontal carousel.
  // Inkluderer også private waypoints (uten linket spot) som syntetiske kort,
  // slik at brukeren ser sine egne loggede plasser i karusellen.
  const miniCardSpots = useMemo(() => {
    if (!currentViewBounds) return [];
    const inView = mapSpots.filter(s =>
      !s.isDeleted && !s.isInvalid &&
      s.location.latitude >= currentViewBounds.latMin &&
      s.location.latitude <= currentViewBounds.latMax &&
      s.location.longitude >= currentViewBounds.lonMin &&
      s.location.longitude <= currentViewBounds.lonMax
    );

    // Adapt private-only waypoints (alle, ikke bare layer-kategori) til CampingSpot
    // for karusellen, slik at brukeren alltid finner sine egne loggede plasser.
    // Hopp over visit-bridges og linkede waypoints — de matcher allerede et spot.
    const privateWaypointCards: CampingSpot[] = myContent.waypoints
      .filter(w =>
        !w.linkedSpotId &&
        !w.id.startsWith('visit_') &&
        w.latitude >= currentViewBounds.latMin &&
        w.latitude <= currentViewBounds.latMax &&
        w.longitude >= currentViewBounds.lonMin &&
        w.longitude <= currentViewBounds.lonMax
      )
      .map(w => synthesizeSpotFromWaypoint(w));

    // Brukerens egne private waypoints går FØRST i karusellen (de er viktigst for eieren),
    // deretter spots sortert etter score desc. Limit 30 totalt.
    const sortedSpots = inView.sort((a, b) => (b.score || 0) - (a.score || 0));
    return [...privateWaypointCards, ...sortedSpots].slice(0, 30);
  }, [mapSpots, currentViewBounds, myContent.waypoints]);

  // Filtrer algoritmiske plasser bort fra mini-card-strip for gratisbrukere.
  // De får en "teaser"-kort på slutten av raden som forteller hvor mange
  // gratisplasser de går glipp av i området.
  const { miniCardSpotsForUser, hiddenPremiumCount } = useMemo(() => {
    if (isPaidUser) {
      return { miniCardSpotsForUser: miniCardSpots, hiddenPremiumCount: 0 };
    }
    const visible = miniCardSpots.filter(s => s.spotType !== 'algorithmic');
    return {
      miniCardSpotsForUser: visible,
      hiddenPremiumCount: miniCardSpots.length - visible.length,
    };
  }, [isPaidUser, miniCardSpots]);

  const canReturnToRoute = !!routeReturnContext &&
    (routeReturnContext.routeIds.length > 0 || !!routeReturnContext.selectedRouteId);
  const { isSearchingPhoton, mergedSearchResults } = useAppSearch(searchQuery, spots, routes, searchCountries);

  // --- Auto-check-in: detect nearby spots on app open ---
  const [autoCheckInSpot, setAutoCheckInSpot] = useState<{ spot: CampingSpot; mode: 'checkin' | 'extend' } | null>(null);
  const autoCheckInCheckedRef = useRef(false);

  useEffect(() => {
    if (autoCheckInCheckedRef.current || !currentUser || !userPosition || !spotsReady || spots.length === 0) return;
    // Nærmeste plass innenfor innsjekk-avstand (150 m).
    const nearest = spots
      .filter(s => s.location)
      .map(s => ({ s, dist: getDistance(userPosition.latitude, userPosition.longitude, s.location!.latitude, s.location!.longitude) }))
      .filter(x => x.dist <= 150)
      .sort((a, b) => a.dist - b.dist)[0]?.s;
    // VIKTIG: `_firestoreReady` gater beslutningen. Den foreløpige auth-callbacken
    // mangler `currentCheckIn`, så uten denne guarden ville vi tro at brukeren IKKE
    // er sjekket inn og mase om innsjekk hver app-start — selv når hen alt står
    // innsjekket (også etter innsjekk på en annen enhet). Ved `defer` låser vi
    // heller IKKE «kun én gang»-ref-en, så effekten kjører på nytt når det ekte
    // brukerdokumentet (med currentCheckIn) er lastet.
    const decision = evaluateAutoCheckIn({
      firestoreReady: !!currentUser._firestoreReady,
      hasNearbySpot: !!nearest,
      nearbyDismissed: nearest ? isDismissedRecently(nearest.id) : false,
      nearbySpotId: nearest?.id,
      nearbyOccupiedUntil: nearest?.occupiedUntil,
      currentCheckIn: currentUser.currentCheckIn,
    });
    if (decision === 'defer') return;
    autoCheckInCheckedRef.current = true;
    if (nearest && (decision === 'extend' || decision === 'checkin')) {
      setAutoCheckInSpot({ spot: nearest, mode: decision });
    }
  }, [currentUser, userPosition, spotsReady, spots]);

  const handleAutoCheckIn = async (spotId: string) => {
    setAutoCheckInSpot(null);
    await handleMarkOccupied(spotId, 1, []);
  };

  const handleAutoExtend = async (spotId: string, days: number) => {
    setAutoCheckInSpot(null);
    await handleExtendOccupancy(spotId, days);
  };

  // Vedvarende innsjekk/utsjekk-pill over kartet. To tilstander:
  //  - `checkout`: brukeren er sjekket inn et sted → alltid synlig peker + «Sjekk ut».
  //  - `checkin`: ikke innsjekket, men står ved en innsjekkbar plass (<150 m) → «Sjekk inn».
  // Løser at (a) det manglet et fast utsjekk-holdepunkt etter innsjekk, og (b) etter
  // utsjekk kom ingen ny innsjekk-mulighet (arrival-banneret fyrer kun én gang per økt).
  const checkInPill = useMemo<CheckInOutPillProps | null>(() => {
    if (!currentUser) return null;
    const ci = currentUser.currentCheckIn;
    if (ci?.spotId) {
      const nights = Math.max(1, Math.floor((Date.now() - (ci.checkedInAt || Date.now())) / 86400000) + 1);
      const endLabel = ci.occupiedUntil
        ? t('spot.pill.until', { date: occupancyEndDate(ci.occupiedUntil, getDateLocale()), defaultValue: 'til {{date}}' })
        : '';
      return {
        mode: 'checkout',
        spotName: ci.spotName || '',
        nightLabel: t('spot.pill.night', { n: nights, defaultValue: 'natt {{n}}' }),
        endLabel,
        onOpen: () => { const s = resolveSpotById(ci.spotId); if (s) handleSelectSpot(s); },
        onExtend: (days: number) => { void handleExtendOccupancy(ci.spotId, days); },
        onCheckOut: () => { void handleClearOccupancy(ci.spotId); },
      };
    }
    if (!userPosition) return null;
    const near = findNearbyCheckInSpot(userPosition, spots, { currentUserUid: currentUser.uid });
    if (!near) return null;
    return {
      mode: 'checkin',
      spotName: near.name || '',
      onCheckIn: () => { void handleMarkOccupied(near.id, 1, []); },
    };
  }, [currentUser, userPosition, spots, resolveSpotById, t]);

  // Er mini-kort-stripa synlig nederst? (samme betingelse som selve stripa.) Da må
  // innsjekk/utsjekk-pillen løftes over den så den ikke legger seg oppå kort-raden.
  const miniCardStripVisible = mobileView === 'map'
    && (miniCardSpotsForUser.length > 0 || hiddenPremiumCount > 0)
    && !routePlan && !pendingAnalysisCoords && !sidebarExpanded && trackingStatus === 'idle';

  // Åpne innsjekk-prompten for en plass-id (Android Auto-prompt hvis tilkoblet,
  // ellers bunnkort-banneret). Delt av lokalvarsel-tap, geofence-trigger og AA.
  const triggerCheckInPrompt = useCallback((spotId: string) => {
    if (!spotId) return;
    const spot = spots.find(s => s.id === spotId);
    if (!spot) return;
    isAndroidAutoConnected().then(connected => {
      if (connected) {
        showAutoCheckInPrompt(spot.id, spot.name || '');
      } else {
        setAutoCheckInSpot({ spot, mode: 'checkin' });
      }
    }).catch(() => setAutoCheckInSpot({ spot, mode: 'checkin' }));
  }, [spots]);

  // --- Local Notification Listener for Background Proximity Check-in ---
  useEffect(() => {
    const listener = LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
      if (action.notification.extra?.action === 'check_in') {
        triggerCheckInPrompt(action.notification.extra.spotId);
      }
    });

    return () => {
      listener.then(sub => sub.remove()).catch(() => {});
    };
  }, [triggerCheckInPrompt]);

  // --- Ekte bakgrunns-innsjekk via native OS-geofence (Trinn 2) ---
  // Registrerer 150 m-soner rundt de nærmeste egnede plassene; native fyrer
  // «vil du sjekke inn?» etter opphold selv når appen er lukket. Mens appen lever
  // ruter `checkInTriggered` inn i samme prompt som lokalvarsel-tap.
  useGeofenceCheckIn({
    enabled: GEOFENCE_CHECKIN_ENABLED,
    spots,
    position: userPosition,
    currentUserUid: currentUser?.uid,
    onCheckInTriggered: triggerCheckInPrompt,
  });

  // Konsumer ventende geofence-innsjekk (kald-start via ?checkin=<spotId>, eller
  // warm via appUrlOpen som bumper checkinNonce). Venter til spots er lastet.
  useEffect(() => {
    if (spots.length === 0) return;
    let spotId = pendingCheckinRef.current;
    if (!spotId) {
      spotId = new URLSearchParams(window.location.search).get('checkin');
    }
    if (!spotId) return;
    pendingCheckinRef.current = null;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkin')) {
      params.delete('checkin');
      const cleanUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
    triggerCheckInPrompt(spotId);
  }, [spots, checkinNonce, triggerCheckInPrompt]);

  // Listen for check-in confirmations from Android Auto
  useEffect(() => {
    const cleanup = onAutoCheckInConfirmed((spotId) => {
      handleAutoCheckIn(spotId);
    });
    return () => cleanup?.();
  }, []);

  // --- Auto-navigation mode: idle + moving fast → follow user on map ---
  // Disabled while a route is already being followed (avoid double-follow)
  // and while a tracking session is in 3D follow mode.
  const autoNav = useAutoNavigationMode({
    enabled: !!currentUser && !followActive,
    userPosition,
    currentSpeed: activeSpeed > 0 ? activeSpeed : liveGpsSpeed,
    spots,
  });

  // Holder kjøretilstanden tilgjengelig for useSpotManager, som er kalt lenger opp
  // i filen. Under navigasjon og auto-nav hentes plasser automatisk — man kan ikke
  // kreve et knappetrykk i 80 km/t.
  useEffect(() => {
    isDrivingRef.current = !!followRouteState || autoNav.active || followActive;
  }, [followRouteState, autoNav.active, followActive]);

  // When auto-nav turns on, collapse the sidebar so the map is unobstructed.
  // The actual GPS-follow (edge-aware pan, constant zoom) lives in MapComponent
  // via the autoFollowUser prop — see the follow effect in Map.tsx. Doing it there
  // avoids re-flying the whole map (and refetching spots) on every GPS fix.
  useEffect(() => {
    autoNavActiveRef.current = autoNav.active;
    if (!autoNav.active) return;
    setSidebarExpanded(false);
  }, [autoNav.active]);

  // Abonner på brukerens auto-nav-kandidater (kjappe punkter markert under kjøring).
  useEffect(() => {
    if (!currentUser?.uid) { setAutonavCandidates([]); return; }
    const unsub = subscribeToAutonavCandidates(currentUser.uid, setAutonavCandidates);
    return () => unsub();
  }, [currentUser?.uid]);

  // Lagre en kandidat når brukeren trykker «Fricamp?»-boblen under auto-nav.
  const handleDropCandidate = useCallback(async (coords: Coordinates) => {
    if (!currentUser?.uid) { addToast(t('autonav.candidate.login', 'Logg inn for å markere plasser'), 'info'); return; }
    const id = await addAutonavCandidate(currentUser.uid, coords);
    if (id) addToast(t('autonav.candidate.saved', 'Markert – gå gjennom senere'), 'success');
  }, [currentUser?.uid, addToast, t]);

  // Åpne full gjennomgang (AddSpotModal) forhåndsutfylt på kandidatens punkt.
  const handleReviewCandidate = useCallback((candidate: AutonavCandidate) => {
    reviewingCandidateIdRef.current = candidate.id;
    setShowReviewCandidates(false);
    setPendingAnalysisCoords(candidate.location);
    setShowAddModal(true);
  }, [setPendingAnalysisCoords, setShowAddModal]);

  const handleDiscardCandidate = useCallback(async (candidateId: string) => {
    if (!currentUser?.uid) return;
    await deleteAutonavCandidate(currentUser.uid, candidateId);
  }, [currentUser?.uid]);

  // Rydd «under gjennomgang»-referansen når AddSpotModal lukkes (uansett årsak) slik at
  // en senere, urelatert plass-innsending ikke feilaktig sletter en kandidat.
  useEffect(() => { if (!showAddModal) reviewingCandidateIdRef.current = null; }, [showAddModal]);

  return (
    <PremiumNudgeProvider
      ref={nudgeRef}
      onUpgrade={() => setShowUpgrade(true)}
      onLogin={() => setIsAuthOpen(true)}
      isGuest={!currentUser}
      currentUser={currentUser}
      isPaidUser={isPaidUser}
      isPremiumUser={isPremiumUser}
      hasPremiumAccess={hasPremiumAccess}
      userTier={currentUser?.tier}
    >
      <div className="h-screen w-full overflow-hidden bg-black text-[var(--text-primary)] relative">
        <AndroidAutoBanner
          isConnected={isAutoPluggedIn}
          isNavigating={!!followRouteState}
          isRecording={trackingStatus === 'recording'}
          destinationName={followRouteState?.routeName}
          remainingMeters={followRouteState?.routeTotalDistanceM}
          remainingSeconds={followRouteState ? followRouteState.routeTotalDistanceM / (50 / 3.6) : undefined}
          onStopNavigation={() => setFollowRouteState(null)}
          onClick={() => setShowAARouteSheet(true)}
          hasAccess={hasAutoAccess}
          onUpgrade={() => setShowUpgrade(true)}
        />
        {/* Driving-mode lock: while Android Auto is connected the phone must not
            present interactive UI (Android for Cars guideline "Visual info on
            phone"). Covers the whole app and swallows touch input. Gated on the
            raw plugged-in state (not premium) so the phone locks whenever the
            car is projecting. */}
        <DrivingModeOverlay
          isActive={isAutoPluggedIn}
          isNavigating={!!followRouteState}
          destinationName={followRouteState?.routeName}
          remainingMeters={followRouteState?.routeTotalDistanceM}
        />
        <AndroidAutoRouteSheet
          open={showAARouteSheet && isAutoConnected && hasAutoAccess && !!followRouteState}
          followRouteState={followRouteState}
          progressM={routeFollowResult.progressM}
          isOffRoute={routeFollowResult.isOffRoute}
          spots={spots}
          onClose={() => setShowAARouteSheet(false)}
          onStop={() => setFollowRouteState(null)}
          onSelectSpot={(spot) => handleSelectSpot(spot)}
        />
        <NordicComingSoonBanner country={nordicCountry} onDismiss={dismissNordic} />
        {/* Offline upload queue indicator */}
        {(pendingUploadCount > 0 || uploadQueueProcessing) && (
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[4000] animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] px-4 py-2.5 flex items-center gap-3 min-w-[220px]">
              {uploadQueueProcessing ? (
                <Loader2 size={16} className="text-emerald-400 animate-spin shrink-0" />
              ) : (
                <CloudUpload size={16} className="text-amber-400 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-tiny font-bold text-[var(--text-primary)] leading-tight">
                  {uploadQueueProcessing
                    ? uploadQueueProgress
                      ? `${t('toast.uploading', 'Laster opp')} ${uploadQueueProgress.current}/${uploadQueueProgress.total}...`
                      : t('toast.uploading', 'Laster opp') + '...'
                    : t('toast.images_pending', { count: pendingUploadCount, defaultValue: `${pendingUploadCount} bilde(r) venter` })
                  }
                </p>
                {uploadQueueProcessing && uploadQueueProgress && (
                  <div className="mt-1.5 h-1.5 bg-[var(--bg-input)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand rounded-full transition-[width] duration-500 ease-out"
                      style={{ width: `${Math.max(5, (uploadQueueProgress.current / uploadQueueProgress.total) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
              {!uploadQueueProcessing && navigator.onLine && (
                <button
                  onClick={handleManualQueueRetry}
                  className="shrink-0 px-2.5 py-1.5 rounded-lg bg-brand/20 text-emerald-400 text-2xs font-black uppercase tracking-wide hover:bg-brand/30 transition-colors border border-brand/30"
                >
                  {t('toast.retry_now', 'Last opp')}
                </button>
              )}
              {!uploadQueueProcessing && !navigator.onLine && (
                <span className="shrink-0 text-3xs font-bold text-[var(--text-muted)] uppercase">{t("auto.app.offline", "Offline")}</span>
              )}
            </div>
          </div>
        )}
        {showVehiclePrompt && currentUser && !currentUser.onboarding_vehicle_done && (
          <VehicleOnboardingOverlay
            user={currentUser}
            onComplete={() => {
              setShowVehiclePrompt(false);
              setCurrentUser({ ...currentUser, onboarding_vehicle_done: true });
            }}
          />
        )}
	        {levelUpTier && (
		          <LevelUpModal
		            newTier={levelUpTier}
		            onClose={() => setLevelUpTier(null)}
		            userPhotoURL={currentUser?.photoURL}
		            visitedSpotCount={new Set(visits.map((visit) => visit.spotId)).size}
		          />
	        )}
	        {contributionToast && !levelUpTier && (
	          <ContributionProgressToast
	            state={contributionToast}
	            onClose={() => {
	              if (contributionToastTimerRef.current) {
	                clearTimeout(contributionToastTimerRef.current);
	                contributionToastTimerRef.current = null;
	              }
	              setContributionToast(null);
	            }}
	          />
	        )}
	        {verifyPromptSpot && currentUser && (
	          <VerifyAfterVisitModal
	            spot={verifyPromptSpot}
	            user={currentUser}
	            onClose={() => setVerifyPromptSpot(null)}
	            onVerified={(result) => {
	              showContributionFeedback(
	                'verification',
	                verifyPromptSpot,
	                result.firstPhoto
	                  ? 'Du la inn det aller første bildet av plassen! Nå blir den langt mer attraktiv for andre — og tryggere å stole på.'
	                  : 'Bra verifisering. Nå blir plassen tryggere å stole på for andre, og innsatsen din vises i nivåprogresjonen.',
	                result.firstPhoto ? 60 : 35
	              );
	              updateSpotLocally({ ...verifyPromptSpot, isVerified: true });
	              setVerifyPromptSpot(null);
	            }}
	          />
	        )}
	        {reviewPromptSpot && currentUser && (
	          <VerifyAfterVisitModal
	            spot={reviewPromptSpot}
	            user={currentUser}
	            mode="review"
	            onClose={() => setReviewPromptSpot(null)}
	            onVerified={(result) => {
	              showContributionFeedback(
	                'write_review',
	                reviewPromptSpot,
	                result.firstPhoto
	                  ? 'Takk! Du la inn det aller første bildet av plassen — det gjør den langt mer attraktiv for andre.'
	                  : 'Takk for anmeldelsen! Den hjelper andre å velge plass, og dukker opp i nyhetsfeeden.',
	                result.firstPhoto ? 50 : 25
	              );
	              setReviewPromptSpot(null);
	            }}
	          />
	        )}
	        {campingRatingSpot && currentUser && (
	          <CampingRatingModal
	            spot={campingRatingSpot}
	            user={currentUser}
	            onClose={() => setCampingRatingSpot(null)}
	          />
	        )}
        {sharedReviewId && (
          <SharedReviewModal
            reviewId={sharedReviewId}
            onClose={() => {
              setSharedReviewId(null);
              const url = new URL(window.location.href);
              url.searchParams.delete('openReview');
              window.history.replaceState({}, '', url);
            }}
            onGoToSpot={(spotId) => {
              setSharedReviewId(null);
              const spot = spots.find(s => s.id === spotId);
              if (spot) {
                handleSelectSpot(spot);
                setMapCommand({ center: spot.location, zoom: 16, id: Date.now() });
              }
            }}
          />
        )}
        <OfflineBanner isOffline={isOffline} />

        <div className="absolute inset-0 overflow-hidden">
          {/* Header floats on top of map with glass effect */}
          <Header
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchResults={mergedSearchResults}
            userPosition={userPosition || (mapCenter ? { latitude: mapCenter.latitude, longitude: mapCenter.longitude } : null)}
            onResultClick={(r) => {
              const resClass = String(r.class || '');
              if (resClass.startsWith('spot:')) {
                const spotId = resClass.slice(5);
                const spot = spots.find((s) => s.id === spotId);
                if (spot) {
                  handleSelectSpot(spot);
                  setMapCommand({ center: spot.location, zoom: 16, id: Date.now() });
                  setCoordinatePin(null);
                  return;
                }
              }
              if (resClass.startsWith('route:')) {
                const routeId = resClass.slice(6);
                const route = routes.find((rt) => rt.id === routeId);
                if (route) {
                  setActiveRouteIds(new Set([route.id]));
                  setSelectedRouteId(route.id);
                  setSidebarMode('routes');
                  expandSidebar();
                  if (route.center) {
                    setMapCommand({ center: route.center, zoom: 13, id: Date.now() });
                  } else {
                    const reg = String(route.region || '').toLowerCase();
                    let center = { latitude: 59.9139, longitude: 10.7522 };
                    if (reg.includes('north') || reg.includes('nord')) center = { latitude: 69.6492, longitude: 18.9553 };
                    else if (reg.includes('central') || reg.includes('midt')) center = { latitude: 63.4305, longitude: 10.3951 };
                    setMapCommand({ center, zoom: 10, id: Date.now() });
                  }
                  return;
                }
              }
              const center = { latitude: parseFloat(r.lat), longitude: parseFloat(r.lon) };
              if (!Number.isFinite(center.latitude) || !Number.isFinite(center.longitude)) return;

              // Fornøyelsesparker o.l. POI-er: vis som attraksjons-kort med Google-bilder
              // (gjenbruker den deployede fetchGooglePlacePhotos via en lett attraksjons-spot).
              const POI_PHOTO_TYPES = new Set(['theme_park', 'zoo', 'water_park', 'aquarium']);
              if (POI_PHOTO_TYPES.has(String(r.type))) {
                const parkSpot: CampingSpot = {
                  id: `place-${center.latitude.toFixed(5)}-${center.longitude.toFixed(5)}`,
                  name: (r.display_name || '').split(',')[0].trim() || r.display_name || 'Sted',
                  location: center,
                  spotType: 'attraction',
                  attractionCategory: String(r.type),
                  source: 'osm',
                  sourceLevel: 3,
                  score: 0,
                  features: [],
                  isVerified: true,
                };
                setSpots(prev => prev.some(s => s.id === parkSpot.id) ? prev : [parkSpot, ...prev]);
                setCoordinatePin(null);
                if (selectedRouteId) setSelectedRouteId(null);
                setMapCommand({ center, zoom: 16, id: Date.now() });
                handleSelectSpot(parkSpot);
                (async () => {
                  try {
                    await upsertSpotsToFirebase([parkSpot]);
                    await fetchGooglePhotosForSpot(parkSpot.id);
                  } catch { /* bilder er beste-innsats */ }
                })();
                return;
              }

              setMapCommand({ center, zoom: 14, id: Date.now() });
              setCoordinatePin(center);
              setPendingAnalysisCoords(center);
              setSelectedSpotId(null);
              setShowMobileSheet(false);
              setHighlightedRoad(null);
              setBarrierHighlightSpot(null);
              setDiscoverHighlight(null);
              setDiscoverSelectedId(null);
              if (selectedRouteId) setSelectedRouteId(null);
              setMobileView('map');
            }}
            onCategoryClick={() => { }}
            currentUser={currentUser}
            onAuthClick={() => setIsAuthOpen(true)}
            onShowProfile={() => setIsProfileOpen(true)}
            onOpenStickerBoard={() => setIsStickerBoardOpen(true)}
            onLogout={logoutUser}
            filterUnknownSurface={false}
            onToggleSurfaceFilter={() => { }}
            filterVerified={filterVerified}
            onToggleVerifiedFilter={() => setFilterVerified(prev => !prev)}
            adminBadgeCount={adminBadgeCount}
            onOpenAdmin={() => {
              setMobileAdminInboxOnly(false);
              setAdminTab('spots');
              setShowAdmin(true);
            }}
            onOpenFeedbackInbox={() => {
              if (!currentUser) {
                setIsAuthOpen(true);
                return;
              }
              setShowFeedbackInbox(true);
            }}
            onOpenUpgrade={() => setShowUpgrade(true)}
            isPaidUser={isPaidUser}
            vehicleType={currentUser?.vehicleType || 'standard'}
            onSetVehicleType={(t) => setCurrentUser(p => p ? ({ ...p, vehicleType: t }) : null)}
            isDev={false}
            isSearching={isSearchingPhoton}
            onOpenNotifications={() => setShowNotifications(true)}
            unreadNotificationsCount={mergedUnreadCount}
            onOpenInstallApp={() => setShowInstallApp(true)}
            onOpenInfo={() => setShowLegend(true)}
            onOpenLegal={() => setShowLegal(true)}
            onOpenTierGuide={() => setShowTierGuide(true)}
            onOpenWhatsNew={() => setShowWhatsNew(true)}
            onOpenCopilot={openCopilot}
            copilotEnabled={hasCopilotAccess(currentUser)}
            whatsNewUnread={whatsNewUnread}
            testerApplicationsEnabled={testerApplicationsEnabled}
            onOpenTesterApplication={() => setShowTesterApplication(true)}
            onPlanRoute={(coords, name) => setRoutePlan({ toCoords: coords, toName: name, waypoints: [] })}
            isNavigating={followActive && !isAutoConnected}
            onLogoClick={() => {
              if (userPosition) {
                setMapCommand({ center: userPosition, zoom: MAP_ZOOM_LEVEL, id: Date.now() });
                return;
              }
              addToast('Henter posisjon for å navigere til deg …', 'info');
              gatedGetCurrentPosition(
                (pos) => {
                  const coords = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
                  setLastKnownGpsPosition(coords);
                  setGpsLastUpdated(Date.now());
                  try { localStorage.setItem(LAST_KNOWN_POS_KEY, JSON.stringify(coords)); } catch {}
                  setMapCommand({ center: coords, zoom: MAP_ZOOM_LEVEL, id: Date.now() });
                },
                () => addToast('Kunne ikke hente posisjon', 'error'),
                { enableHighAccuracy: true, timeout: 10000 }
              );
            }}
            isPremiumUser={isPremiumUser}
            hasPremiumAccess={hasPremiumAccess}
          />

          {/* Desktop/tablet sidebar rail — hidden on touch devices during route following */}
          {!(followActive && isTouchDevice) && (
            <SidebarRail
              sidebarMode={sidebarMode}
              panelOpen={sidebarExpanded}
              onSelectMode={(mode) => {
                if (sidebarExpanded && sidebarMode === mode) {
                  setSidebarExpanded(false);
                } else {
                  setSidebarMode(mode);
                  setSidebarExpanded(true);
                }
              }}
              newsCount={newsCount}
              myContentCount={myContentBadgeCount}
              expanded={railExpanded}
              onToggleExpand={() => setRailExpanded(v => !v)}
              copilotEnabled={hasCopilotAccess(currentUser)}
            />
          )}

          <SidebarPanel
            sidebarExpanded={(navigationBuilding || followPreflight) ? false : sidebarExpanded}
            sidebarWidth={sidebarWidth}
            isDesktop={isDesktop}
            isResizingSidebar={isResizingSidebar}
            mobileView={(navigationBuilding || followPreflight) ? 'map' : mobileView}
            railWidth={railWidthPx}
            onSetResizing={setIsResizingSidebar}
            onWidthChange={setSidebarWidth}
            onCloseSidebar={() => setSidebarExpanded(false)}
            onSetMobileView={setMobileView}
          >
            {sidebarMode === 'copilot' ? (
              <CopilotPanel
                copilot={copilot}
                routeStats={copilotRouteStats}
                onShowOnMap={() => setSidebarExpanded(false)}
                onExploreStop={onSelectCopilotStop}
                onExploreAttraction={(a) => { setMapCommand({ center: { latitude: a.latitude, longitude: a.longitude }, zoom: 12, id: Date.now() }); void handleRouteAttractionTap(a); }}
                onStartNavigation={() => {
                  const alt = routePlanAlternatives[routePlanSelectedIndex ?? 0];
                  if (alt) handleNavigateRoutePlanAlternative(alt);
                }}
              />
            ) : sidebarMode === 'mycontent' ? (
              <MyContentInner
                content={myContent.content}
                folders={myContent.folders}
                isPaidUser={isPaidUser}
                userId={currentUser?.uid}
                initialTab={myContentInitialTab}
                isVisible={(sidebarExpanded || mobileView === 'list') && sidebarMode === 'mycontent'}
                onClose={() => {
                  // Panelet har ingen X her (showCloseButton={false}), men onClose er
                  // fortsatt «vis meg kartet»-signalet fra innholdet: trykk på et punkt
                  // flyr kartet og ber så om å komme til det. Var en no-op, så på mobil
                  // ble trykket dødt — kartet fløy bak et panel som lå igjen over det.
                  setSidebarExpanded(false);
                  setMobileView('map');
                }}
                showCloseButton={false}
                spotPhotos={spotPhotos}
                reviewedSpotIds={myContent.reviewedSpotIds}
                collaborativeMaps={collaborativeMaps}
                currentUser={currentUser}
                allSpots={spots}
                allRoutes={routes}
                onWaypointsPublished={(newSpots) => {
                  setSpots(prev => {
                    const existingIds = new Set(prev.map(s => s.id));
                    const additions = newSpots.filter(s => !existingIds.has(s.id));
                    return additions.length ? [...additions, ...prev] : prev;
                  });
                }}
                onSelectSpotFromCollabMap={(spot, fromMapId) => {
                  setCollabReturnContext({ mapId: fromMapId });
                  handleSelectSpot(spot);
                  setMapCommand({ center: spot.location, zoom: 16, id: Date.now() });
                }}
                onSelectRouteFromCollabMap={(routeId, fromMapId) => {
                  setCollabReturnContext({ mapId: fromMapId });
                  handleRouteClick(routeId);
                }}
                onShowCollabMapOnMap={(mapId) => {
                  const map = collaborativeMaps.find(m => m.id === mapId);
                  if (!map) return;
                  let latMin = Infinity, latMax = -Infinity, lonMin = Infinity, lonMax = -Infinity;
                  let hasPoints = false;
                  for (const spotId of map.spotIds) {
                    const spot = spots.find(s => s.id === spotId);
                    if (spot) {
                      latMin = Math.min(latMin, spot.location.latitude);
                      latMax = Math.max(latMax, spot.location.latitude);
                      lonMin = Math.min(lonMin, spot.location.longitude);
                      lonMax = Math.max(lonMax, spot.location.longitude);
                      hasPoints = true;
                    }
                  }
                  for (const routeId of (map.routeIds || [])) {
                    const route = routes.find(r => r.id === routeId);
                    if (route) {
                      for (const c of [route.center, route.startCoords, route.endCoords].filter(Boolean)) {
                        latMin = Math.min(latMin, c!.latitude);
                        latMax = Math.max(latMax, c!.latitude);
                        lonMin = Math.min(lonMin, c!.longitude);
                        lonMax = Math.max(lonMax, c!.longitude);
                        hasPoints = true;
                      }
                    }
                  }
                  if (!hasPoints) return;
                  if (latMin === latMax) { latMin -= 0.005; latMax += 0.005; }
                  if (lonMin === lonMax) { lonMin -= 0.005; lonMax += 0.005; }
                  setMapCommand({
                    center: { latitude: (latMin + latMax) / 2, longitude: (lonMin + lonMax) / 2 },
                    zoom: 14,
                    bounds: [[latMin, lonMin], [latMax, lonMax]],
                    id: Date.now()
                  });
                }}
                onCreateCollabMap={() => {
                  setActiveCollabMapId(null);
                  setAddToMapSpotId(null);
                  setAddToMapRouteId(null);
                  setShowCollabMapModal(true);
                }}
                onFlyToContent={(bounds) => {
                  setMapCommand({
                    center: { latitude: (bounds.latMin + bounds.latMax) / 2, longitude: (bounds.lonMin + bounds.lonMax) / 2 },
                    zoom: 14,
                    bounds: [[bounds.latMin, bounds.lonMin], [bounds.latMax, bounds.lonMax]],
                    id: Date.now()
                  });
                }}
                onShowContentOnMap={setFocusedContent}
                onStartDrawing={(mode) => {
                  drawing.startDrawing(mode);
                  setSidebarExpanded(false);
                }}
                onViewTrack={(track) => handleOpenMyContentTrackAsRoute(track)}
                onShowNudge={(key) => nudgeRef.current?.showNudge(key)}
              />
            ) : (
            <SpotList
              spots={routePlanSidebarSpots}
              allSpots={spots}
              currentViewBounds={currentViewBounds}
              selectedSpotId={selectedSpotId}
              activeHikingTrailId={activeHikingTrailId}
              onSelectSpot={async (id) => {
                let spot = resolveSpotById(id) || visibleSpots.find(s => s.id === id);
                if (!spot) {
                  try {
                    const loaded = await fetchSpotsByIds([id]);
                    if (loaded.length > 0) {
                      spot = loaded[0];
                      setSpots(prev => prev.some(s => s.id === id) ? prev : [loaded[0], ...prev]);
                    }
                  } catch { /* ignore */ }
                }
                if (!spot) return;
                onMobileSelectSpot(spot);
                setMobileView('map');
              }}
              onSelectNewsSpot={onMobileSelectNewsSpot}
              onSelectHikingTrail={setActiveHikingTrailId}
              onRemoveSpot={(id) => { removeSpotLocally(id); setSelectedSpotId(null); setShowMobileSheet(false); }}
              generatingMetaIds={generatingMetaIds}
              onEditSpot={handleEditSpot}
              onResetOverview={() => { }}
              onShowMap={() => { }}
              draftRouteDetail={draftRouteDetail}
              draftRoutePath={selectedRoutePlanAlternative?.fullPolyline || null}
              onCloseDraftRouteDetail={() => {
                setShowRoutePlanContent(false);
                setRoutePlanWildlifeCount(0);
                setRoutePreviewWebcams([]);
                setRouteFacilityLoading({
                  routeId: null,
                  fricamp: false,
                  restAreas: false,
                  campsites: false,
                  attractions: false,
                  chargers: false,
                  wildlife: false
                });
                setSidebarMode('spots');
              }}
              onRouteWebcamsChange={setRoutePreviewWebcams}
              isLoading={isLoading}
              user={currentUser}
              onOpenAuth={() => setIsAuthOpen(true)}
              showFavoritesOnly={sidebarMode === 'favorites'}
              onToggleFavoritesOnly={() => { }}
              favoriteSpotIds={favoriteSpotIds}
              visitedSpotIds={visitedSpotIds}
              visits={visits}
              replayPromoEligible={replayPromoEligible}
              onOpenReplay={() => setIsReplayOpen(true)}
              onOpenStickerBoard={() => setIsStickerBoardOpen(true)}
              collaborativeMaps={collaborativeMaps}
              onToggleFavoriteSpot={handleToggleFavoriteSpot}
              onToggleVisit={handleToggleVisit}
              onAddPastStay={handleAddPastStay}
              onDeletePastStay={handleDeletePastStay}
              onMarkOccupied={handleMarkOccupied}
              onExtendOccupancy={handleExtendOccupancy}
              onClearOccupancy={handleClearOccupancy}
              userPosition={userPosition}
              onAddToMap={handleAddSpotToMap}
              onOpenLegal={() => setShowLegal(true)}
              onOpenSymbolLegend={() => setShowLegend(true)}
              isAnalyzing={isViewportAnalyzing}
              activeAnalysisIds={activeAnalysisIds}
              onReanalyze={reanalyzeSpot}
              isSearchActive={!!searchQuery}
              onClearSearch={() => setSearchQuery('')}
              onSaveSearchResult={(s) => { }}
              sidebarMode={sidebarMode}
              setSidebarMode={setSidebarMode}
              routes={visibleRoutesWithSelection}
              onUploadRoute={() => setShowRouteUpload(true)}
              onRouteStatusChange={(routeId, newStatus) => {
                setRoutes(prev => prev.map(r => r.id === routeId ? { ...r, status: newStatus as any } : r));
              }}
              routeFacilityLoading={routeFacilityLoading}
              activeRouteIds={activeRouteIds}
              setActiveRouteIds={setActiveRouteIds}
              previewRegion={previewRegion}
              setPreviewRegion={handleSetPreviewRegion}
              onPreviewFilteredRouteIds={setPreviewFilteredRouteIds}
              loadingRouteId={loadingRouteId}
              onToggleRoute={onMobileToggleRoute}
              isRegionLoading={isRegionLoading}
              onRefreshRoutes={loadRoutesList}
              onEditRoute={(r) => { setEditingRoute(r); setShowRouteUpload(true); }}
              routeCategoryFilters={new Set()}
              toggleRouteCategoryFilter={() => { }}
              routeVehicleFilterOff={routeVehicleFilterOff}
              onToggleRouteVehicleFilter={() => setRouteVehicleFilterOff(p => !p)}
              mapZoom={mapZoom}
              layers={layers}
              wikiSpots={wikiSpots}
              isWikiLoading={false}
              onFitBounds={(b) => {
                // Keep selected card open while fitBounds animation runs.
                preserveSelectionUntilRef.current = Date.now() + 3000;
                setMapCommand({ center: { latitude: (b[0][0] + b[1][0]) / 2, longitude: (b[0][1] + b[1][1]) / 2 }, zoom: 14, bounds: b, id: Date.now() });
              }}
              onCreateRouteFromSpot={handleCreateRouteFromSpot}
              creatingRouteSpotId={creatingRouteSpotId}
              selectedRouteId={selectedRouteId}
              overlaySpot={
                isDesktop && selectedSpotId && (
                  (sidebarMode === 'routes' && selectedRouteId) || showRoutePlanContent
                )
                  ? (spots.find(s => s.id === selectedSpotId) || null)
                  : null
              }
              onCloseOverlaySpot={() => setSelectedSpotId(null)}
              onCloseRouteDetails={() => {
                setSelectedRouteId(null);
                setSelectedSpotId(null);
                setMobileView('map');
              }}
              onShowOnMap={() => {
                if (showRoutePlanContent && draftRouteDetail) {
                  setSidebarExpanded(false);
                  setMobileView('map');
                  if (draftRouteDetail.center) {
                    setMapCommand({ center: draftRouteDetail.center, zoom: 11, id: Date.now() });
                  }
                  return;
                }
                if (selectedRouteId) {
                  setRouteReturnContext(null);
                  setActiveRouteIds(new Set([selectedRouteId]));
                  // Zoom to the route's center so spots are visible
                  const route = routes.find(r => r.id === selectedRouteId);
                  if (route?.center) {
                    setMapCommand({ center: route.center, zoom: 9, id: Date.now() });
                  }
                }
                setSelectedRouteId(null);
                setPreviewRegion(null);
                setSidebarMode('routes');
                setMobileView('map');
              }}
              onOpenRouteDetails={handleRouteClick}
              onReanalyzeRoute={handleReanalyzeRoute}
              onReanalyzeRouteLayer={handleReanalyzeRouteLayer}
              analyzingRouteIds={analyzingRouteIds}
              isPaidUser={isPaidUser}
              isPremiumUser={isPremiumUser}
              hasPremiumAccess={hasPremiumAccess}
              onOpenUpgrade={() => setShowUpgrade(true)}
              activeFilters={activeFilters}
              onToggleFilter={toggleFilter}
              onClearFilters={clearFilters}
              canReturnToRoute={canReturnToRoute}
              onReturnToRoute={handleBackToRouteFocus}
              canReturnToCollabMap={!!collabReturnContext}
              onReturnToCollabMap={handleBackToCollabMap}
              onAddRouteToMap={handleAddRouteToMap}
              onStartFollowRoute={handleStartFollowRoute}
              onNavigateToRoute={handleNavigateToRoute}
              trackingStatus={trackingStatus}
              onShowNearestBarrier={(spot) => {
                setBarrierHighlightSpot(spot);
                setMobileView('map');
              }}
              onEnsureSpotPersisted={handleEnsureSpotPersisted}
              onNavigateInApp={handleNavigateToSpot}
              followMode={followMode}
              showFollowSummary={showTabletFollowPanel}
              discoverWeather={discoverData.weather}
              discoverAttractions={mergedDiscoverAttractions}
              discoverHeritage={discoverData.heritage}
              discoverLoading={discoverData.loading}
              discoverError={discoverData.error}
              mapCenter={mapCenter}
              discoverSelectedId={discoverSelectedId}
              onDiscoverSelect={(id, lat, lon) => {
                if (discoverSelectedId === id) {
                  setDiscoverSelectedId(null);
                  setDiscoverHighlight(null);
                } else {
                  setDiscoverSelectedId(id);
                  setDiscoverHighlight({ latitude: lat, longitude: lon });
                }
              }}
              onDiscoverFilterChange={setDiscoverHiddenCats}
              followSummary={showTabletFollowPanel ? {
                activeTargetName: followTarget?.name ?? null,
                activeTargetDistanceM: followTarget?.distanceMeters ?? null,
                routeDistanceM: routeFollowResult.distanceFromRoute,
                routeName: followRouteState?.routeName ?? null,
              } : null}
              // `effectiveNordicConfig`, ikke rå `nordicConfig`: admin og aktive
              // testere skal se Danmark/Finland uavhengig av lanseringsbryteren
              // i system/nordic_config. Kartet (useSpotManager) og søket bruker
              // alt den effektive — denne listen var den eneste som fortsatt
              // leste den rå, så dansk aktivitet forsvant ut av feeden for dem
              // som nettopp SKAL se den.
              enabledCountries={effectiveNordicConfig}
            />
            )}
          </SidebarPanel>

          <div className="absolute inset-0">
            {paidBannerVisible && (
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[3300] bg-white/95 px-5 py-3 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-emerald-100/50 flex items-center gap-3 text-sm text-emerald-950 cursor-pointer hover:scale-105 hover:bg-emerald-50/80 transition font-bold w-[90%] md:w-auto max-w-sm justify-center text-center animate-in zoom-in-95" onClick={() => setShowUpgrade(true)}>
                <span>{t("auto.app.du_vil_ikke_fa_se_de_noyaktige", "Lås opp nøyaktige posisjoner med betalt versjon — trykk her")}</span>
              </div>
            )}
            {/* Laster-pille — SØSKEN av søkeknappen, ikke innhold i den.
                Spinneren lå før INNE i knappen, men `runSearch` setter
                `needsManualSearch = false` med én gang, så knappen forsvant i samme
                øyeblikk du trykket — og spinneren med den. Målt i ekte nettleser
                16.08.2026: ingen lasteindikator var synlig ETT eneste øyeblikk av
                ventetiden. Derfor føltes søket som om det ikke gjorde noe.
                Her lever den av `isFetchingBounds` alene og overlever knappen. */}
            {viserLasting && !needsManualSearch && (isDesktop || mobileView === 'map') && !isAutoConnected && (
              <div
                role="status"
                aria-live="polite"
                className="absolute left-1/2 -translate-x-1/2 z-[3350] bg-white/95 backdrop-blur-sm text-stone-900 px-4 py-2 rounded-full shadow-lg border border-stone-200/70 flex items-center gap-2 text-sm font-bold animate-in slide-in-from-top-2 min-h-[44px]"
                style={{ top: 'calc(var(--header-clear) + 8px)' }}
              >
                <i className="fa-solid fa-circle-notch fa-spin text-sm text-brand" />
                <span className="whitespace-nowrap">{t('map.searching_area', 'Henter plasser i området …')}</span>
              </div>
            )}
            {/* «Søk i dette området» — vises når kartet er panorert til geoceller vi
                ikke har hentet. Terskelen er cellebasert, ikke «flyttet i det hele
                tatt», så småjusteringer aldri utløser knappen. Under navigasjon og
                auto-nav hentes plasser automatisk og knappen vises ikke. */}
            {needsManualSearch && (isDesktop || mobileView === 'map') && !isAutoConnected && (
              <button
                type="button"
                onClick={searchHere}
                aria-label={t('map.search_this_area', 'Søk i dette området')}
                className="absolute left-1/2 -translate-x-1/2 z-[3350] bg-brand hover:bg-brand-dark text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 text-sm font-bold animate-in slide-in-from-top-2 min-h-[44px]"
                style={{ top: 'calc(var(--header-clear) + 8px)' }}
              >
                {isFetchingBounds
                  ? <i className="fa-solid fa-circle-notch fa-spin text-sm" />
                  : <i className="fa-solid fa-magnifying-glass text-sm" />}
                <span className="whitespace-nowrap">{t('map.search_this_area', 'Søk i dette området')}</span>
              </button>
            )}
            {!needsManualSearch && !offroadBannerDismissed && offroadBannerShowThisSession === true && (isDesktop || mobileView === 'map') && spotsReady && mapZoom >= 9 && offroadHiddenStats.total >= 30 && layers.algorithmic && !isAutoConnected && (
              <div className="absolute left-1/2 -translate-x-1/2 z-[3300] bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md border border-amber-200/50 flex items-center gap-2 text-xs text-stone-900 w-auto max-w-[calc(100vw-1.5rem)] animate-in slide-in-from-top-2" style={{ top: 'calc(var(--header-clear) + 8px)' }}>
                <span className="text-sm shrink-0">{offroadHiddenIsDanishTractor ? '🚜' : '🌲'}</span>
                <span className="font-bold whitespace-nowrap">{offroadHiddenStats.total} plasser skjult</span>
                {/* I Danmark er markvei ikke et unntak, det er hovedregelen:
                    9 902 av 10 150 plasser (97,6 %) ligger på kjørespor mellom
                    jorder, og tre av fire står inntil dyrket mark. Da er «traktorveg
                    av» en misvisende parentes — brukeren trenger å vite at det
                    han ser er nesten ingenting, og hva han får hvis han skrur på. */}
                <span className="hidden sm:inline text-tiny text-stone-500 truncate">
                  {offroadHiddenIsDanishTractor
                    ? t('map.offroad_banner.dk_markvei', 'nesten alle danske plasser ligger på markvei — vurder hver enkelt på stedet')
                    : `(${[
                      offroadHiddenStats.forest > 0 && 'skogsbilveg',
                      offroadHiddenStats.tractor > 0 && 'traktorveg',
                      offroadHiddenStats.service > 0 && 'serviceveg',
                    ].filter(Boolean).join(', ')} av)`}
                </span>
                <button
                  type="button"
                  onClick={() => setLayers(prev => ({ ...prev, tractorRoads: true, forestRoads: true, serviceRoads: true }))}
                  className="shrink-0 px-2.5 py-1 rounded-full bg-brand-dark hover:bg-emerald-700 text-white text-tiny font-bold transition-colors"
                >
                  {t("auto.app.vis_alle", "Vis alle")}
                </button>
                <button
                  type="button"
                  onClick={handleDismissOffroadBanner}
                  aria-label={t("auto.app.aria-label.lukk", "Lukk")}
                  className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-stone-500 hover:bg-stone-100 hover:text-stone-900 transition-colors"
                >
                  ✕
                </button>
              </div>
            )}
            <MapComponent
              spots={mapDisplaySpots}
              center={mapCenter}
              zoom={mapZoom}
              currentViewBounds={currentViewBounds}
              mapCommand={mapCommand}
              selectedSpotId={selectedSpotId}
              activeHikingTrailId={activeHikingTrailId}
              onSelectSpot={handleMarkerSelectSpot}
              isCoveredByModal={showAddModal}

              onSelectHikingTrail={setActiveHikingTrailId}
              onMapMove={handleMapMove}
              searchCircle={searchCircle}
              onMapClick={handleMapClick}
              onMapLongPress={(coords) => { if (currentUser) setMapCorrectionCoords(coords); }}
              onMapContextMenu={(coords) => {
                setMapContextMenuCoords({ latitude: coords.latitude, longitude: coords.longitude });
              }}
              onSearchThisArea={() => handleScanWithBg()}
              onDeepAnalyzeViewport={handleDeepAnalyzeViewport}
              layers={layers}
              onUpdateLayers={setLayers}
              isLoading={isLoading}
              isFetchingBounds={isFetchingBounds}
              spotsReady={spotsReady}
              isFirstTimeLoad={isFirstTimeLoad}
              scanProgress={scanProgress}
              onOpenAuth={() => setIsAuthOpen(true)}
              isPaidUser={isPaidUser}
              userTier={currentUser?.tier}
              onOpenUpgrade={() => setShowUpgrade(true)}
              showLayerTip={showLayerTip}
              onDismissLayerTip={dismissLayerTip}
              currentUser={currentUser}
              activeAnalysisIds={activeAnalysisIds}
              isDev={false}
              routes={visibleRoutesWithSelection}
              activeRouteIds={activeRouteIds}
              setActiveRouteIds={setActiveRouteIds}
              previewRegion={previewRegion}
              previewFilteredRouteIds={previewFilteredRouteIds}
              onRegionLoading={setIsRegionLoading}
              loadingState={{
                restAreas: loadingRest,
                osmAmenities: loadingPOIBatch,
                commercialCampsites: loadingPOIBatch,
                coastal: loadingCoastal,
                dumpStations: loadingPOIBatch,
                playgrounds: loadingPOIBatch,
                routeFricamp: routeFacilityLoading.fricamp,
                routeRestAreas: routeFacilityLoading.restAreas,
                routeCampsites: routeFacilityLoading.campsites,
                routeChargers: routeFacilityLoading.chargers,
                routeWildlife: routeFacilityLoading.wildlife,
              }}
              errorState={appErrorState}
              wikiSpots={wikiSpots}
              isMobileListOpen={mobileView === 'list'}
              sidebarWidthPx={isDesktop ? (sidebarExpanded ? sidebarWidth + railWidthPx : railWidthPx) : 0}
              highlightedGeoJson={highlightedRoad}
              draftRouteGeoJson={routeBuilderSegments}
              onSelectRoute={handleRouteClick}
              topUiOffsetPx={showAdminReevaluateCta ? 88 : 0}
              shareContextText={searchQuery}
              onRouteActivated={handleRouteActivated}
              onLoadCachedRouteSpots={handleLoadCachedRouteSpotsByIds}
              trackPoints={trackPoints}
              trackingStatus={trackingStatus}
              onStartTracking={handleStartTrackingWithSnowCheck}
              isFollowingRoute={followActive && !isAutoConnected}
              followRouteCoords={followRouteState?.routeCoords ?? null}
              followRouteNearestIndex={routeFollowResult.nearestRouteIndex}
              onOpenMyTracks={() => setShowMyTracks(true)}
              userPosition={userPosition}
              autoFollowUser={autoNav.active}
              onDropCandidate={handleDropCandidate}
              autonavCandidates={autonavCandidates}
              userHeading={activeHeading}
              trailerHeading={trailerHeading}
              gpsLastUpdated={gpsLastUpdated}
              onGpsPositionAcquired={(coords) => {
                setLastKnownGpsPosition(coords);
                setGpsLastUpdated(Date.now());
                try { localStorage.setItem(LAST_KNOWN_POS_KEY, JSON.stringify(coords)); } catch {}
              }}
              nearbyUsers={nearbyUsers}
              isSharing={isSharing}
              onStartSharing={(groupId: string, groupName: string) => startSharing(groupId, groupName)}
              onStopSharing={stopSharing}
              shareError={shareError}
              collaborativeMaps={collaborativeMaps}
              activeGroupId={activeGroupId}
              activeGroupName={activeGroupName}
              groupViewers={groupViewers}
              myContentLines={myContent.lines}
              myContentAreas={myContent.areas}
              myContentWaypoints={myContent.waypoints}
              focusedWaypoints={focusedContent.filter((c: any) => c.type === 'waypoint')}
              drawingState={drawing.state}
              drawingToolType={drawing.toolType}
              drawingPoints={drawing.points}
              onDrawingMapClick={handleDrawingMapClick}
              onDeleteContent={handleDeleteContentItem}
              onEditContent={(id) => {
                const item = myContent.content.find(c => c.id === id);
                if (item && (item.type === 'line' || item.type === 'area')) {
                  drawing.startEditing(id, (item as any).coordinates, item.type);
                }
              }}
              drawingEditPoints={drawing.state === 'editing' ? drawing.points : undefined}
              drawingEditToolType={drawing.state === 'editing' ? drawing.toolType : undefined}
              onDrawingEditUpdatePoint={(idx: number, coords: Coordinates) => drawing.updateEditPoint(idx, coords.latitude, coords.longitude)}
              onDrawingEditInsertPoint={(idx: number, coords: Coordinates) => drawing.insertEditPoint(idx, coords.latitude, coords.longitude)}
              onDrawingEditRemovePoint={drawing.removeEditPoint}
              isMyContentOpen={showMyContent || (isDesktop && sidebarMode === 'mycontent' && sidebarExpanded)}
              coordinatePin={coordinatePin}
              onClearCoordinatePin={() => setCoordinatePin(null)}
              is3DMode={is3DMode && !isAutoConnected}
              onToggle3D={() => {
                // Hard guard: 3D is forbidden while Android Auto owns the
                // nav surface — the phone stays in browse-only mode.
                if (isAutoConnected) return;
                setIs3DMode(v => !v);
                setFollowViewMode(prev => prev === '2d' ? '3d' : '2d');
                if (userPosition) {
                  setMapCenter(userPosition);
                  setMapCommand({ center: userPosition, zoom: mapZoom, id: Date.now() });
                }
              }}
              onToggleRecorder={() => setTrackingPanelSnap(prev => prev === 'mini' ? 'peek' : 'mini')}
              recorderVisible={trackingPanelSnap !== 'mini'}
              followMapOrientation={followMapOrientation}
              onToggleFollowMapOrientation={() => setFollowMapOrientation(prev => prev === 'north_up' ? 'heading_up' : 'north_up')}
              nearestRoutePoint={routeFollowResult.nearestRoutePoint}
              activeFollowTarget={followTarget ? {
                id: followTarget.id,
                location: followTarget.location,
                name: followTarget.name,
                source: followTarget.source,
                spotId: followTarget.spotId,
              } : null}
              nordicEnabledCountries={effectiveNordicConfig}
              nordicConfigReady={nordicConfigReady}
              isAdmin={isAdmin}
              adminBadgeCount={adminBadgeCount}
              onOpenAdmin={() => {
                setMobileAdminInboxOnly(false);
                setShowAdmin(true);
              }}
              onOpenAdminInbox={() => {
                setMobileAdminInboxOnly(true);
                setAdminTab('testers');
                setShowAdmin(true);
              }}
              authReady={authReady}
              routePlanAlternatives={routePlanAlternatives}
              routePlanSelectedIndex={routePlanSelectedIndex}
              onRoutePlanSelectIndex={setRoutePlanSelectedIndex}
              isRoutePlanning={!!routePlan}
              onAddRouteStop={handleAddRouteStop}
              routePlanStartCoords={routePlan?.fromCoords || userPosition || undefined}
              routePlanEndCoords={routePlan?.toCoords || undefined}
              routePlanStopCoords={routePlan?.waypoints.map(w => w.coords) || []}
              routeAttractions={routePlan && copilot.planResult ? copilot.planResult.attractions : undefined}
              onRouteAttractionTap={handleRouteAttractionTap}
              onRemoveRouteStop={handleRemoveRouteStop}
              onSelectRouteStop={copilot.planResult ? (i) => {
                const s = copilot.planResult!.stops[i];
                if (s) onSelectCopilotStop(s);
              } : undefined}
              barrierHighlightSpot={barrierHighlightSpot}
              onClearBarrierHighlight={() => setBarrierHighlightSpot(null)}
              selectedRouteId={selectedRouteId}
              onPromoteBathingPlace={handlePromoteBathingPlace}
              routeHighlightWebcams={routePreviewWebcams}
              onEnsureSpotPersisted={handleEnsureSpotPersisted}
              discoverItems={layers.discoverAttractions ? [...mergedDiscoverAttractions, ...discoverData.heritage].filter(p => !discoverHiddenCats.has(p.category)) : undefined}
              discoverHighlight={discoverHighlight}
              onClearHighlight={() => { setDiscoverHighlight(null); setDiscoverSelectedId(null); }}
              onContextualAlertsChange={setContextualAlerts}
              nearestSpot={followActive ? (followTargetSpot ?? null) : null}
              nearestSpotDistanceM={followActive ? (followTarget?.distanceMeters ?? null) : null}
              onShowSpotDetails={(id) => { const s = mapSpots.find(sp => sp.id === id); if (s) handleSelectSpotDetails(s, { openMobileSheet: true }); }}
              onDownloadSpotArea={async (spotId) => {
                try {
                  const spot = spots.find(s => s.id === spotId);
                  if (!spot?.location) return;
                  const pad = 0.01;
                  const dlBounds: MapBounds = {
                    latMin: spot.location.latitude - pad,
                    latMax: spot.location.latitude + pad,
                    lonMin: spot.location.longitude - pad,
                    lonMax: spot.location.longitude + pad,
                  };
                  const { downloadTiles, saveOfflineArea } = await import('./services/tileDownloaderService');
                  const area = {
                    id: `spot-${Date.now()}`,
                    name: spot.name || 'Spot-område',
                    createdAt: Date.now(),
                    bounds: dlBounds,
                    zoomMin: 13,
                    zoomMax: 16,
                    layers: ['NORWAY_TOPO' as const, 'SATELLITE' as const],
                    tileCount: 0,
                    estimatedBytes: 0,
                    status: 'pending' as const,
                  };
                  const ac = new AbortController();
                  await downloadTiles(area, () => {}, ac.signal);
                  saveOfflineArea({ ...area, status: 'complete' as const });
                  addToast('Kart lastet ned for dette området', 'success');
                } catch (err: any) {
                  if (err?.name !== 'AbortError') addToast('Nedlasting feilet', 'error');
                }
              }}
              onCreateFromPhoto={handleCreateFromPhoto}
              onDiscoverMarkerClick={(id, lat, lon) => {
                if (discoverSelectedId === id) {
                  setDiscoverSelectedId(null);
                  setDiscoverHighlight(null);
                } else {
                  setDiscoverSelectedId(id);
                  setDiscoverHighlight({ latitude: lat, longitude: lon });
                  if (sidebarMode !== 'discover') setSidebarMode('discover');
                  if (!sidebarExpanded) setSidebarExpanded(true);
                }
              }}
              onRequestCountry={(country) => {
                if (!currentUser) {
                  setIsAuthOpen(true);
                  return;
                }
                recordUserCountryRequest(currentUser.uid, country).catch(() => {});
                setFeedbackPrefill(`Jeg ønsker at Fricamp utvides til ${country}! 🌍\n\n`);
                setShowFeedbackInbox(true);
              }}
            />

            {is3DMode && !isAutoConnected && (
              <Suspense fallback={<div className="absolute inset-0 z-[500] bg-[var(--bg-surface)] flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={32} /></div>}>
                <Map3DView
                  center={mapCenter}
                  zoom={mapZoom}
                  spots={mapDisplaySpots}
                  selectedSpotId={selectedSpotId}
                  activeFollowTarget={followTarget?.location ?? null}
                  followPosition={userPosition}
                  followHeading={activeHeading}
                  trailerHeading={trailerHeading}
                  isFollowingMode={followActive && !isAutoConnected}
                  followMapOrientation={followMapOrientation}
                  followRouteCoords={followRouteState?.routeCoords ?? null}
                  followRouteNearestIndex={routeFollowResult.nearestRouteIndex}
                  nearestSpot={followTargetSpot ?? null}
                  nearestSpotDistanceM={followTarget?.distanceMeters ?? null}
                  activeRouteCoords={activeRouteGeometry ?? (routeBuilderSegments.length > 0
                    ? routeBuilderSegments.flatMap((seg: any) =>
                        (seg.geometry?.coordinates || []).map(([lon, lat]: [number, number]) => ({ latitude: lat, longitude: lon }))
                      )
                    : null)}
                  routePlanAlternatives={routePlanAlternatives}
                  routePlanSelectedIndex={routePlanSelectedIndex}
                  isRoutePlanning={!!routePlan}
                  onSelectSpot={(id) => {
                    const s = resolveSpotById(id);
                    if (s) {
                      // During follow mode: show tapped spot in overlay card (no zoom change)
                      if (followActive) {
                        if (!isPaidUser && s.spotType === 'algorithmic') {
                          nudgeRef.current?.showNudge('algorithmic_spot');
                          return;
                        }
                        // Set override — auto-returns to nearest after 60s
                        setFollowOverrideSpotId(id);
                        if (followOverrideTimerRef.current) clearTimeout(followOverrideTimerRef.current);
                        followOverrideTimerRef.current = setTimeout(() => {
                          setFollowOverrideSpotId(null);
                        }, 60_000);
                        return;
                      }
                      if (isDesktop && sidebarMode === 'routes' && selectedRouteId) {
                        if (!isPaidUser && s.spotType === 'algorithmic') {
                          nudgeRef.current?.showNudge('algorithmic_spot');
                          return;
                        }
                        handleSelectSpot(s);
                      } else {
                        onMobileSelectSpot(s);
                      }
                    }
                  }}
                  onMapMove={(coords, zoom) => {
                    setMapCenter(coords);
                    setMapZoom(zoom);
                    setMapCommand({ center: coords, zoom, id: Date.now() });
                  }}
                  onClose={() => {
                    setIs3DMode(false);
                    setFollowViewMode('2d');
                    if (userPosition) {
                      setMapCenter(userPosition);
                      setMapCommand({ center: userPosition, zoom: mapZoom, id: Date.now() });
                    }
                  }}
                  basemapStyle={basemap3D}
                  onChangeBasemap={setBasemap3D}
                  highlightCoord={discoverHighlight}
                  onClearHighlight={() => { setDiscoverHighlight(null); setDiscoverSelectedId(null); }}
                  vehicleMarkerEnabled={currentUser?.vehicleMarkerEnabled}
                  vehicleMarkerCategory={currentUser?.activeVehicleCategory}
                  vehicleMarkerPhotoUrl={currentUser?.activeVehiclePhotoUrl}
                  vehicleMarkerAiAvatarSvg={currentUser?.activeVehicleAiAvatarSvg}
                  vehicleMarkerColor={currentUser?.vehicleMarkerColor}
                  activeVehicleHasTow={currentUser?.activeVehicleHasTow}
                />
              </Suspense>
            )}

            {mustRotateForFollow && (
              <div className="absolute inset-0 z-[4300] bg-black/70 backdrop-blur-sm flex items-center justify-center px-6">
                <div className="max-w-sm w-full rounded-3xl border border-white/20 bg-white/95 text-center px-6 py-7 shadow-2xl">
                  <div className="text-[12px] font-black uppercase tracking-widest text-stone-500 mb-2">{t("auto.app.tablet_modus", "Tablet modus")}</div>
                  <div className="text-[20px] font-black text-stone-800 leading-tight">{t("auto.app.roter_enheten_horisontalt", "Roter enheten horisontalt")}</div>
                  <div className="text-[13px] text-stone-600 mt-2">{t("auto.app.navigasjon_pa_tablet_krever_la", "Navigasjon pa tablet krever landscape for kart og meny.")}</div>
                </div>
              </div>
            )}

            {showTabletFollowPanel && (
              <TabletFollowPanel
                mode={followMode ?? 'route_follow'}
                routeName={followRouteState?.routeName || 'Kjorevisning'}
                progressM={routeFollowResult.progressM}
                totalDistanceM={followRouteState?.routeTotalDistanceM ?? 0}
                distanceFromRoute={routeFollowResult.distanceFromRoute}
                nearestPointDistanceAlongM={routeFollowResult.nearestPointDistanceAlongM}
                nextPois={routeFollowResult.nextPois}
                activeTargetName={followTarget?.name ?? null}
                activeTargetDistanceM={followTarget?.distanceMeters ?? null}
                mapOrientationMode={followMapOrientation}
                onToggleMapOrientation={() => setFollowMapOrientation(prev => prev === 'north_up' ? 'heading_up' : 'north_up')}
                viewMode={followViewMode}
                onToggleViewMode={() => {
                  setFollowViewMode(prev => prev === '2d' ? '3d' : '2d');
                  if (userPosition) {
                    setMapCenter(userPosition);
                    setMapCommand({ center: userPosition, zoom: mapZoom, id: Date.now() });
                  }
                }}
                isOffRoute={routeFollowResult.isOffRoute}
                contextualAlerts={contextualAlerts}
                onAddWaypoint={handleAddTrackWaypoint}
                waypointCount={trackWaypoints.length}
                elevationProfile={trackPointsToElevationProfile(trackPoints)}
              />
            )}

            {/* Navigasjons-HUD: mobil topp-overlay under aktiv navigasjon.
                Skjules på tablet landscape (TabletFollowPanel dekker) og når
                Android Auto er aktiv (AndroidAutoBanner viser status i toppen
                og bilen styrer selve navigasjonen — telefonen skal være ledig). */}
            {trackingStatus !== 'idle' && !isDesktop && followActive && !showTabletFollowPanel && !isAutoConnected && (trackingPanelSnap === 'mini' || trackingPanelSnap === 'peek') && (
              <NavigationHUD
                userPosition={userPosition}
                targetLocation={followTarget?.location ?? null}
                targetName={followTarget?.name ?? followRouteState?.routeName ?? ''}
                distanceToTargetM={followTarget?.distanceMeters ?? null}
                speedKmh={trackStats.currentSpeed !== null ? trackStats.currentSpeed * 3.6 : null}
                isOffRoute={routeFollowResult.isOffRoute}
                distanceFromRouteM={routeFollowResult.distanceFromRoute}
                nearestRoutePoint={routeFollowResult.nearestRoutePoint}
                userHeading={activeHeading}
                progressPercent={routeFollowResult.progressPercent}
                nextPoi={routeFollowResult.nextPois[0] ?? null}
                nextPoiDistanceM={routeFollowResult.nextPois[0]
                  ? (routeFollowResult.progressM !== null
                    ? routeFollowResult.nextPois[0].distanceAlongRoute - routeFollowResult.progressM
                    : null)
                  : null}
              />
            )}

            {/* Turlogg: aktiv sporing-panel.
                Skjules helt når Android Auto er aktiv — AA-pillen i toppen
                viser allerede nødvendig nav/opptak-status, og denne panelet
                ville bare blokkere brukerens normale appfunksjonalitet. */}
            {trackingStatus !== 'idle' && !isAutoConnected && (
              <TrackingPanel
                status={trackingStatus}
                stats={trackStats}
                pointCount={trackPoints.length}
                onPause={pauseTracking}
                onResume={resumeTracking}
                onStop={handleStopAndSaveTrack}
                onDiscard={() => { resetTracking(); setFollowRouteState(null); }}
                gpsError={gpsError}
                elevationProfile={trackPointsToElevationProfile(trackPoints)}
                onAddWaypoint={handleAddTrackWaypoint}
                waypointCount={trackWaypoints.length}
                routeFollow={followActive && followRouteState && !isAutoConnected ? {
                  routeName: followRouteState.routeName,
                  progressPercent: routeFollowResult.progressPercent,
                  distanceFromRoute: routeFollowResult.distanceFromRoute,
                  isOffRoute: routeFollowResult.isOffRoute,
                  nextPois: routeFollowResult.nextPois,
                  nearbyPoi: routeFollowResult.nearbyPoi,
                  totalDistanceM: followRouteState.routeTotalDistanceM,
                  totalDurationS: followRouteState.routeTotalDurationS,
                  progressM: routeFollowResult.progressM,
                  nearestRoutePoint: routeFollowResult.nearestRoutePoint,
                  userPosition,
                } : undefined}
                nearestSpot={followTargetSpot ?? undefined}
                nearestSpotDistanceM={followTarget?.distanceMeters ?? undefined}
                onSelectSpot={(id) => {
                  const spot = resolveSpotById(id);
                  if (spot) handleSelectSpotDetails(spot, { openMobileSheet: true });
                }}
                onShowSpotDetails={(id) => {
                  const spot = resolveSpotById(id);
                  if (spot) {
                    if (isDesktop) {
                      handleSelectSpotDetails(spot, { openMobileSheet: false });
                      setSidebarExpanded(true);
                    } else {
                      handleSelectSpotDetails(spot, { openMobileSheet: true });
                      // Auto-lukk etter 30s inaktivitet under navigasjon
                      if (followSheetAutoCloseRef.current) clearTimeout(followSheetAutoCloseRef.current);
                      followSheetAutoCloseRef.current = setTimeout(() => {
                        setShowMobileSheet(false);
                        setSelectedSpotId(null);
                      }, 30_000);
                    }
                  }
                }}
                onDownloadRoute={followActive && followRouteState ? async () => {
                  const coords = followRouteState.routeCoords;
                  if (!coords || coords.length < 2) return;
                  const features = [{ type: 'Feature' as const, geometry: { type: 'LineString' as const, coordinates: coords.map(c => [c.longitude, c.latitude]) }, properties: {} }];
                  const gpxString = geoJsonToGpx(features, followRouteState.routeName || 'Rute');
                  const fileName = `${(followRouteState.routeName || 'rute').replace(/\s+/g, '_')}.gpx`;
                  const blob = new Blob([gpxString], { type: 'application/gpx+xml' });

                  if (Capacitor.isNativePlatform() && navigator.share) {
                    try {
                      const file = new File([blob], fileName, { type: 'application/gpx+xml' });
                      await navigator.share({ files: [file], title: followRouteState.routeName || 'Rute' });
                      addToast('Rute delt som GPX', 'success');
                    } catch (e: any) {
                      if (e?.name !== 'AbortError') addToast('Kunne ikke dele ruten', 'error');
                    }
                  } else {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = fileName;
                    a.click(); URL.revokeObjectURL(url);
                    addToast('Rute lastet ned som GPX', 'success');
                  }
                } : undefined}
                sidebarWidthPx={sidebarExpanded ? sidebarWidth + railWidthPx : (followActive && isTouchDevice && !isAutoConnected) ? 0 : railWidthPx}
                isDesktop={isDesktop}
                hideNearestSpotCard={followActive && !isAutoConnected}
                contextualAlerts={contextualAlerts}
                forcePeek={showTabletFollowPanel || (isTouchDevice && followActive && !isAutoConnected)}
                onSnapChange={setTrackingPanelSnap}
                requestedSnap={trackingPanelSnap}
              />
            )}

            {/* Quick-add FAB under navigasjon — mobil (skjules på tablet landscape der TabletFollowPanel har waypoint-knapper) */}
            {trackingStatus === 'recording' && !isDesktop && !showTabletFollowPanel && trackingPanelSnap !== 'mini' && (
              <QuickAddFAB
                onAddWaypoint={handleAddTrackWaypoint}
                onAddPhotoWaypoint={handleAddPhotoWaypoint}
                isUploadingPhoto={isUploadingWaypointPhoto}
              />
            )}

            {/* Mini rute-oversikt — alle enheter under navigasjon */}
            {followActive && followRouteState?.routeCoords && !is3DMode && (
              <RouteOverviewMini
                routeCoords={followRouteState.routeCoords}
                nearestIndex={routeFollowResult.nearestRouteIndex}
                userPosition={userPosition}
                compact={isTouchDevice}
                isLandscape={isLandscapeOrientation || (!isTouchDevice && window.innerWidth > window.innerHeight)}
                bottomOffsetPx={isTouchDevice ? (trackingPanelSnap === 'mini' ? 56 : trackingPanelSnap === 'peek' ? 100 : 180) : 100}
              />
            )}

            {/* Desktop/tablet: flytende spot-overlegg under navigasjon (skjult på mobil — bruker mini-card) */}
            {followActive && isDesktop && followDisplaySpot && !is3DMode && !sidebarExpanded && !(isTouchDevice && !isTabletDevice) && (
              <FollowSpotOverlay
                spot={followDisplaySpot}
                distanceM={
                  isFollowOverride
                    ? (userPosition && followDisplaySpot.location
                        ? getDistance(userPosition.latitude, userPosition.longitude, followDisplaySpot.location.latitude, followDisplaySpot.location.longitude)
                        : null)
                    : (followTarget?.distanceMeters ?? null)
                }
                isUserOverride={isFollowOverride}
                onNavigateExternal={(lat, lon) => openExternalNavigation(lat, lon)}
                onCreateRoute={handleCreateRouteFromSpot}
                onShare={async (spot) => {
                  const shareUrl = `https://app.fricamp.com/share/spot/${spot.id}`;
                  if (navigator.share) {
                    try { await navigator.share({ title: spot.name, url: shareUrl }); } catch {}
                  } else {
                    try { await navigator.clipboard.writeText(shareUrl); addToast('Lenke kopiert'); } catch {}
                  }
                }}
                onSelectSpot={(id) => {
                  setFollowOverrideSpotId(null);
                  const spot = resolveSpotById(id);
                  if (spot) {
                    handleSelectSpotDetails(spot, { openMobileSheet: false });
                    setSidebarExpanded(true);
                  }
                }}
                userPosition={userPosition}
              />
            )}

            {/* Nearest-spot pill — vises når FollowSpotOverlay ikke viser noe (ingen plass innen 3km) */}
            {followActive && isDesktop && !followDisplaySpot && nearestRouteSpot && !is3DMode && !sidebarExpanded && !(isTouchDevice && !isTabletDevice) && (
              <button
                onClick={() => setFollowOverrideSpotId(nearestRouteSpot.spot.id)}
                className="absolute z-[2500] top-[96px] left-[72px] pointer-events-auto flex items-center gap-2.5 px-4 py-2.5 rounded-2xl bg-[var(--bg-primary)]/95 backdrop-blur-xl border border-[var(--border-primary)] shadow-2xl hover:bg-[var(--bg-surface-alt)] transition active:scale-95 group"
                style={{ maxWidth: 'min(340px, calc(100vw - 120px))' }}
              >
                <div className="w-8 h-8 rounded-xl bg-brand/15 border border-brand/30 flex items-center justify-center shrink-0">
                  <MapPin size={14} className="text-brand" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <span className="text-2xs font-black text-[var(--text-muted)] uppercase tracking-widest block">{t("auto.app.naermeste_plass", "Nærmeste plass")}</span>
                  <span className="text-[12px] font-bold text-[var(--text-primary)] truncate block">
                    {nearestRouteSpot.spot.name || 'Ukjent plass'}
                  </span>
                </div>
                <span className="text-tiny font-bold text-brand shrink-0">
                  {nearestRouteSpot.distance < 1000
                    ? `${Math.round(nearestRouteSpot.distance)}m`
                    : `${(nearestRouteSpot.distance / 1000).toFixed(1)}km`
                  }
                </span>
              </button>
            )}

            {/* Mobil spot-kort rendres av TrackingPanel (nearestSpot + onShowSpotDetails) */}

            {/* Detaljer-visning rendres på toppnivå — se slutten av return */}

            {/* Turlogg: lagrings-feil-melding */}
            {trackSaveError && (
              <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-[4000] bg-red-600/95 text-white text-tiny font-bold px-4 py-2 rounded-2xl shadow-xl flex items-center gap-2">
                {trackSaveError}
                <button onClick={() => setTrackSaveError(null)} className="ml-1 text-red-200 hover:text-white">✕</button>
              </div>
            )}


            {showPackingList && (
              <PackingListModal onClose={() => setShowPackingList(false)} currentUser={currentUser} />
            )}

            {(saveToMyContentSpot || saveToMyContentRoute) && currentUser?.uid && (
              <SaveToMyContentModal
                spot={saveToMyContentSpot ?? undefined}
                route={saveToMyContentRoute ?? undefined}
                userId={currentUser.uid}
                isPaidUser={isPaidUser}
                folders={myContent.folders}
                collaborativeMaps={collaborativeMaps}
                onClose={() => { setSaveToMyContentSpot(null); setSaveToMyContentRoute(null); }}
                onSaved={() => {
                  setSaveToMyContentSpot(null);
                  setSaveToMyContentRoute(null);
                  addToast(t('myContent.saved'), 'success');
                }}
              />
            )}

            {mapCorrectionCoords && currentUser?.uid && (
              <MapCorrectionModal
                coords={mapCorrectionCoords}
                userId={currentUser.uid}
                onClose={() => setMapCorrectionCoords(null)}
              />
            )}

            {mapContextMenuCoords && (
              <MapContextMenuModal
                coords={mapContextMenuCoords}
                onClose={() => setMapContextMenuCoords(null)}
                onDirectionsHere={() => {
                  setRoutePlan({
                    toCoords: mapContextMenuCoords,
                    toName: mapContextMenuCoords.name || `${mapContextMenuCoords?.latitude?.toFixed(4)}, ${mapContextMenuCoords?.longitude?.toFixed(4)}`,
                    waypoints: []
                  });
                  setMapContextMenuCoords(null);
                }}
                onStartRouteHere={() => {
                  const name = mapContextMenuCoords.name || `${mapContextMenuCoords?.latitude?.toFixed(4)}, ${mapContextMenuCoords?.longitude?.toFixed(4)}`;
                  if (routePlan) {
                    setRoutePlan(prev => prev ? { ...prev, fromCoords: mapContextMenuCoords, fromName: name } : prev);
                  } else {
                    setRoutePlan({
                      fromCoords: mapContextMenuCoords,
                      fromName: name,
                      toCoords: mapContextMenuCoords,
                      toName: 'Velg destinasjon...',
                      waypoints: []
                    });
                  }
                  setMapContextMenuCoords(null);
                }}
                onAddAsStop={routePlan ? () => {
                  const name = mapContextMenuCoords.name || `${mapContextMenuCoords?.latitude?.toFixed(4)}, ${mapContextMenuCoords?.longitude?.toFixed(4)}`;
                  setRoutePlan(prev => prev ? { ...prev, waypoints: [...prev.waypoints, { coords: mapContextMenuCoords, name }] } : prev);
                  setMapContextMenuCoords(null);
                } : undefined}
                onSaveSpot={() => {
                  if (!currentUser) { setIsAuthOpen(true); setMapContextMenuCoords(null); return; }
                  setPendingAnalysisCoords(mapContextMenuCoords);
                  setShowAddModal(true);
                  setMapContextMenuCoords(null);
                }}
                onExploreNearby={() => {
                  setMapCommand({ center: mapContextMenuCoords, zoom: 16, id: Date.now() });
                  setMapContextMenuCoords(null);
                }}
                onSharePosition={() => {
                  const url = `${window.location.origin}/?lat=${mapContextMenuCoords.latitude.toFixed(5)}&lon=${mapContextMenuCoords.longitude.toFixed(5)}`;
                  navigator.clipboard.writeText(url);
                  addToast(t('map.position_copied', 'Posisjonslenke kopiert til utklippstavlen!'), 'success');
                  setMapContextMenuCoords(null);
                }}
              />
            )}

            {showMyTracks && currentUser?.uid && (
              <MyTracksModal
                userId={currentUser.uid}
                user={currentUser}
                onClose={() => setShowMyTracks(false)}
                onViewTrack={(track) => {
                  setShowMyTracks(false);
                  return handleOpenMyContentTrackAsRoute({
                    id: `legacy_track_${track.id}`,
                    userId: track.userId,
                    type: 'track',
                    name: track.name,
                    color: '#10b981',
                    distance: track.distance,
                    duration: track.duration,
                    elevationGain: track.elevationGain,
                    elevationLoss: track.elevationLoss,
                    pointCount: track.pointCount,
                    gpxUrl: track.gpxUrl,
                    startLocation: track.startLocation,
                    linkedRouteId: track.linkedRouteId,
                    bounds: track.bounds,
                    folderId: null,
                    createdAt: track.createdAt,
                    updatedAt: track.createdAt,
                  } as TrackContent, { switchMobileToMap: true });
                }}
              />
            )}

            {!showRoutePlanContent && !(!isDesktop && copilotMobileExpanded) && !(!isDesktop && showMobileSheet) && routePlan && currentUser && (routePlanFetching || routePlanAlternatives.length > 0 || routePlanError) && (
              <RoutePlanBar
                isDesktop={isDesktop}
                alternatives={routePlanAlternatives}
                selectedIndex={routePlanSelectedIndex}
                onSelectIndex={setRoutePlanSelectedIndex}
                toName={routePlan.toName}
                fromName={routePlanFromName || 'Min posisjon'}
                fromCoords={routePlan.fromCoords || userPosition || { latitude: 0, longitude: 0 }}
                toCoords={routePlan.toCoords}
                waypoints={routePlan.waypoints}
                onAddWaypoint={(coords, name) => setRoutePlan(prev => prev ? { ...prev, waypoints: [...prev.waypoints, { coords, name }] } : prev)}
                onRemoveWaypoint={copilot.planResult ? (i) => {
                  // Belte-og-seler: fjern fra tegningen UMIDDELBART uansett copilot-tilstand
                  // (stale planResult skal aldri gi et «dødt» trykk); copiloten re-planlegger
                  // så med plassen ekskludert og kan foreslå et annet stopp.
                  setRoutePlan(prev => prev ? { ...prev, waypoints: prev.waypoints.filter((_, x) => x !== i) } : prev);
                  copilot.removeStop(i);
                } : handleRemoveRouteStop}
                onReorderWaypoints={(waypoints) => setRoutePlan(prev => prev ? { ...prev, waypoints } : prev)}
                onChangeFrom={copilot.planResult
                  ? (coords, name) => copilot.updateEndpoint('origin', coords, name)
                  : (coords, name) => setRoutePlan(prev => prev ? { ...prev, fromCoords: coords, fromName: name } : prev)}
                onResetFrom={() => setRoutePlan(prev => prev ? { ...prev, fromCoords: undefined, fromName: undefined } : prev)}
                onChangeTo={copilot.planResult
                  ? (coords, name) => copilot.updateEndpoint('destination', coords, name)
                  : (coords, name) => setRoutePlan(prev => prev ? { ...prev, toCoords: coords, toName: name } : prev)}
                user={currentUser}
                fetching={routePlanFetching}
                error={routePlanError}
                onClose={handleCloseRoutePlan}
                onSaved={() => handleCloseRoutePlan()}
                onNavigate={handleNavigateRoutePlanAlternative}
                onSaveRequested={handleSaveRoutePlanAlternative}
                onWaypointTap={copilotOpen && copilot.planResult ? (i) => {
                  const s = copilot.planResult!.stops[i];
                  if (s) onSelectCopilotStop(s);
                } : undefined}
                onExploreContent={copilot.planResult ? exploreCopilotRoute : undefined}
                onShowRouteContent={() => {
                  setShowRoutePlanContent(true);
                  setSidebarMode('routes');
                  expandSidebar();
                  if (draftRouteDetail?.center) {
                    setMapCommand({ center: draftRouteDetail.center, zoom: 11, id: Date.now() });
                  }
                }}
              />
            )}

            {/* My Content Panel (mobile only — desktop uses sidebar) */}
            {!isDesktop && <MyContentPanel
              isOpen={showMyContent}
              onClose={() => setShowMyContent(false)}
              content={myContent.content}
              folders={myContent.folders}
              isPaidUser={isPaidUser}
              userId={currentUser?.uid}
              initialTab={myContentInitialTab}
              spotPhotos={spotPhotos}
              collaborativeMaps={collaborativeMaps}
              currentUser={currentUser}
              onOpenCollabMap={(mapId) => {
                setShowMyContent(false);
                setActiveCollabMapId(mapId);
                setAddToMapSpotId(null);
                setAddToMapRouteId(null);
                setShowCollabMapModal(true);
              }}
              onShowCollabMapOnMap={(mapId) => {
                const map = collaborativeMaps.find(m => m.id === mapId);
                if (!map) return;
                let latMin = Infinity, latMax = -Infinity, lonMin = Infinity, lonMax = -Infinity;
                let hasPoints = false;
                for (const spotId of map.spotIds) {
                  const spot = spots.find(s => s.id === spotId);
                  if (spot) {
                    latMin = Math.min(latMin, spot.location.latitude);
                    latMax = Math.max(latMax, spot.location.latitude);
                    lonMin = Math.min(lonMin, spot.location.longitude);
                    lonMax = Math.max(lonMax, spot.location.longitude);
                    hasPoints = true;
                  }
                }
                for (const routeId of (map.routeIds || [])) {
                  const route = routes.find(r => r.id === routeId);
                  if (route) {
                    for (const c of [route.center, route.startCoords, route.endCoords].filter(Boolean)) {
                      latMin = Math.min(latMin, c!.latitude);
                      latMax = Math.max(latMax, c!.latitude);
                      lonMin = Math.min(lonMin, c!.longitude);
                      lonMax = Math.max(lonMax, c!.longitude);
                      hasPoints = true;
                    }
                  }
                }
                if (!hasPoints) return;
                if (latMin === latMax) { latMin -= 0.005; latMax += 0.005; }
                if (lonMin === lonMax) { lonMin -= 0.005; lonMax += 0.005; }
                setShowMyContent(false);
                setMapCommand({
                  center: { latitude: (latMin + latMax) / 2, longitude: (lonMin + lonMax) / 2 },
                  zoom: 14,
                  bounds: [[latMin, lonMin], [latMax, lonMax]],
                  id: Date.now()
                });
              }}
              onCreateCollabMap={() => {
                setShowMyContent(false);
                setActiveCollabMapId(null);
                setAddToMapSpotId(null);
                setAddToMapRouteId(null);
                setShowCollabMapModal(true);
              }}
              onWaypointsPublished={(newSpots) => {
                setSpots(prev => {
                  const existingIds = new Set(prev.map(s => s.id));
                  const additions = newSpots.filter(s => !existingIds.has(s.id));
                  return additions.length ? [...additions, ...prev] : prev;
                });
              }}
              onFlyToContent={(bounds) => {
                setMapCommand({
                  center: { latitude: (bounds.latMin + bounds.latMax) / 2, longitude: (bounds.lonMin + bounds.lonMax) / 2 },
                  zoom: 14,
                  bounds: [[bounds.latMin, bounds.lonMin], [bounds.latMax, bounds.lonMax]],
                  id: Date.now()
                });
              }}
              onShowContentOnMap={setFocusedContent}
              onStartDrawing={(mode) => {
                drawing.startDrawing(mode);
                setShowMyContent(false);
              }}
              onViewTrack={(track) => handleOpenMyContentTrackAsRoute(track, { closeDesktopSidebar: true, switchMobileToMap: true })}
              onShowNudge={(key) => nudgeRef.current?.showNudge(key)}
            />}

            {/* Drawing mode toolbar */}
            {drawing.state === 'drawing' && drawing.toolType && (
              <DrawingToolbar
                toolType={drawing.toolType}
                pointCount={drawing.points.length}
                formattedDistance={drawing.formattedDistance}
                formattedArea={drawing.formattedArea}
                canFinish={drawing.canFinish}
                onUndo={drawing.undoLastPoint}
                onFinish={drawing.finishDrawing}
                onCancel={drawing.cancelDrawing}
              />
            )}

            {/* Drawing save dialog */}
            {drawing.state === 'saving' && drawing.toolType && (
              <DrawingSaveDialog
                toolType={drawing.toolType}
                formattedDistance={drawing.formattedDistance}
                formattedArea={drawing.formattedArea}
                onSave={handleSaveDrawing}
                onCancel={drawing.cancelDrawing}
              />
            )}

            {isOverlayMinimized && isViewportAnalyzing && (
              <div className="fixed bottom-24 right-4 md:bottom-6 md:right-20 z-[3000] flex flex-col gap-2 pointer-events-none">
                <div className="bg-[var(--bg-surface)]/95 backdrop-blur-xl border border-brand/30 text-[var(--text-primary)] px-4 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-right-4 fade-in duration-300 max-w-[250px]">
                  <div className="relative">
                    <RefreshCw size={18} className="text-brand animate-spin opacity-50" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 size={10} className="text-[var(--text-primary)] animate-spin" />
                    </div>
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-3xs font-black uppercase tracking-widest text-emerald-400 leading-none mb-0.5">
                      {t('common.analyzing')}
                    </span>
                    <span className="text-2xs font-bold text-[var(--text-primary)] truncate leading-tight">
                      {analysisProgress.current} / {analysisProgress.total} {t('common.points')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {analysisReport && (
              <div className="fixed inset-0 z-[11000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in">
                <div className="bg-[var(--bg-surface)] border border-[var(--border-secondary)] p-6 rounded-3xl w-full max-w-sm shadow-2xl relative animate-in zoom-in-95">
                  <button onClick={() => setAnalysisReport(null)} className="absolute top-4 right-4 p-2 bg-[var(--bg-input)] hover:bg-stone-700 rounded-full text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                    <X size={16} />
                  </button>

                  <div className="flex flex-col items-center mb-6">
                    <div className="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center mb-3">
                      <RefreshCw size={24} className="text-brand" />
                    </div>
                    <h3 className="text-lg font-black text-[var(--text-primary)] uppercase tracking-tight">{t('admin.quality_report')}</h3>
                    <p className="text-2xs text-[var(--text-muted)] font-bold uppercase tracking-widest">{t('admin.reevaluation_result')}</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center p-3 bg-[var(--bg-surface-alt)] rounded-xl border border-[var(--border-primary)]">
                      <span className="text-[var(--text-secondary)] text-xs font-bold uppercase">{t('admin.total_checked')}</span>
                      <span className="text-[var(--text-primary)] font-black">{analysisReport.checked}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-red-500/10 rounded-xl border border-red-500/20">
                        <span className="block text-3xs text-red-400 font-black uppercase mb-1">{t('admin.removed_house')}</span>
                        <span className="text-xl font-black text-[var(--text-primary)]">{analysisReport.removedHouse}</span>
                      </div>
                      <div className="p-3 bg-red-500/10 rounded-xl border border-red-500/20">
                        <span className="block text-3xs text-red-400 font-black uppercase mb-1">{t('admin.removed_road')}</span>
                        <span className="text-xl font-black text-[var(--text-primary)]">{analysisReport.removedRoad}</span>
                      </div>
                      <div className="p-3 bg-amber-500/10 rounded-xl border border-amber-500/20">
                        <span className="block text-3xs text-amber-400 font-black uppercase mb-1">{t('admin.removed_ai')}</span>
                        <span className="text-xl font-black text-[var(--text-primary)]">{analysisReport.removedAI}</span>
                      </div>
                      <div className="p-3 bg-brand/10 rounded-xl border border-brand/20">
                        <span className="block text-3xs text-emerald-400 font-black uppercase mb-1">{t('admin.approved')}</span>
                        <span className="text-xl font-black text-[var(--text-primary)]">{analysisReport.kept}</span>
                      </div>
                    </div>

                    <button
                      onClick={() => setAnalysisReport(null)}
                      className="w-full mt-6 bg-white text-black py-4 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-stone-200 transition-colors"
                    >
                      {t('admin.close_report')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {routeCreationStatus && mobileView === 'map' && (
              <div className={`absolute ${showAdminReevaluateCta ? 'top-44' : 'top-24'} left-1/2 -translate-x-1/2 z-[2100] animate-in slide-in-from-top-3`}>
                <div className={`px-4 py-2 rounded-2xl border shadow-2xl backdrop-blur-xl flex items-center gap-2 ${routeCreationStatus.type === 'loading'
                  ? 'bg-blue-600/90 border-blue-400/50 text-white'
                  : routeCreationStatus.type === 'success'
                    ? 'bg-brand-dark/90 border-emerald-400/50 text-white'
                    : 'bg-amber-600/95 border-amber-300/50 text-white'
                  }`}>
                  {routeCreationStatus.type === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <Route size={14} />}
                  <span className="text-2xs font-black uppercase tracking-wider">{routeCreationStatus.text}</span>
                </div>
              </div>
            )}

            {isPickingRouteStartOnMap && routePendingSpotForMapPick && (
              <div className="absolute bottom-24 md:bottom-8 left-1/2 -translate-x-1/2 z-[2200] animate-in slide-in-from-bottom-3">
                <div className="bg-[var(--bg-surface)]/95 border border-white/15 rounded-2xl px-3 py-2 flex items-center gap-2 shadow-2xl">
                  <span className="text-2xs font-black uppercase tracking-widest text-[var(--text-primary)]">{t("auto.app.klikk_avreisepunkt_i_kartet", "Klikk avreisepunkt i kartet")}</span>
                  <button
                    onClick={() => {
                      setIsPickingRouteStartOnMap(false);
                      if (routePendingSpotForMapPick) setRoutePlanningSpot(routePendingSpotForMapPick);
                      setRoutePendingSpotForMapPick(null);
                      setRouteCreationStatus(null);
                    }}
                    className="px-2 py-1 rounded-lg bg-[var(--bg-surface)] hover:bg-white/20 text-3xs font-black uppercase tracking-widest text-[var(--text-primary)]"
                  >
                    {t("auto.app.avbryt", "Avbryt")}
                  </button>
                </div>
              </div>
            )}

            {routePlanningSpot && (
              <div className="fixed inset-0 z-[11000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in">
                <div className="w-full max-w-md rounded-3xl border border-[var(--border-secondary)] bg-[var(--bg-surface)] p-5 space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-black uppercase tracking-widest text-[var(--text-primary)]">Lag rute til {routePlanningSpot.name || 'lokasjon'}</h3>
                      <p className="text-2xs text-[var(--text-secondary)] mt-1">{t("auto.app.velg_avreisested_vi_beregner_f", "Velg avreisested. Vi beregner faktisk veirute.")}</p>
                    </div>
                    <button
                      onClick={() => {
                        setRoutePlanningSpot(null);
                        setRoutePendingSpotForMapPick(null);
                        setIsPickingRouteStartOnMap(false);
                      }}
                      className="p-1.5 rounded-lg bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-secondary)]"
                      aria-label={t("auto.app.aria-label.lukk", "Lukk")}
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {showRoutePlannerHint && (
                    <div className="rounded-2xl border border-brand/25 bg-brand/10 px-3 py-2">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-2xs text-emerald-100 leading-relaxed">
                          {t("auto.app.tips_velg", "Tips: Velg")} <span className="font-black">{t("auto.app.sok_adresse", "Søk adresse")}</span> for presis avreise, eller
                          <span className="font-black"> {t("auto.app.velg_punkt_i_kartet", "Velg punkt i kartet")}</span> for å starte fra et valgfritt sted.
                        </p>
                        <button
                          onClick={dismissRoutePlannerHint}
                          className="text-3xs font-black uppercase tracking-widest text-emerald-200 hover:text-white"
                        >
                          {t("auto.app.skjul", "Skjul")}
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="flex items-center gap-2 p-2 rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] text-tiny text-[var(--text-primary)]">
                      <input type="radio" checked={routeStartMode === 'current'} onChange={() => setRouteStartMode('current')} />
                      {t("auto.app.min_posisjon_anbefalt", "Min posisjon (anbefalt)")}
                    </label>
                    <label className="flex items-center gap-2 p-2 rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] text-tiny text-[var(--text-primary)]">
                      <input type="radio" checked={routeStartMode === 'address'} onChange={() => setRouteStartMode('address')} />
                      {t("auto.app.sok_adresse", "Søk adresse")}
                    </label>
                    <label className="flex items-center gap-2 p-2 rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] text-tiny text-[var(--text-primary)]">
                      <input type="radio" checked={routeStartMode === 'map_pick'} onChange={() => setRouteStartMode('map_pick')} />
                      {t("auto.app.velg_punkt_i_kartet", "Velg punkt i kartet")}
                    </label>

                    {routeStartMode === 'address' && (
                      <div className="space-y-2">
                        {!routeAddressConfirmed ? (
                          <>
                            <div className="relative">
                              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                              <input
                                value={routeAddressQuery}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setRouteAddressQuery(next);
                                  if (routeAddressLockedQuery && next.trim() !== routeAddressLockedQuery.trim()) {
                                    setRouteAddressLockedQuery('');
                                  }
                                  if (routeAddressConfirmed) setRouteAddressConfirmed(false);
                                }}
                                placeholder={t("auto.app.placeholder.sok_adresse_eller_sted", "Søk adresse eller sted...")}
                                className="w-full rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-input)] pl-9 pr-3 py-2 text-[12px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                              />
                            </div>
                            {routeAddressLoading && <div className="text-2xs text-[var(--text-secondary)]">{t("auto.app.soker_adresser", "Søker adresser...")}</div>}
                            {!routeAddressLoading && routeAddressQuery.trim().length >= 3 && routeAddressResults.length > 0 && (
                              <div className="max-h-40 overflow-y-auto rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-input)]">
                                {routeAddressResults.slice(0, 10).map((r: any, idx: number) => (
                                  <button
                                    key={`${r.lat}-${r.lon}-${idx}`}
                                    onClick={() => {
                                      const chosen = r.display_name || `${r.lat}, ${r.lon}`;
                                      const selected = { latitude: Number(r.lat), longitude: Number(r.lon) };
                                      setRouteAddressStart(selected);
                                      setRouteAddressLabel(chosen);
                                      setRouteAddressQuery(chosen);
                                      setRouteAddressLockedQuery(chosen);
                                      setRouteAddressResults([]);
                                      setRouteAddressConfirmed(true);
                                      setMapCommand({ center: selected, zoom: 16, id: Date.now() });
                                    }}
                                    className="w-full text-left px-3 py-2 text-tiny text-[var(--text-primary)] hover:bg-[var(--bg-surface)] border-b border-[var(--border-primary)] last:border-b-0"
                                  >
                                    {r.display_name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <button
                            onClick={() => {
                              setRouteAddressConfirmed(false);
                              setRouteAddressQuery('');
                              setRouteAddressResults([]);
                            }}
                            className="w-full rounded-xl border border-white/15 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] px-3 py-2 text-2xs font-black uppercase tracking-widest text-[var(--text-primary)]"
                          >
                            {t("auto.app.sok_ny_adresse", "Søk ny adresse")}
                          </button>
                        )}
                        {routeAddressLabel && (
                          <div className="text-2xs text-emerald-300 bg-brand/10 border border-brand/20 rounded-lg px-2 py-1">
                            Avreise: {routeAddressLabel}
                          </div>
                        )}
                      </div>
                    )}

                    {routeStartMode === 'map_pick' && (
                      <div className="space-y-2">
                        <button
                          onClick={() => {
                            setIsPickingRouteStartOnMap(true);
                            setRoutePendingSpotForMapPick(routePlanningSpot);
                            setRoutePlanningSpot(null);
                            setRouteCreationStatus({ type: 'loading', text: 'Klikk på ønsket avreisepunkt i kartet' });
                          }}
                          className="w-full rounded-xl bg-[var(--bg-surface)] hover:bg-white/15 border border-white/15 text-[var(--text-primary)] py-2.5 text-2xs font-black uppercase tracking-widest"
                        >
                          {t("auto.app.velg_punkt_i_kartet", "Velg punkt i kartet")}
                        </button>
                        {routeMapStart && (
                          <div className="text-2xs text-emerald-300 bg-brand/10 border border-brand/20 rounded-lg px-2 py-1">
                            Avreise: {routeMapStartLabel || `${routeMapStart.latitude.toFixed(5)}, ${routeMapStart.longitude.toFixed(5)}`}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setRoutePlanningSpot(null);
                        setRoutePendingSpotForMapPick(null);
                        setIsPickingRouteStartOnMap(false);
                      }}
                      className="flex-1 py-2.5 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest"
                    >
                      {t("auto.app.avbryt", "Avbryt")}
                    </button>
                    <button
                      onClick={() => createRouteFromSpotNow(routePlanningSpot)}
                      disabled={isCreatingSpotRoute}
                      className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:bg-blue-700/60 text-white text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2"
                    >
                      {isCreatingSpotRoute ? <Loader2 size={13} className="animate-spin" /> : <Route size={13} />}
                      {isCreatingSpotRoute ? 'Beregner...' : 'Beregn rute'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <NavigationChoiceModal
              isOpen={!!navChoiceTarget}
              onClose={() => setNavChoiceTarget(null)}
              onNativeNav={handleNativeNavToMapClick}
              onInAppNav={handlePlanRouteToMapClick}
              onCopyCoords={handleCopyCoordsClick}
              coords={navChoiceTarget?.coords}
              targetName={navChoiceTarget?.name}
            />

            {routeBuilderSegments.length > 0 && mobileView === 'map' && (
              <div className={`absolute ${showAdminReevaluateCta ? 'top-44' : 'top-24'} left-1/2 -translate-x-1/2 z-[2000] flex flex-col items-center gap-2 animate-in slide-in-from-top-4 w-[calc(100%-2rem)] max-w-md`}>
                <div className="bg-[var(--bg-surface)]/90 backdrop-blur-xl border border-blue-500/30 px-4 py-2 rounded-2xl flex items-center gap-3 shadow-2xl">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                  <span className="text-2xs font-black uppercase text-[var(--text-primary)] tracking-widest">{t('route.segments_selected', { count: routeBuilderSegments.length })}</span>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  <button
                    onClick={handleNavigateFromBuilder}
                    className="bg-brand-dark text-white px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest shadow-lg active:scale-95 transition flex items-center gap-2"
                  >
                    <Navigation size={14} /> {t('spot.navigate')}
                  </button>
                  <button
                    onClick={handleCreateFromBuilder}
                    className="bg-blue-600 text-white px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest shadow-lg active:scale-95 transition flex items-center gap-2"
                  >
                    <CheckCircle size={14} /> {t('route.create_route')}
                  </button>
                  <button
                    onClick={handleClearBuilder}
                    className="bg-[var(--bg-input)] text-[var(--text-secondary)] px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest shadow-lg active:scale-95 transition flex items-center gap-2"
                  >
                    <Trash2 size={14} /> {t('common.reset')}
                  </button>
                </div>
              </div>
            )}

            {isManualAnalyzing && !pendingAnalysisCoords && (mobileView === 'map' || isDesktop) && (
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[2000] flex items-center gap-2 bg-zinc-900/90 text-white text-tiny font-bold px-3 py-2 rounded-full shadow-xl">
                <Loader2 className="animate-spin shrink-0" size={13} />
                <span>Analyserer{queueLength > 0 ? ` – ${queueLength} i kø` : ''}…</span>
              </div>
            )}

            {pendingAnalysisCoords && !selectedSpotId && (mobileView === 'map' || isDesktop) && (
              <div className="absolute bottom-24 md:bottom-8 left-1/2 -translate-x-1/2 z-[2000] animate-in slide-in-from-bottom-4 w-[calc(100vw-1rem)] max-w-[560px] px-2">
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={handleManualAnalysis}
                    className="bg-brand-dark text-white px-2 md:px-5 py-3 rounded-full font-black text-2xs md:text-xs uppercase tracking-widest shadow-2xl active:scale-95 transition flex items-center justify-center gap-1.5 md:gap-2 min-w-0"
                  >
                    <Search size={16} /> {t('common.analyze')}
                    {isManualAnalyzing && queueLength > 0 && (
                      <span className="bg-white/25 rounded-full text-3xs px-1.5 py-0.5 leading-none">{queueLength + 1} i kø</span>
                    )}
                  </button>

                  {!highlightedRoad ? (
                    <button
                      onClick={handleShowRoad}
                      disabled={isLoadingRoad}
                      className="bg-white text-stone-900 px-2 md:px-5 py-3 rounded-full font-black text-2xs md:text-xs uppercase tracking-widest shadow-2xl active:scale-95 transition flex items-center justify-center gap-1.5 md:gap-2 min-w-0"
                    >
                      {isLoadingRoad ? <Loader2 className="animate-spin" size={16} /> : <Route size={16} />} {t('route.show_road')}
                    </button>
                  ) : (
                    <button
                      onClick={handleAddToRouteBuilder}
                      className="bg-blue-600 text-white px-2 md:px-5 py-3 rounded-full font-black text-2xs md:text-xs uppercase tracking-widest shadow-2xl active:scale-95 transition flex items-center justify-center gap-1.5 md:gap-2 min-w-0"
                    >
                      <Plus size={16} /> {t('route.add_to_route')}
                    </button>
                  )}

                  <button
                    onClick={() => {
                      if (!currentUser) { setIsAuthOpen(true); return; }
                      if (!pendingAnalysisCoords) {
                        setPendingAnalysisCoords(lastKnownGpsPosition || mapCenter);
                      }
                      setShowAddModal(true);
                    }}
                    className="bg-white text-stone-900 px-2 md:px-5 py-3 rounded-full font-black text-2xs md:text-xs uppercase tracking-widest shadow-2xl active:scale-95 transition flex items-center justify-center gap-1.5 md:gap-2 min-w-0"
                  >
                    <Plus size={16} /> {t('common.add')}
                  </button>
                </div>
              </div>
            )}

            {/* Floating "clear active route" pill — mobile only, hidden during follow mode */}
            {activeRouteIds.size > 0 && !selectedRouteId && !isDesktop && mobileView === 'map' && !routePlan && !followActive && (
              <div className="sm:hidden absolute bottom-[88px] left-1/2 -translate-x-1/2 z-[3000] flex items-center gap-2 animate-in slide-in-from-bottom-4">
                {activeRouteIds.size === 1 && (
                  <button
                    onClick={() => handleRouteClick(Array.from(activeRouteIds)[0])}
                    className="flex items-center gap-2 bg-emerald-600/95 backdrop-blur-xl border border-emerald-400/40 text-white px-4 py-2.5 rounded-full shadow-2xl active:scale-95 transition"
                  >
                    <MapIcon size={14} strokeWidth={3} />
                    <span className="text-2xs font-black uppercase tracking-widest">{t('sidebar.show_route_card', 'Vis rutekort')}</span>
                  </button>
                )}
                <button
                  onClick={() => { setActiveRouteIds(new Set()); setSelectedRouteId(null); }}
                  className="flex items-center gap-2 bg-stone-900/90 backdrop-blur-xl border border-white/20 text-white px-4 py-2.5 rounded-full shadow-2xl active:scale-95 transition"
                >
                  <X size={14} strokeWidth={3} />
                  <span className="text-2xs font-black uppercase tracking-widest">{t('sidebar.reset_routes')}</span>
                </button>
              </div>
            )}

            {/* Admin-innboks-knappen er flyttet inn i kartets høyre kontroll-stabel
                (rett under ⋮), se MapComponent onOpenAdminInbox. */}

            {/* Horizontal mini-cards carousel.
                Gratisbrukere: algoritmiske plasser filtreres bort, men de ser
                en teaser-kort på slutten som forteller hvor mange de går glipp av. */}
            {mobileView === 'map' && (miniCardSpotsForUser.length > 0 || hiddenPremiumCount > 0) && !routePlan && !pendingAnalysisCoords && !sidebarExpanded && trackingStatus === 'idle' && (
              <MapMiniCards
                spots={miniCardSpotsForUser}
                hiddenPremiumCount={hiddenPremiumCount}
                onUpgrade={() => setShowUpgrade(true)}
                leftOffsetPx={isDesktop ? railWidthPx : 56}
                selectedSpotId={selectedSpotId}
                onSelectSpot={(id) => {
                  // Resolve via felles helper — ekte spots fra Firestore, eller syntetiske
                  // waypoint-kort (wpcard-) registreres lazily i `spots`-state.
                  let spot = resolveSpotById(id);
                  // Karusellen kan også vise generiske personlige waypoints (uten layer-kategori)
                  // som ikke er i syntheticLayerSpots — fall tilbake til miniCardSpots.
                  if (!spot && id.startsWith('wpcard-')) {
                    spot = miniCardSpots.find(s => s.id === id);
                    if (spot) {
                      const fallbackSpot = spot;
                      setSpots(prev => prev.some(s => s.id === id) ? prev : [fallbackSpot, ...prev]);
                    }
                  }
                  if (!spot) return;

                  // Behave exactly like tapping a spot on the map: show the full
                  // SpotCard in the list/sidebar panel, not the overlapping bottom
                  // sheet. (onMobileSelectSpot handles desktop + mobile.)
                  onMobileSelectSpot(spot);
                }}
                onNavigate={handleNavigateToSpot}
                userLocation={userPosition ?? undefined}
              />
            )}

            {!selectedRouteId && !routePlan && (trackingStatus === 'idle' || isAutoConnected) && (
              <div className={`transition-opacity duration-500 ${bottomNavVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                <MobileBottomNav
                  mobileView={mobileView}
                  sidebarMode={sidebarMode}
                  spotsDisabled={activeRouteIds.size > 0 && !isAutoConnected}
                  onSetMobileView={setMobileView}
                  onSetSidebarMode={setSidebarMode}
                  onOpenCopilot={openCopilot}
                  copilotEnabled={hasCopilotAccess(currentUser)}
                  newsCount={newsCount}
                  myContentCount={myContentBadgeCount}
                />
              </div>
            )}

          </div>
        </div>

        {/* Upgrade reminder modal for free users (event-based) */}
        {showUpgradeReminder && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in" onClick={() => setShowUpgradeReminder(false)}>
            <div className="bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-3xl p-8 max-w-sm mx-4 text-center shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4" onClick={e => e.stopPropagation()}>
              <div className="w-14 h-14 rounded-2xl bg-brand/20 flex items-center justify-center mx-auto mb-5">
                <Sparkles className="text-emerald-400" size={28} />
              </div>
              <h3 className="text-[var(--text-primary)] text-lg font-black mb-2">{t('upgrade_reminder.title')}</h3>
              <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-6">{t('upgrade_reminder.description')}</p>
              <div className="flex flex-col gap-2.5">
                <button
                  onClick={() => { setShowUpgradeReminder(false); setShowUpgrade(true); }}
                  className="w-full py-3.5 rounded-xl bg-brand-dark hover:bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg transition active:scale-95"
                >
                  {t('upgrade_reminder.btn_see_plans')}
                </button>
                {testerApplicationsEnabled ? (
                  <button
                    onClick={() => { setShowUpgradeReminder(false); setShowTesterApplication(true); }}
                    className="w-full py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 hover:bg-amber-500/20 text-xs font-black uppercase tracking-widest transition"
                  >
                    {t('profile.membership_status.apply_tester')}
                  </button>
                ) : (
                  <p className="text-2xs text-[var(--text-muted)] text-center py-2">{t('upgrade.tester.not_recruiting')}</p>
                )}
                <button
                  onClick={() => setShowUpgradeReminder(false)}
                  className="w-full py-3 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-secondary)] text-xs font-bold transition-colors"
                >
                  {t('upgrade_reminder.btn_not_now')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Welcome sheet for first-time visitors */}
        {showWelcomeSheet && !currentUser && (
          <WelcomeSheet
            onExplore={() => setShowWelcomeSheet(false)}
            onLogin={() => { setShowWelcomeSheet(false); setIsAuthOpen(true); }}
          />
        )}

        <LoadingOverlay visible={isViewportAnalyzing && !isOverlayMinimized} statusText={t('common.analyzing_progress', { current: analysisProgress.current, total: analysisProgress.total })} onCancel={handleCancelScan} onMinimize={handleMinimize} />

        {navigationBuilding && (
          <div className="fixed inset-0 z-[9000] flex items-center justify-center pointer-events-none">
            <div className="bg-[var(--bg-surface)]/90 backdrop-blur-md border border-[var(--border-secondary)] rounded-3xl px-8 py-6 flex flex-col items-center gap-3 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
              <Loader2 className="animate-spin text-emerald-400" size={32} />
              <span className="text-sm font-bold text-[var(--text-primary)]">{t('routes.building_route', 'Beregner rute...')}</span>
            </div>
          </div>
        )}

        {/* Tydelig, vedvarende indikator mens en plass analyseres (1–8 s). Vises uansett
            om spot-kortet er scrollet inn i visning. Forsvinner automatisk når
            activeAnalysisIds tømmes i useSelectionManager.reanalyzeSpot finally-blokk. */}
        {activeAnalysisIds.size > 0 && (
          <div className="fixed top-[calc(env(safe-area-inset-top)+4.5rem)] left-0 right-0 z-[9000] flex justify-center px-4 pointer-events-none">
            <div className="bg-[var(--bg-surface)]/95 backdrop-blur-md border border-[var(--border-secondary)] border-l-4 border-l-brand rounded-2xl px-5 py-3 flex items-center gap-3 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-300">
              <Loader2 className="animate-spin text-emerald-400" size={20} />
              <span className="text-sm font-bold text-[var(--text-primary)]">{t('spot.analyzing_spot', 'Analyserer plass…')}</span>
            </div>
          </div>
        )}

        {followPreflight && (
          <FollowPreflightModal
            mode={followPreflight.mode}
            title={followPreflight.title}
            bounds={followPreflight.bounds}
            is3DPreferred={followViewMode === '3d'}
            dataReady={followPreflightDataReady}
            dataLoading={followPreflightDataLoading}
            dataStatus={followPreflightDataStatus}
            onDownloadData={handleDownloadFollowData}
            onOpenOfflineMaps={() => setShowFollowOfflineManager(true)}
            onDownloadOfflineMaps={async (b) => {
              const layers: ('NORWAY_TOPO' | 'SATELLITE')[] = followViewMode === '3d' ? ['NORWAY_TOPO', 'SATELLITE'] : ['NORWAY_TOPO'];
              const zoomMin = followViewMode === '3d' ? 10 : 11;
              const zoomMax = 14;
              const est = estimateTileCount(b, zoomMin, zoomMax, layers);
              const area = {
                id: `follow-${Date.now()}`,
                name: followPreflight?.title || 'Navigasjon',
                createdAt: Date.now(),
                bounds: b,
                layers,
                zoomMin,
                zoomMax,
                tileCount: est.tileCount,
                estimatedBytes: est.estimatedBytes,
                status: 'pending' as const,
              };
              const controller = new AbortController();
              await downloadTiles(area, () => {}, controller.signal);
              return { tileCount: est.tileCount };
            }}
            onContinue={async () => {
              const action = followPreflight.continueAction;
              setFollowPreflight(null);
              await action();
            }}
            onClose={() => setFollowPreflight(null)}
          />
        )}

        {showFollowOfflineManager && followPreflight && (
          <OfflineMapManager
            currentViewBounds={currentViewBounds}
            initialBounds={followPreflight.bounds}
            initialLayers={followViewMode === '3d' ? ['NORWAY_TOPO', 'SATELLITE'] : ['NORWAY_TOPO']}
            initialZoomMin={followViewMode === '3d' ? 10 : 11}
            initialZoomMax={followViewMode === '3d' ? 14 : 13}
            initialIncludeSpots={true}
            initialIncludeRoutes={followPreflight.mode === 'route_follow'}
            onClose={() => setShowFollowOfflineManager(false)}
            onFlyTo={(bounds) => {
              setMapCommand({
                center: {
                  latitude: (bounds.latMin + bounds.latMax) / 2,
                  longitude: (bounds.lonMin + bounds.lonMax) / 2,
                },
                zoom: 11,
                bounds: [[bounds.latMin, bounds.lonMin], [bounds.latMax, bounds.lonMax]],
                id: Date.now()
              });
            }}
            isPaidUser={isPaidUser}
            onOpenUpgrade={() => {
              setShowFollowOfflineManager(false);
              setShowUpgrade(true);
            }}
          />
        )}

        {showCountyDownload && userPosition && (
          <CountyDownloadModal
            userPosition={userPosition}
            onClose={() => {
              setShowCountyDownload(false);
              localStorage.setItem('fricamp_county_download_shown', '1');
            }}
            onDownload={(countyName, bounds) => {
              localStorage.setItem('fricamp_county_download_shown', '1');
              // Open the existing offline map manager with county bounds pre-filled
              setMapCommand({
                center: { latitude: (bounds.latMin + bounds.latMax) / 2, longitude: (bounds.lonMin + bounds.lonMax) / 2 },
                zoom: 10,
                bounds: [[bounds.latMin, bounds.lonMin], [bounds.latMax, bounds.lonMax]],
                id: Date.now(),
              });
              addToast(`Laster ned ${countyName}…`, 'info');
            }}
          />
        )}

        {/* Reise-copilot mobil-ark (dra-bart, over kartet). Desktop bruker sidepanelet. */}
        {copilotSheetOpen && !isDesktop && (
          <CopilotMobileSheet
            copilot={copilot}
            spotCardOpen={showMobileSheet}
            routeStats={copilotRouteStats}
            onExploreStop={onSelectCopilotStop}
            onExploreAttraction={(a) => { setMapCommand({ center: { latitude: a.latitude, longitude: a.longitude }, zoom: 12, id: Date.now() }); void handleRouteAttractionTap(a); }}
            onExpandedChange={setCopilotMobileExpanded}
            expandSignal={copilotExpandSignal}
            onClose={() => setCopilotSheetOpen(false)}
            onStartNavigation={() => {
              const alt = routePlanAlternatives[routePlanSelectedIndex ?? 0];
              if (alt) handleNavigateRoutePlanAlternative(alt);
            }}
          />
        )}

        <AppOverlays
          isAuthOpen={isAuthOpen}
          setIsAuthOpen={setIsAuthOpen}
          isProfileOpen={isProfileOpen}
          setIsProfileOpen={setIsProfileOpen}
          currentUser={currentUser}
          visits={visits}
          isStickerBoardOpen={isStickerBoardOpen}
          setIsStickerBoardOpen={setIsStickerBoardOpen}
          isReplayOpen={isReplayOpen}
          setIsReplayOpen={setIsReplayOpen}
          showUpgrade={showUpgrade}
          setShowUpgrade={setShowUpgrade}
          vippsPaymentEnabled={vippsPaymentEnabled}
          showTesterApplication={showTesterApplication}
          setShowTesterApplication={setShowTesterApplication}
          showAndroidGuide={showAndroidGuide}
          setShowAndroidGuide={setShowAndroidGuide}
          showInstallApp={showInstallApp}
          setShowInstallApp={setShowInstallApp}
          showFeedbackInbox={showFeedbackInbox}
          setShowFeedbackInbox={setShowFeedbackInbox}
          feedbackPrefill={feedbackPrefill}
          setFeedbackPrefill={setFeedbackPrefill}
          showAddModal={showAddModal}
          setShowAddModal={setShowAddModal}
          editingSpot={editingSpot}
          clearEditingSpot={() => setEditingSpot(undefined)}
          pendingAnalysisCoords={pendingAnalysisCoords}
          handleAddSpotSubmit={handleAddSpotSubmit}
          initialFiles={preSelectedFiles}
          clearInitialFiles={() => setPreSelectedFiles([])}
          showLegend={showLegend}
          setShowLegend={setShowLegend}
          showLegal={showLegal}
          setShowLegal={setShowLegal}
          showTierGuide={showTierGuide}
          setShowTierGuide={setShowTierGuide}
          showWhatsNew={showWhatsNew}
          setShowWhatsNew={setShowWhatsNew}
          showAdmin={showAdmin}
          setShowAdmin={(show) => {
            setShowAdmin(show);
            if (!show) setMobileAdminInboxOnly(false);
          }}
          mobileAdminInboxOnly={mobileAdminInboxOnly}
          adminTab={adminTab}
          setAdminTab={setAdminTab}
          setMapCommand={setMapCommand}
          handleSelectSpot={handleSelectSpot}
          spots={spots}
          removeSpotLocally={removeSpotLocally}
          clearAlgorithmicSpots={clearAlgorithmicSpots}
          handleReevaluateViewport={handleReevaluateViewport}
          currentViewBounds={currentViewBounds}
          selectedSpotId={selectedSpotId}
          setSelectedSpotId={setSelectedSpotId}
          showMobileSheet={(navigationBuilding || followPreflight) ? false : showMobileSheet}
          setShowMobileSheet={setShowMobileSheet}
          canReturnToRoute={canReturnToRoute}
          onReturnToRoute={handleBackToRouteFocus}
          canReturnToCollabMap={!!collabReturnContext}
          onReturnToCollabMap={handleBackToCollabMap}
          mobileView={mobileView}
          favoriteSpotIds={favoriteSpotIds}
          visitedSpotIds={visitedSpotIds}
          collaborativeMaps={collaborativeMaps}
          onToggleFavoriteSpot={handleToggleFavoriteSpot}
          onToggleVisit={handleToggleVisit}
          onAddPastStay={handleAddPastStay}
          onDeletePastStay={handleDeletePastStay}
          onMarkOccupied={handleMarkOccupied}
          onExtendOccupancy={handleExtendOccupancy}
          onClearOccupancy={handleClearOccupancy}
          userPosition={userPosition}
          onAddToMap={handleAddSpotToMap}
          showCollabMapModal={showCollabMapModal}
          setShowCollabMapModal={setShowCollabMapModal}
          activeCollabMapId={activeCollabMapId}
          setActiveCollabMapId={setActiveCollabMapId}
          addToMapSpotId={addToMapSpotId}
          setAddToMapSpotId={setAddToMapSpotId}
          addToMapRouteId={addToMapRouteId}
          setAddToMapRouteId={setAddToMapRouteId}
          routes={routes}
          isPaidUser={isPaidUser}
          reanalyzeSpot={reanalyzeSpot}
          activeAnalysisIds={activeAnalysisIds}
          handleEditSpot={handleEditSpot}
          showRouteUpload={showRouteUpload}
          setShowRouteUpload={setShowRouteUpload}
          editingRoute={editingRoute}
          setEditingRoute={setEditingRoute}
          preloadedRouteFile={preloadedRouteFile}
          setPreloadedRouteFile={setPreloadedRouteFile}
          preloadedRouteMetadata={preloadedRouteMetadata}
          setPreloadedRouteMetadata={setPreloadedRouteMetadata}
          loadRoutesList={loadRoutesList}
          setPendingRouteId={setPendingRouteId}
          onRouteUploaded={(routeId) => {
            setActiveRouteIds(new Set([routeId]));
            setSelectedRouteId(routeId);
            // Zoom to the route after it loads — read fresh state via setRoutes to avoid stale closure
            setTimeout(() => {
              setRoutes(currentRoutes => {
                const route = currentRoutes.find(r => r.id === routeId);
                if (route?.center) {
                  setMapCommand({ center: route.center, zoom: 10, id: Date.now() });
                }
                return currentRoutes;
              });
            }, 1000);
          }}
          setSidebarMode={setSidebarMode}
          showNotifications={showNotifications}
          setShowNotifications={setShowNotifications}
          notifications={mergedNotifications}
          onMarkNotificationAsRead={handleMarkNotificationAsRead}
          onMarkAllNotificationsRead={handleMarkAllNotificationsAsRead}
          onSelectSpotFromMap={(spot, fromMapId) => {
            setCollabReturnContext({ mapId: fromMapId });
            onMobileSelectSpot(spot);
            setMapCommand({ center: spot.location, zoom: 16, id: Date.now() });
          }}
          onSelectRouteFromMap={(routeId, fromMapId) => {
            setCollabReturnContext({ mapId: fromMapId });
            handleRouteClick(routeId);
            const route = routes.find(r => r.id === routeId);
            if (route?.center) {
              setMapCommand({ center: route.center, zoom: 10, id: Date.now() });
            }
          }}
          onGoToSpot={handleGoToSpot}
          testerApplicationsEnabled={testerApplicationsEnabled}
          onNewUserRegistered={() => {
            setTimeout(() => addToast(t('toast.welcome_new_user'), 'success'), 500);
          }}
        />
      </div>

      {/* Detaljer-visning under navigasjon håndteres nå via sidebar (expandSidebar + handleSelectSpotDetails) */}

      {/* Auto-check-in banner */}
      {autoCheckInSpot && (
        <AutoCheckInBanner
          spot={autoCheckInSpot.spot}
          mode={autoCheckInSpot.mode}
          onCheckIn={handleAutoCheckIn}
          onExtend={handleAutoExtend}
          onDismiss={() => { setDismissed(autoCheckInSpot.spot.id); setAutoCheckInSpot(null); }}
        />
      )}

      {/* Vedvarende innsjekk/utsjekk-pill — skjules mens arrival-banneret eller et
          plass-ark er åpent (der finnes handlingene alt), ellers alltid synlig. */}
      {checkInPill && !autoCheckInSpot && !selectedSpotId && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-[3500] pointer-events-auto max-w-[calc(100vw-2rem)]"
          style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + ${miniCardStripVisible ? '12rem' : '5rem'})` }}
        >
          <CheckInOutPill {...checkInPill} />
        </div>
      )}

      {/* Auto-navigation overlay — appears when app is idle + moving fast */}
      {autoNav.active && autoNav.nearestSpot && autoNav.distanceM != null && (
        <AutoNavOverlay
          spot={autoNav.nearestSpot}
          distanceM={autoNav.distanceM}
          onOpenSpot={(s) => { autoNav.exit(); handleSelectSpot(s); }}
          onExit={autoNav.exit}
        />
      )}

      {/* «Fricamp til gjennomgang»-chip — vises når man IKKE kjører og har markerte kandidater */}
      {autonavCandidates.length > 0 && !autoNav.active && !showReviewCandidates && !showAddModal && (
        <button
          onClick={() => setShowReviewCandidates(true)}
          className="fixed top-[calc(env(safe-area-inset-top,0px)+76px)] left-1/2 -translate-x-1/2 z-[9990] flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-tiny font-black shadow-2xl border-2 border-white/70 active:scale-95 transition-all animate-in fade-in slide-in-from-top-2 duration-300"
        >
          <i className="fa-solid fa-tent" />
          {autonavCandidates.length} {t('autonav.review.chip', 'fricamp til gjennomgang')}
        </button>
      )}

      {showReviewCandidates && (
        <ReviewCandidatesModal
          candidates={autonavCandidates}
          onClose={() => setShowReviewCandidates(false)}
          onReview={handleReviewCandidate}
          onDiscard={handleDiscardCandidate}
        />
      )}

      {/* Hurtiggodkjenning av artikkelutkast (push / ?review_article=). Lastes lat —
          bare admin ser den noen gang. Callablene bak den er admin-gated uansett. */}
      {reviewArticleId && (
        <Suspense fallback={null}>
          <ArticleQuickReviewModal
            articleId={reviewArticleId}
            onClose={() => setReviewArticleId(null)}
          />
        </Suspense>
      )}

    </PremiumNudgeProvider>
  );
}

export default App;
